import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import NavigationHeader from "../../components/ui/NavigationHeader";
import PcpAuditHistory from "./PcpAuditHistory";
import Button from "../../components/ui/Button";
import Input from "../../components/ui/Input";
import Icon from "../../components/AppIcon";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "http://localhost:8080";
const apiBase = API_BASE_URL.replace(/\/+$/, "");

const today = () => new Date().toISOString().slice(0, 10);
const thirtyDaysAgo = () => {
  const date = new Date();
  date.setDate(date.getDate() - 30);
  return date.toISOString().slice(0, 10);
};
const nowTime = () => {
  const date = new Date();
  return `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
};

const blankProductionForm = {
  data: today(),
  produtoNome: "",
  previsaoQuantidade: "",
  realizadoQuantidade: "",
  perdaQuantidade: "",
  motivoPerda: "",
  observacao: "",
};

const blankTemperatureForm = {
  data: today(),
  horario: nowTime(),
  localNome: "",
  objetoAmbiente: "",
  responsavel: "",
  temperatura: "",
  umidade: "",
  observacao: "",
};

const blankConfigForm = {
  localNome: "",
  temperaturaMinima: "",
  temperaturaMaxima: "",
  umidadeMinima: "",
  umidadeMaxima: "",
  ativo: true,
};

const blankHumidityUrForm = {
  data: today(),
  horario: nowTime(),
  localNome: "",
  objetoAmbiente: "",
  responsavel: "",
  umidade: "",
  observacao: "",
};

const blankHumidityUrConfigForm = {
  localNome: "",
  umidadeMinima: "",
  umidadeMaxima: "",
  ativo: true,
};

const hygieneItems = [
  { key: "avental", label: "Avental" },
  { key: "calca", label: "Calça" },
  { key: "blusa", label: "Blusa" },
  { key: "sapato", label: "Sapato" },
  { key: "touca", label: "Touca" },
  { key: "mascara", label: "Máscara" },
  { key: "ausenciaBarba", label: "Ausência de barba" },
  { key: "ausenciaAdornos", label: "Ausência de adornos" },
];

const blankHygieneForm = {
  data: today(),
  funcionarioNome: "",
  responsavel: "",
  observacao: "",
  itens: hygieneItems.reduce((acc, item) => ({ ...acc, [item.key]: "C" }), {}),
};

const buildingCleaningItems = [
  { key: "bancadas", label: "Bancadas", grupo: "Área de uso" },
  { key: "pas", label: "Pás", grupo: "Área de uso" },
  { key: "torneiras", label: "Torneiras", grupo: "Área de uso" },
  { key: "balanca", label: "Balança", grupo: "Área de uso" },
  { key: "equipamentos", label: "Equipamentos", grupo: "Área de uso" },
  { key: "utensilios", label: "Utensílios", grupo: "Área de uso" },
  { key: "pisos", label: "Pisos", grupo: "Geral" },
  { key: "rodapes", label: "Rodapés", grupo: "Geral" },
  { key: "banheiro", label: "Banheiro", grupo: "Geral" },
  { key: "paredes", label: "Paredes", grupo: "Geral" },
  { key: "lixeiras", label: "Lixeiras", grupo: "Geral" },
  { key: "refrigeradoresFreezer", label: "Refrigeradores e Freezer", grupo: "Geral" },
  { key: "portasJanelas", label: "Portas e Janelas", grupo: "Estrutura" },
  { key: "interruptores", label: "Interruptores", grupo: "Estrutura" },
  { key: "tomadas", label: "Tomadas", grupo: "Estrutura" },
  { key: "luminarias", label: "Luminárias", grupo: "Estrutura" },
  { key: "lampadas", label: "Lâmpadas", grupo: "Estrutura" },
  { key: "batedeira", label: "Batedeira", grupo: "Máquinas" },
  { key: "forno1", label: "Forno 1", grupo: "Máquinas" },
  { key: "forno2", label: "Forno 2", grupo: "Máquinas" },
  { key: "derretedeiraChocolate", label: "Derretedeira de Chocolate", grupo: "Máquinas" },
  { key: "flowpack", label: "Flowpack", grupo: "Máquinas" },
  { key: "dosadoraRecheio", label: "Dosadora de Recheio", grupo: "Máquinas" },
  { key: "cobrideiraChocolate", label: "Cobrideira de Chocolate", grupo: "Máquinas" },
  { key: "tunelResfriamento", label: "Túnel de Resfriamento", grupo: "Máquinas" },
  { key: "geladeira", label: "Geladeira", grupo: "Máquinas" },
  { key: "freezer", label: "Freezer", grupo: "Máquinas" },
];

const blankBuildingCleaningForm = {
  data: today(),
  responsavel: "",
  acaoCorretiva: "",
  observacao: "",
  itens: buildingCleaningItems.reduce((acc, item) => ({ ...acc, [item.key]: "C" }), {}),
};

const blankEquipmentMaintenanceForm = {
  data: today(),
  equipamentoNome: "",
  tipo: "limpeza",
  conformidade: "C",
  responsavel: "",
  observacao: "",
};

const blankEquipmentMaintenanceConfigForm = {
  equipamentoNome: "",
  limpezaScheduleTipo: "semanal",
  limpezaIntervaloDias: "7",
  limpezaDiaSemana: "5",
  limpezaDiaMes: "1",
  manutencaoScheduleTipo: "intervalo",
  manutencaoIntervaloDias: "90",
  manutencaoDiaSemana: "5",
  manutencaoDiaMes: "1",
  ativo: true,
};

const weekDayOptions = [
  { value: "0", label: "Domingo" },
  { value: "1", label: "Segunda-feira" },
  { value: "2", label: "Terça-feira" },
  { value: "3", label: "Quarta-feira" },
  { value: "4", label: "Quinta-feira" },
  { value: "5", label: "Sexta-feira" },
  { value: "6", label: "Sábado" },
];


const genericRegistryModules = {
  "recebimento-mercadorias": {
    label: "Recebimento de Mercadoria",
    description: "Registro de recebimento de matérias-primas, ingredientes e embalagens.",
    icon: "Truck",
    endpoint: "/api/controle-producao/recebimento-mercadorias",
    emptyForm: {
      data: today(),
      hora: nowTime(),
      produto: "",
      fornecedor: "",
      notaFiscal: "",
      quantidade: "",
      lote: "",
      materialConforme: "C",
      responsavel: "",
      observacao: "",
    },
    fields: [
      { key: "data", label: "Data", type: "date", required: true },
      { key: "hora", label: "Hora", type: "time", required: true },
      { key: "produto", label: "Produto", required: true },
      { key: "fornecedor", label: "Fornecedor", required: true },
      { key: "notaFiscal", label: "Nota fiscal", required: true },
      { key: "quantidade", label: "Quantidade", type: "number", required: true },
      { key: "lote", label: "Lote", required: true },
      { key: "materialConforme", label: "Material conforme?", type: "select", options: [{ value: "C", label: "C - Conforme" }, { value: "NC", label: "NC - Não conforme" }], required: true },
      { key: "responsavel", label: "Responsável pelo recebimento", required: true },
      { key: "observacao", label: "Observação", type: "textarea", full: true },
    ],
    table: ["data", "hora", "produto", "fornecedor", "notaFiscal", "quantidade", "lote", "materialConforme", "responsavel"],
    tableLabels: { data: "Data", hora: "Hora", produto: "Produto", fornecedor: "Fornecedor", notaFiscal: "NF", quantidade: "Qtd.", lote: "Lote", materialConforme: "Conforme", responsavel: "Responsável" },
  },
  "teste-desenvolvimento-produtos": {
    label: "Teste e Desenvolvimento de Produtos",
    description: "Controle de teste, qualidade, P&D, processo, avaliação visual e sensorial.",
    icon: "FlaskConical",
    endpoint: "/api/controle-producao/teste-desenvolvimento-produtos",
    emptyForm: {
      codTeste: "",
      dataPlanejada: today(),
      dataProducao: today(),
      produto: "",
      objetivo: "",
      descricao: "",
      responsavel: "",
      descritivoProcesso: "",
      resultadoVisual: "",
      descricaoVisual: "",
      resultadoSensorial: "",
      descricaoSensorial: "",
      textura: "",
      sabor: "",
      aroma: "",
      pesoMedio: "",
      shelfLifeEstimado: "",
      dificuldadesProdutivas: "",
      observacao: "",
      linkFoto: "",
      status: "EM TESTE",
    },
    fields: [
      { key: "codTeste", label: "Código do teste", required: true },
      { key: "dataPlanejada", label: "Data planejada", type: "date" },
      { key: "dataProducao", label: "Data produção", type: "date" },
      { key: "produto", label: "Produto", required: true },
      { key: "objetivo", label: "Objetivo", full: true },
      { key: "descricao", label: "Descrição", type: "textarea", full: true },
      { key: "responsavel", label: "Responsável", required: true },
      { key: "descritivoProcesso", label: "Descritivo do processo", type: "textarea", full: true },
      { key: "resultadoVisual", label: "Resultado visual" },
      { key: "descricaoVisual", label: "Descrição visual" },
      { key: "resultadoSensorial", label: "Resultado sensorial" },
      { key: "descricaoSensorial", label: "Descrição sensorial" },
      { key: "textura", label: "Textura" },
      { key: "sabor", label: "Sabor" },
      { key: "aroma", label: "Aroma" },
      { key: "pesoMedio", label: "Peso médio", type: "number" },
      { key: "shelfLifeEstimado", label: "Shelf life estimado" },
      { key: "dificuldadesProdutivas", label: "Dificuldades produtivas", type: "textarea", full: true },
      { key: "observacao", label: "Observação", type: "textarea", full: true },
      { key: "linkFoto", label: "Link/foto" },
      { key: "status", label: "Status", type: "select", options: [{ value: "EM TESTE", label: "Em teste" }, { value: "APROVADO", label: "Aprovado" }, { value: "REPROVADO", label: "Reprovado" }, { value: "CANCELADO", label: "Cancelado" }], required: true },
    ],
    table: ["codTeste", "dataProducao", "produto", "responsavel", "status", "pesoMedio", "shelfLifeEstimado"],
    tableLabels: { codTeste: "Cod.", dataProducao: "Produção", produto: "Produto", responsavel: "Responsável", status: "Status", pesoMedio: "Peso médio", shelfLifeEstimado: "Shelf life" },
  },
  "rastreabilidade-produto": {
    label: "Rastreabilidade de Produto",
    description: "Controle de lote, validade e quantidade por produto/vendedor.",
    icon: "SearchCheck",
    endpoint: "/api/controle-producao/rastreabilidade-produto",
    emptyForm: {
      data: today(),
      linha: "BROWNIE",
      produto: "",
      sabor: "",
      lote: "",
      validade: "",
      quantidade: "",
      vendedor: "",
      responsavel: "",
      observacao: "",
    },
    fields: [
      { key: "data", label: "Data", type: "date", required: true },
      { key: "linha", label: "Linha", type: "select", options: [{ value: "BROWNIE", label: "Brownie" }, { value: "ALFAJOR", label: "Alfajor" }, { value: "OUTROS", label: "Outros" }] },
      { key: "produto", label: "Produto", required: true },
      { key: "sabor", label: "Sabor" },
      { key: "lote", label: "Lote", required: true },
      { key: "validade", label: "Validade", type: "date", required: true },
      { key: "quantidade", label: "Quantidade", type: "number", required: true },
      { key: "vendedor", label: "Vendedor" },
      { key: "responsavel", label: "Responsável" },
      { key: "observacao", label: "Observação", type: "textarea", full: true },
    ],
    table: ["data", "linha", "produto", "sabor", "lote", "validade", "quantidade", "vendedor"],
    tableLabels: { data: "Data", linha: "Linha", produto: "Produto", sabor: "Sabor", lote: "Lote", validade: "Validade", quantidade: "Qtd.", vendedor: "Vendedor" },
  },
  "retencao-amostras": {
    label: "Retenção de Amostras",
    description: "Controle da planilha física de retenção: produto, lote, fabricação, validade, descarte e assinatura do responsável.",
    icon: "ArchiveRestore",
    endpoint: "/api/controle-producao/retencao-amostras",
    emptyForm: {
      produto: "",
      lote: "",
      dataFabricacao: today(),
      dataValidade: "",
      dataDescarte: "",
      responsavel: "",
      observacao: "",
    },
    fields: [
      { key: "produto", label: "Produto", required: true },
      { key: "lote", label: "Lote", required: true },
      { key: "dataFabricacao", label: "Data de fabricação", type: "date", required: true },
      { key: "dataValidade", label: "Data de validade", type: "date", required: true },
      { key: "dataDescarte", label: "Data de descarte", type: "date" },
      { key: "responsavel", label: "Assinatura / responsável", required: true },
      { key: "observacao", label: "Observação", type: "textarea", full: true },
    ],
    table: ["produto", "lote", "dataFabricacao", "dataValidade", "dataDescarte", "responsavel"],
    tableLabels: { produto: "Produto", lote: "Lote", dataFabricacao: "Fabricação", dataValidade: "Validade", dataDescarte: "Descarte", responsavel: "Responsável" },
  },
};

const statusInfo = {
  meta_batida: { label: "Meta batida", icon: "CheckCircle2", cls: "bg-success/10 text-success border-success/20" },
  com_perda: { label: "Com perda", icon: "AlertTriangle", cls: "bg-warning/10 text-warning border-warning/20" },
  abaixo: { label: "Abaixo da previsão", icon: "TrendingDown", cls: "bg-error/10 text-error border-error/20" },
  sem_previsao: { label: "Sem previsão", icon: "CircleHelp", cls: "bg-muted text-muted-foreground border-border" },
};

const temperatureStatusInfo = {
  conforme: { label: "Dentro do padrão", icon: "CheckCircle2", cls: "bg-success/10 text-success border-success/20" },
  alerta: { label: "Fora do padrão", icon: "AlertTriangle", cls: "bg-error/10 text-error border-error/20" },
  sem_parametro: { label: "Sem parâmetro", icon: "CircleHelp", cls: "bg-warning/10 text-warning border-warning/20" },
};

const hygieneStatusInfo = {
  conforme: { label: "Conforme", icon: "CheckCircle2", cls: "bg-success/10 text-success border-success/20" },
  nao_conforme: { label: "Não conforme", icon: "AlertTriangle", cls: "bg-error/10 text-error border-error/20" },
};

const chartColors = {
  brown: "#5A3528",
  caramel: "#8B5E3C",
  orange: "#F29F05",
  amber: "#D97706",
  light: "#EADFD6",
  axis: "#6B4E3D",
  pie: ["#F29F05", "#D97706", "#8B5E3C", "#5A3528", "#C2410C", "#A16207", "#B45309", "#7C2D12"],
};

const formatNumber = (value) => Number(value || 0).toLocaleString("pt-BR");
const formatDecimal = (value) => Number(value || 0).toLocaleString("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
const formatPercent = (value) => `${Number(value || 0).toLocaleString("pt-BR", { maximumFractionDigits: 1 })}%`;
const formatDate = (value) => {
  if (!value) return "-";
  const [year, month, day] = String(value).split("-");
  return `${day}/${month}/${year}`;
};
const formatDateTime = (value) => {
  const date = new Date(value);
  if (!value || Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};
const cn = (...classes) => classes.filter(Boolean).join(" ");

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-xl border border-border bg-card shadow-card p-3 text-sm max-w-xs">
      {label && <p className="font-body-medium text-card-foreground mb-2">{label}</p>}
      {payload.map((item) => (
        <p key={item.dataKey || item.name} className="text-muted-foreground">
          {item.name}: <span className="font-data text-card-foreground">{formatNumber(item.value)}</span>
        </p>
      ))}
    </div>
  );
};

function MetricCard({ title, value, sub, icon, accent = "primary" }) {
  const accentClass =
    accent === "success"
      ? "bg-success/10 text-success"
      : accent === "warning"
      ? "bg-warning/10 text-warning"
      : accent === "error"
      ? "bg-error/10 text-error"
      : "bg-primary/10 text-primary";

  return (
    <div className="bg-card border border-border rounded-xl shadow-card p-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-xs font-caption text-muted-foreground uppercase tracking-wide">{title}</p>
          <p className="text-2xl font-heading-bold text-card-foreground mt-2">{value}</p>
          {sub ? <p className="text-xs text-muted-foreground mt-1">{sub}</p> : null}
        </div>
        <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${accentClass}`}>
          <Icon name={icon} size={22} />
        </div>
      </div>
    </div>
  );
}

function SummaryBlock({ label, data, icon, mode = "production" }) {
  const isTemperature = mode === "temperature";
  const isHumidityUr = mode === "humidityUr";
  const isHygiene = mode === "hygiene";
  return (
    <div className="rounded-xl border border-[#eadfd6] bg-gradient-to-br from-white to-[#fff7ed] p-4 shadow-sm">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl bg-[#5A3528]/10 text-[#5A3528] flex items-center justify-center">
            <Icon name={icon || "BarChart3"} size={18} />
          </div>
          <p className="font-body-medium text-card-foreground">{label}</p>
        </div>
        <span className="text-xs bg-[#F29F05]/15 text-[#8B5E00] rounded-full px-2.5 py-1 border border-[#F29F05]/25">
          {isTemperature || isHumidityUr || isHygiene ? formatPercent(data?.conformidade) : formatPercent(data?.eficiencia)} {isTemperature || isHumidityUr || isHygiene ? "conformidade" : "eficiência"}
        </span>
      </div>
      {isHygiene ? (
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div className="rounded-lg bg-white/80 border border-[#eadfd6] p-3">
            <p className="text-muted-foreground text-xs">Registros</p>
            <p className="font-data text-[#5A3528] mt-1">{formatNumber(data?.registros)}</p>
          </div>
          <div className="rounded-lg bg-white/80 border border-[#eadfd6] p-3">
            <p className="text-muted-foreground text-xs">Não conformidades</p>
            <p className="font-data text-[#B45309] mt-1">{formatNumber(data?.naoConformidades)}</p>
          </div>
          <div className="rounded-lg bg-white/80 border border-[#eadfd6] p-3">
            <p className="text-muted-foreground text-xs">Itens verificados</p>
            <p className="font-data text-[#5A3528] mt-1">{formatNumber(data?.itensVerificados)}</p>
          </div>
          <div className="rounded-lg bg-white/80 border border-[#eadfd6] p-3">
            <p className="text-muted-foreground text-xs">Registros conformes</p>
            <p className="font-data text-[#D97706] mt-1">{formatNumber(data?.registrosConformes)}</p>
          </div>
        </div>
      ) : isHumidityUr ? (
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div className="rounded-lg bg-white/80 border border-[#eadfd6] p-3">
            <p className="text-muted-foreground text-xs">Medições</p>
            <p className="font-data text-[#5A3528] mt-1">{formatNumber(data?.totalRegistros)}</p>
          </div>
          <div className="rounded-lg bg-white/80 border border-[#eadfd6] p-3">
            <p className="text-muted-foreground text-xs">Alertas</p>
            <p className="font-data text-[#B45309] mt-1">{formatNumber(data?.alertas)}</p>
          </div>
          <div className="rounded-lg bg-white/80 border border-[#eadfd6] p-3">
            <p className="text-muted-foreground text-xs">UR média</p>
            <p className="font-data text-[#D97706] mt-1">{formatDecimal(data?.mediaUmidade)}%</p>
          </div>
          <div className="rounded-lg bg-white/80 border border-[#eadfd6] p-3">
            <p className="text-muted-foreground text-xs">Dentro padrão</p>
            <p className="font-data text-[#5A3528] mt-1">{formatNumber(data?.dentroPadrao)}</p>
          </div>
        </div>
      ) : isTemperature ? (
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div className="rounded-lg bg-white/80 border border-[#eadfd6] p-3">
            <p className="text-muted-foreground text-xs">Medições</p>
            <p className="font-data text-[#5A3528] mt-1">{formatNumber(data?.totalRegistros)}</p>
          </div>
          <div className="rounded-lg bg-white/80 border border-[#eadfd6] p-3">
            <p className="text-muted-foreground text-xs">Alertas</p>
            <p className="font-data text-[#B45309] mt-1">{formatNumber(data?.alertas)}</p>
          </div>
          <div className="rounded-lg bg-white/80 border border-[#eadfd6] p-3">
            <p className="text-muted-foreground text-xs">Temp. média</p>
            <p className="font-data text-[#5A3528] mt-1">{formatDecimal(data?.mediaTemperatura)}°C</p>
          </div>
          <div className="rounded-lg bg-white/80 border border-[#eadfd6] p-3">
            <p className="text-muted-foreground text-xs">Dentro padrão</p>
            <p className="font-data text-[#D97706] mt-1">{formatNumber(data?.dentroPadrao)}</p>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-3">
          <div className="rounded-lg bg-white/80 border border-[#eadfd6] p-3">
            <p className="text-muted-foreground text-xs">Previsão</p>
            <p className="font-data text-[#5A3528] mt-1">{formatNumber(data?.previsao)}</p>
          </div>
          <div className="rounded-lg bg-white/80 border border-[#eadfd6] p-3">
            <p className="text-muted-foreground text-xs">Realizado</p>
            <p className="font-data text-[#D97706] mt-1">{formatNumber(data?.realizado)}</p>
          </div>
          <div className="rounded-lg bg-white/80 border border-[#eadfd6] p-3">
            <p className="text-muted-foreground text-xs">Perdas</p>
            <p className="font-data text-[#B45309] mt-1">{formatNumber(data?.perdas)}</p>
          </div>
        </div>
      )}
    </div>
  );
}

function PageTabs({ activeTab, onChange }) {
  const tabs = [
    {
      id: "producao",
      label: "Registros de produção",
      description: "Cadastro manual de produção, previsão, realizado e perdas.",
      icon: "ClipboardList",
    },
    {
      id: "checklist-diario",
      label: "Checklist e Relatório Diário",
      description: "Preenchimento diário, histórico e envio automático às 19:00.",
      icon: "ListChecks",
    },
    {
      id: "temperatura",
      label: "Controle de Temperatura",
      description: "Medições diárias de temperatura, parâmetros mínimos/máximos e alertas.",
      icon: "Thermometer",
    },
    {
      id: "umidade-ur",
      label: "Controle de Umidade (UR)",
      description: "Medições de umidade relativa para sala de forneamento e manipulação.",
      icon: "Droplets",
    },
    {
      id: "higiene-pessoal",
      label: "Registro de Higiene Pessoal",
      description: "Verificação diária de EPIs, aparência e conformidade dos funcionários.",
      icon: "UserCheck",
    },
    {
      id: "limpeza-edificacao",
      label: "Limpeza da Edificação",
      description: "Verificação diária/mensal de limpeza, máquinas, utensílios e estrutura.",
      icon: "Sparkles",
    },
    {
      id: "limpeza-manutencao-equipamentos",
      label: "Limpeza e Manutenção de Equipamentos",
      description: "Parametrize equipamentos, registre limpeza/manutenção e acompanhe alertas.",
      icon: "Wrench",
    },
    {
      id: "recebimento-mercadorias",
      label: "Recebimento de Mercadoria",
      description: "Registro de matérias-primas, ingredientes, embalagens, NF, lote e responsável.",
      icon: "Truck",
    },
    {
      id: "teste-desenvolvimento-produtos",
      label: "Teste e Desenvolvimento",
      description: "Controle de teste, qualidade, P&D, avaliação visual e sensorial.",
      icon: "FlaskConical",
    },
    {
      id: "rastreabilidade-produto",
      label: "Rastreabilidade de Produto",
      description: "Controle de lote, validade e quantidade por produto.",
      icon: "SearchCheck",
    },
    {
      id: "retencao-amostras",
      label: "Retenção de Amostras",
      description: "Planilha de retenção com lote, fabricação, validade, descarte e responsável.",
      icon: "ArchiveRestore",
    },
  ];

  return (
    <div className="bg-card border border-border rounded-xl shadow-card p-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-2">
      {tabs.map((tab) => {
        const active = activeTab === tab.id;
        return (
          <button
            key={tab.id}
            type="button"
            onClick={() => onChange(tab.id)}
            className={cn(
              "w-full h-full rounded-xl px-4 py-4 text-left transition-smooth border",
              active
                ? "bg-primary text-primary-foreground border-primary shadow-sm"
                : "bg-transparent hover:bg-muted/50 border-transparent text-card-foreground"
            )}
          >
            <div className="flex items-start gap-3">
              <div className={cn("w-10 h-10 rounded-xl flex items-center justify-center", active ? "bg-white/15" : "bg-primary/10 text-primary")}>
                <Icon name={tab.icon} size={20} />
              </div>
              <div className="min-w-0">
                <div className="font-body-semibold text-sm leading-snug">{tab.label}</div>
                <div className={cn("text-xs mt-1 leading-relaxed", active ? "text-primary-foreground/80" : "text-muted-foreground")}>{tab.description}</div>
              </div>
            </div>
          </button>
        );
      })}
      </div>
    </div>
  );
}


const dailyChecklistGroups = [
  {
    key: "acompanhamentoProducao",
    title: "Acompanhamento da Produção",
    items: [
      ["manipulacaoCorreta", "Manipulação correta"],
      ["etiquetagemConferida", "Etiquetagem conferida"],
      ["fluxoProducaoAdequado", "Fluxo da produção adequado"],
    ],
  },
  {
    key: "controleQualidade",
    title: "Controle de Qualidade",
    items: [
      ["realizado", "Controle de qualidade realizado"],
      ["pesoConferido", "Peso conferido"],
      ["aparenciaAvaliada", "Aparência avaliada"],
      ["produtoConformeFichaTecnica", "Produto conforme ficha técnica"],
    ],
  },
  {
    key: "controleValidade",
    title: "Controle de Validade",
    items: [
      ["realizado", "Controle de validade realizado"],
      ["pvpsConferido", "PVPS conferido"],
      ["produtosProximosVencimentoSinalizados", "Produtos próximos ao vencimento sinalizados"],
      ["nenhumProdutoVencido", "Nenhum produto vencido encontrado"],
    ],
  },
  {
    key: "recebimentoMercadorias",
    title: "Recebimento de Mercadorias",
    items: [
      ["realizadoQuandoHouve", "Recebimento de mercadorias conferido quando houve"],
      ["quantidadeConferida", "Quantidade conferida"],
      ["validadeConferida", "Validade conferida"],
      ["loteConferido", "Lote conferido"],
    ],
  },
  {
    key: "expedicao",
    title: "Expedição",
    items: [
      ["conferida", "Expedição conferida"],
      ["pedidosSeparadosAposConferencia", "Pedidos executados e separados após conferência"],
      ["displaysCaixasIdentificados", "Displays e caixas organizados e identificados"],
    ],
  },
];

const blankDailyChecklist = (data = today()) => ({
  data,
  responsavel: "",
  observacao: "",
  itens: dailyChecklistGroups.reduce((groups, group) => {
    groups[group.key] = group.items.reduce((items, [key]) => ({ ...items, [key]: false }), {});
    return groups;
  }, {}),
});

function StyledCheckbox({ checked, onChange, disabled = false, label, compact = false }) {
  return (
    <label
      className={cn(
        "group flex items-center gap-3 rounded-xl border transition-all duration-200",
        compact ? "px-3 py-2.5" : "p-3.5",
        disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer",
        checked
          ? "border-primary/40 bg-primary/10 shadow-sm"
          : "border-border bg-card hover:border-primary/30 hover:bg-primary/[0.04]"
      )}
    >
      <input
        type="checkbox"
        className="sr-only"
        checked={Boolean(checked)}
        disabled={disabled}
        onChange={onChange}
      />
      <span
        className={cn(
          "flex h-5 w-5 shrink-0 items-center justify-center rounded-md border-2 transition-all duration-200",
          checked
            ? "border-primary bg-primary text-primary-foreground shadow-sm"
            : "border-muted-foreground/40 bg-background group-hover:border-primary/60"
        )}
        aria-hidden="true"
      >
        {checked ? <Icon name="Check" size={13} /> : null}
      </span>
      <span className={cn("leading-snug text-card-foreground", compact ? "text-sm" : "text-sm font-body-medium")}>{label}</span>
    </label>
  );
}

function PcpReportHistoryDetails({ sections = [] }) {
  if (!Array.isArray(sections) || sections.length === 0) {
    return <div className="rounded-xl border border-border bg-muted/20 p-4 text-sm text-muted-foreground">Este relatório antigo não possui os detalhes salvos na tela. O PDF continua disponível.</div>;
  }
  return (
    <div className="grid gap-4 xl:grid-cols-2">
      {sections.map((section, sectionIndex) => (
        <div key={`${section.title || "secao"}-${sectionIndex}`} className="rounded-xl border border-border bg-card p-4">
          <h4 className="font-body-semibold text-card-foreground">{section.title || "Detalhes"}</h4>
          <div className="mt-3 space-y-2">
            {(section.lines || []).map((line, lineIndex) => (
              <div key={lineIndex} className={cn("text-sm leading-relaxed", line.color === "#b42318" ? "text-error" : line.color === "#16794b" ? "text-success" : "text-muted-foreground", line.indent ? "pl-3" : "")}>{line.text || "---"}</div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function DailyChecklistSection() {
  const [selectedDate, setSelectedDate] = useState(today());
  const [checklist, setChecklist] = useState(blankDailyChecklist());
  const [settings, setSettings] = useState({ enabled: true, hour: 19, minute: 0, timezone: "America/Sao_Paulo", whatsappEnabled: true, whatsappNumber: "5521985011644", emailEnabled: false, emailTo: "", sendDashboardReport: true, sendPcpReport: true, sendStockReport: true, sendDirectorReport: true });
  const [reports, setReports] = useState([]);
  const [reportFilterDate, setReportFilterDate] = useState("");
  const [expandedReportId, setExpandedReportId] = useState("");
  const [whatsappDiagnostic, setWhatsappDiagnostic] = useState(null);
  const [loadingDaily, setLoadingDaily] = useState(true);
  const [savingDaily, setSavingDaily] = useState(false);
  const [testingWhatsapp, setTestingWhatsapp] = useState(false);
  const [dailyMessage, setDailyMessage] = useState({ type: "", text: "" });

  const request = async (method, path, data) => {
    const response = await axios({ method, url: `${apiBase}${path}`, data, timeout: 180000 });
    return response.data || {};
  };

  const loadDailyData = async (date = selectedDate) => {
    setLoadingDaily(true);
    setDailyMessage({ type: "", text: "" });
    try {
      const [checklistResponse, settingsResponse, reportsResponse, diagnosticResponse] = await Promise.all([
        request("get", `/api/relatorios-diarios/checklist?data=${encodeURIComponent(date)}`),
        request("get", "/api/relatorios-diarios/configuracao"),
        request("get", "/api/relatorios-diarios/historico?limit=30"),
        request("get", "/api/relatorios-diarios/diagnostico-whatsapp"),
      ]);
      setChecklist({ ...blankDailyChecklist(date), ...(checklistResponse.checklist || {}), data: date });
      setSettings((current) => ({ ...current, ...(settingsResponse.settings || {}) }));
      setReports(Array.isArray(reportsResponse.items) ? reportsResponse.items : []);
      setWhatsappDiagnostic(diagnosticResponse.diagnostic || null);
    } catch (error) {
      setDailyMessage({ type: "error", text: error?.response?.data?.message || error?.message || "Erro ao carregar checklist diário." });
    } finally {
      setLoadingDaily(false);
    }
  };

  useEffect(() => { loadDailyData(selectedDate); }, [selectedDate]);

  const toggleItem = (groupKey, itemKey) => {
    if (isFinalized) return;
    setChecklist((current) => ({
      ...current,
      itens: {
        ...(current.itens || {}),
        [groupKey]: {
          ...(current.itens?.[groupKey] || {}),
          [itemKey]: !current.itens?.[groupKey]?.[itemKey],
        },
      },
    }));
  };

  const checklistStats = useMemo(() => {
    const values = dailyChecklistGroups.flatMap((group) => group.items.map(([key]) => Boolean(checklist.itens?.[group.key]?.[key])));
    const completed = values.filter(Boolean).length;
    return { completed, total: values.length, percent: values.length ? Math.round((completed / values.length) * 100) : 0 };
  }, [checklist]);

  const isFinalized = checklist.status === "finalized";
  const finalizedAtLabel = checklist.finalizedAt ? formatDateTime(checklist.finalizedAt) : "";
  const selectedReport = reports.find((report) => report.id === checklist.reportId || report.data === selectedDate);
  const visibleReports = useMemo(() => reportFilterDate ? reports.filter((report) => report.data === reportFilterDate) : reports, [reports, reportFilterDate]);

  const saveChecklist = async () => {
    if (isFinalized) {
      setDailyMessage({ type: "error", text: "A análise deste dia já foi fechada e está disponível apenas para consulta." });
      return;
    }
    setSavingDaily(true);
    try {
      const response = await request("put", `/api/relatorios-diarios/checklist/${encodeURIComponent(selectedDate)}`, checklist);
      setChecklist(response.checklist || checklist);
      setDailyMessage({ type: "success", text: "Andamento salvo. Às 19:00 a análise será fechada automaticamente e enviada em PDF." });
    } catch (error) {
      setDailyMessage({ type: "error", text: error?.response?.data?.message || error?.message || "Erro ao salvar checklist." });
    } finally { setSavingDaily(false); }
  };

  const saveSettings = async () => {
    setSavingDaily(true);
    try {
      const payload = { ...settings, hour: 19, minute: 0, sendPcpReport: true };
      const response = await request("put", "/api/relatorios-diarios/configuracao", payload);
      setSettings(response.settings || payload);
      setDailyMessage({ type: "success", text: "Configuração de envio atualizada." });
    } catch (error) {
      setDailyMessage({ type: "error", text: error?.response?.data?.message || error?.message || "Erro ao salvar configuração." });
    } finally { setSavingDaily(false); }
  };

  const generateNow = async () => {
    if (isFinalized) {
      setDailyMessage({ type: "error", text: "Esta análise já foi fechada. Use Reenviar relatório ou Reabrir checklist." });
      return;
    }
    if (!window.confirm("Fechar a análise agora e enviar o PDF? Depois, você ainda poderá reabrir o checklist ou reenviar o relatório quantas vezes precisar.")) return;
    setSavingDaily(true);
    setDailyMessage({ type: "", text: "" });
    try {
      await request("put", `/api/relatorios-diarios/checklist/${encodeURIComponent(selectedDate)}`, checklist);
      const response = await request("post", "/api/relatorios-diarios/gerar", { data: selectedDate, force: true });
      setDailyMessage({ type: "success", text: `Relatório gerado. WhatsApp: ${response.report?.whatsappStatus || "não informado"}; e-mail: ${response.report?.emailStatus || "não informado"}.` });
      await loadDailyData(selectedDate);
    } catch (error) {
      setDailyMessage({ type: "error", text: error?.response?.data?.message || error?.message || "Erro ao gerar relatório." });
    } finally { setSavingDaily(false); }
  };

  const resendReport = async () => {
    if (!window.confirm("Reenviar o PDF deste checklist para os destinatários configurados?")) return;
    setSavingDaily(true);
    setDailyMessage({ type: "", text: "" });
    try {
      const response = await request("post", "/api/relatorios-diarios/gerar", { data: selectedDate, force: true });
      setDailyMessage({ type: "success", text: `Relatório reenviado. WhatsApp: ${response.report?.whatsappStatus || "não informado"}; e-mail: ${response.report?.emailStatus || "não informado"}.` });
      await loadDailyData(selectedDate);
    } catch (error) {
      setDailyMessage({ type: "error", text: error?.response?.data?.message || error?.message || "Erro ao reenviar relatório." });
    } finally { setSavingDaily(false); }
  };

  const reopenChecklist = async () => {
    if (!window.confirm("Reabrir este checklist? Ele voltará a aceitar alterações. Os PDFs já enviados continuarão no histórico.")) return;
    setSavingDaily(true);
    setDailyMessage({ type: "", text: "" });
    try {
      const response = await request("post", `/api/relatorios-diarios/checklist/${encodeURIComponent(selectedDate)}/reabrir`);
      setChecklist(response.checklist || { ...checklist, status: "draft", finalizedAt: "", finalizedBy: "", reportId: "" });
      setDailyMessage({ type: "success", text: "Checklist reaberto. Você já pode editar, salvar e fechar/enviar novamente." });
      await loadDailyData(selectedDate);
    } catch (error) {
      setDailyMessage({ type: "error", text: error?.response?.data?.message || error?.message || "Erro ao reabrir checklist." });
    } finally { setSavingDaily(false); }
  };

  const testWhatsapp = async () => {
    const phone = String(settings.whatsappNumber || "").trim();
    if (!phone) {
      setDailyMessage({ type: "error", text: "Informe o número do WhatsApp antes de testar." });
      return;
    }

    setTestingWhatsapp(true);
    setDailyMessage({ type: "", text: "" });
    try {
      const settingsPayload = { ...settings, hour: 19, minute: 0, sendPcpReport: true };
      const settingsResponse = await request("put", "/api/relatorios-diarios/configuracao", settingsPayload);
      setSettings(settingsResponse.settings || settingsPayload);

      const response = await request("post", "/api/relatorios-diarios/teste-whatsapp", { whatsappNumber: phone });
      const test = response.test || {};
      setWhatsappDiagnostic((current) => ({
        ...(current || {}),
        configuredNumber: test.phone || phone,
        normalizedNumber: test.phone || phone,
        lastTest: {
          status: test.status || "accepted",
          phone: test.phone || phone,
          providerStatus: test.providerStatus || "accepted",
          providerMessageId: test.providerMessageId || "",
          testedAt: test.testedAt || new Date().toISOString(),
        },
      }));
      setDailyMessage({
        type: "success",
        text: `Teste aceito pelo ChatCenter para ${test.phone || phone}${test.providerMessageId ? ` · ID ${test.providerMessageId}` : ""}. Confirme agora se a mensagem chegou no WhatsApp.`,
      });
    } catch (error) {
      const message = error?.response?.data?.message || error?.message || "Erro no teste de envio pelo WhatsApp.";
      setDailyMessage({ type: "error", text: `Teste falhou: ${message}` });
      try {
        const diagnosticResponse = await request("get", "/api/relatorios-diarios/diagnostico-whatsapp");
        setWhatsappDiagnostic(diagnosticResponse.diagnostic || null);
      } catch {}
    } finally {
      setTestingWhatsapp(false);
    }
  };

  const whatsappStatusLabel = (status) => {
    const value = String(status || "").toLowerCase();
    if (value === "accepted") return "Aceito pelo ChatCenter";
    if (value === "sent") return "Aceito pelo ChatCenter (registro antigo)";
    if (value === "error") return "Erro no envio";
    if (value === "disabled") return "Desativado";
    if (value === "pending") return "Pendente";
    return status || "Não informado";
  };

  if (loadingDaily) return <div className="bg-card rounded-xl border border-border p-8 text-center text-muted-foreground">Carregando checklist e relatórios...</div>;

  return (
    <div id="pcp-checklist-diario" className="space-y-6">
      {dailyMessage.text ? <div className={cn("rounded-xl border px-4 py-3 text-sm", dailyMessage.type === "error" ? "bg-error/10 border-error/20 text-error" : "bg-success/10 border-success/20 text-success")}>{dailyMessage.text}</div> : null}

      <section className="bg-card rounded-xl border border-border shadow-card p-6 space-y-5">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div><h2 className="text-xl font-heading-semibold text-card-foreground">Checklist diário do PCP</h2><p className="text-sm text-muted-foreground mt-1">A pessoa responsável preenche durante o dia. Às 19:00 o sistema fecha a análise, gera o PDF e envia ao número configurado. Depois, o checklist pode ser reaberto ou o relatório pode ser reenviado.</p></div>
          <div className="grid w-full grid-cols-1 gap-3 sm:grid-cols-2 lg:w-auto lg:min-w-[470px]">
            <div className={cn(
              "flex min-h-[72px] items-center gap-3 rounded-2xl border px-4 py-3",
              isFinalized
                ? "border-success/25 bg-success/10"
                : "border-warning/25 bg-warning/10"
            )}>
              <div className={cn(
                "flex h-10 w-10 shrink-0 items-center justify-center rounded-xl",
                isFinalized ? "bg-success/15 text-success" : "bg-warning/15 text-warning"
              )}>
                <Icon name={isFinalized ? "LockKeyhole" : "Clock3"} size={18} />
              </div>
              <div className="min-w-0">
                <div className="text-xs font-body-medium uppercase tracking-wide text-muted-foreground">Status da análise</div>
                <div className={cn("mt-1 font-body-semibold leading-snug", isFinalized ? "text-success" : "text-warning")}>
                  {isFinalized ? `Análise fechada${finalizedAtLabel ? ` · ${finalizedAtLabel}` : ""}` : "Em preenchimento até 19:00"}
                </div>
              </div>
            </div>

            <div className="flex min-h-[72px] items-center gap-3 rounded-2xl border border-primary/20 bg-primary/[0.07] px-4 py-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/15 text-primary">
                <Icon name="ListChecks" size={18} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-3">
                  <div className="text-xs font-body-medium uppercase tracking-wide text-muted-foreground">Progresso</div>
                  <div className="text-sm font-body-semibold text-primary">{checklistStats.percent}%</div>
                </div>
                <div className="mt-1 font-body-semibold text-card-foreground">{checklistStats.completed} de {checklistStats.total} itens concluídos</div>
                <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-primary/15">
                  <div className="h-full rounded-full bg-primary transition-all duration-300" style={{ width: `${checklistStats.percent}%` }} />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4"><Input type="date" label="Data da análise" value={selectedDate} onChange={(event) => setSelectedDate(event.target.value)} /><Input disabled={isFinalized} label="Responsável" value={checklist.responsavel || ""} onChange={(event) => setChecklist((current) => ({ ...current, responsavel: event.target.value }))} /></div>
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          {dailyChecklistGroups.map((group) => (
            <div key={group.key} className="rounded-2xl border border-border bg-muted/20 p-4">
              <div className="mb-3 flex items-center gap-2">
                <div className="h-2 w-2 rounded-full bg-primary" />
                <h3 className="font-body-semibold text-card-foreground">{group.title}</h3>
              </div>
              <div className="space-y-2.5">
                {group.items.map(([itemKey, label]) => (
                  <StyledCheckbox
                    key={itemKey}
                    disabled={isFinalized}
                    checked={Boolean(checklist.itens?.[group.key]?.[itemKey])}
                    onChange={() => toggleItem(group.key, itemKey)}
                    label={label}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
        <Input disabled={isFinalized} label="Observação/análise do dia" value={checklist.observacao || ""} onChange={(event) => setChecklist((current) => ({ ...current, observacao: event.target.value }))} placeholder="Ocorrências, desvios, ações corretivas ou observações gerais" />
        {isFinalized && (selectedReport?.pcpPdfPath || selectedReport?.pcpSummaryPdfPath || selectedReport?.pcpDetailedPdfPath) ? (
          <div className="rounded-xl border border-success/20 bg-success/10 px-4 py-3 text-sm text-card-foreground">
            <div className="font-body-semibold">Esta análise foi encerrada.</div>
            <div className="mt-2 flex flex-wrap gap-3">
              <a className="font-body-semibold text-primary underline" href={`${apiBase}/api/relatorios-diarios/${selectedReport.id}/arquivo/pcp-summary-pdf`} target="_blank" rel="noreferrer">Abrir PCP resumido</a>
              <a className="font-body-semibold text-primary underline" href={`${apiBase}/api/relatorios-diarios/${selectedReport.id}/arquivo/pcp-detailed-pdf`} target="_blank" rel="noreferrer">Abrir PCP detalhado</a>
              {selectedReport.stockPdfPath ? <a className="font-body-semibold text-primary underline" href={`${apiBase}/api/relatorios-diarios/${selectedReport.id}/arquivo/stock-pdf`} target="_blank" rel="noreferrer">Abrir relatório de estoques</a> : null}
              {selectedReport.directorPdfPath ? <a className="font-body-semibold text-primary underline" href={`${apiBase}/api/relatorios-diarios/${selectedReport.id}/arquivo/director-pdf`} target="_blank" rel="noreferrer">Abrir Relatório 2 da Diretoria</a> : null}
            </div>
            <div className="mt-2 text-muted-foreground">Você pode reenviar os relatórios ou reabrir o checklist sem apagar o histórico anterior.</div>
          </div>
        ) : null}
        <div className="flex flex-wrap gap-3">
          {!isFinalized ? <>
            <Button loading={savingDaily} iconName="Save" onClick={saveChecklist}>Salvar andamento</Button>
            <Button loading={savingDaily} variant="outline" iconName="Lock" onClick={generateNow}>Fechar e enviar agora</Button>
          </> : <>
            <Button loading={savingDaily} iconName="Send" onClick={resendReport}>Reenviar relatório</Button>
            <Button loading={savingDaily} variant="outline" iconName="UnlockKeyhole" onClick={reopenChecklist}>Reabrir checklist</Button>
          </>}
        </div>
      </section>

      <section className="bg-card rounded-xl border border-border shadow-card p-6 space-y-5">
        <div><h2 className="text-xl font-heading-semibold text-card-foreground">Fechamento e envio automático</h2><p className="text-sm text-muted-foreground mt-1">Todos os dias, a partir das 19:00, o checklist do dia é fechado, transformado em PDF e enviado automaticamente. O fechamento pode ser reaberto e o PDF pode ser reenviado quando necessário.</p></div>
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          <div className="rounded-xl border border-border bg-muted/30 px-4 py-3"><div className="text-xs text-muted-foreground">Horário fixo de fechamento</div><div className="mt-1 font-heading-semibold text-card-foreground">19:00</div></div>
          <Input label="Número do WhatsApp" value={settings.whatsappNumber || ""} onChange={(event) => setSettings((current) => ({ ...current, whatsappNumber: event.target.value }))} />
          <Input type="email" label="E-mail do destinatário" value={settings.emailTo || ""} onChange={(event) => setSettings((current) => ({ ...current, emailTo: event.target.value }))} />
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
          <StyledCheckbox compact checked={Boolean(settings.enabled)} onChange={(event) => setSettings((current) => ({ ...current, enabled: event.target.checked }))} label="Rotina diária ativa" />
          <StyledCheckbox compact checked={Boolean(settings.whatsappEnabled)} onChange={(event) => setSettings((current) => ({ ...current, whatsappEnabled: event.target.checked }))} label="Enviar no WhatsApp" />
          <StyledCheckbox compact checked={Boolean(settings.emailEnabled)} onChange={(event) => setSettings((current) => ({ ...current, emailEnabled: event.target.checked }))} label="Enviar por e-mail" />
          <StyledCheckbox compact checked={Boolean(settings.sendDashboardReport)} onChange={(event) => setSettings((current) => ({ ...current, sendDashboardReport: event.target.checked }))} label="Enviar também o relatório geral" />
          <StyledCheckbox compact checked={Boolean(settings.sendStockReport)} onChange={(event) => setSettings((current) => ({ ...current, sendStockReport: event.target.checked }))} label="Enviar relatório de Estoque + Produtos Acabados" />
          <StyledCheckbox compact checked={Boolean(settings.sendDirectorReport)} onChange={(event) => setSettings((current) => ({ ...current, sendDirectorReport: event.target.checked }))} label="Enviar Relatório 2 resumido para Diretoria" />
          <StyledCheckbox compact checked disabled onChange={() => {}} label="PDF do checklist PCP obrigatório" />
        </div>
        <div className="rounded-xl border border-warning/20 bg-warning/10 px-4 py-3 text-sm text-card-foreground">WhatsApp configurado para um único destinatário. Número atual: <b>{settings.whatsappNumber || "não informado"}</b>. O envio usa a mesma integração ChatCenter já utilizada nas cobranças.</div>

        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <div className="rounded-xl border border-border bg-muted/30 px-4 py-3">
            <div className="text-xs text-muted-foreground">ChatCenter</div>
            <div className={cn("mt-1 font-body-semibold", whatsappDiagnostic?.enabled && whatsappDiagnostic?.keyConfigured ? "text-success" : "text-error")}>{whatsappDiagnostic?.enabled ? "Ativado" : "Desativado"} · {whatsappDiagnostic?.keyConfigured ? "Chave configurada" : "Sem chave"}</div>
          </div>
          <div className="rounded-xl border border-border bg-muted/30 px-4 py-3">
            <div className="text-xs text-muted-foreground">Modo de envio</div>
            <div className={cn("mt-1 font-body-semibold", whatsappDiagnostic?.dryRun ? "text-warning" : "text-card-foreground")}>{whatsappDiagnostic?.dryRun ? "Simulação — não envia" : "Envio real"}</div>
          </div>
          <div className="rounded-xl border border-border bg-muted/30 px-4 py-3">
            <div className="text-xs text-muted-foreground">Número normalizado</div>
            <div className="mt-1 font-body-semibold text-card-foreground">{whatsappDiagnostic?.normalizedNumber || settings.whatsappNumber || "---"}</div>
          </div>
          <div className="rounded-xl border border-border bg-muted/30 px-4 py-3">
            <div className="text-xs text-muted-foreground">Último teste</div>
            <div className={cn("mt-1 font-body-semibold", whatsappDiagnostic?.lastTest?.status === "error" ? "text-error" : "text-card-foreground")}>{whatsappDiagnostic?.lastTest ? `${whatsappStatusLabel(whatsappDiagnostic.lastTest.status)}${whatsappDiagnostic.lastTest.testedAt ? ` · ${formatDateTime(whatsappDiagnostic.lastTest.testedAt)}` : ""}` : "Ainda não realizado"}</div>
            {whatsappDiagnostic?.lastTest?.error ? <div className="mt-1 text-xs text-error">{whatsappDiagnostic.lastTest.error}</div> : null}
          </div>
        </div>

        <div className="flex flex-wrap gap-3">
          <Button loading={savingDaily} iconName="Settings2" onClick={saveSettings}>Salvar configuração</Button>
          <Button loading={testingWhatsapp} variant="outline" iconName="MessageCircleMore" onClick={testWhatsapp}>Testar envio no WhatsApp</Button>
        </div>
      </section>

      <section className="bg-card rounded-xl border border-border shadow-card overflow-hidden">
        <div className="p-6 border-b border-border flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between"><div><h2 className="text-xl font-heading-semibold text-card-foreground">Histórico de relatórios</h2><p className="text-sm text-muted-foreground mt-1">Filtre por uma data exata para visualizar somente os PDFs daquele dia.</p></div><div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-end"><Input type="date" label="Data do relatório" value={reportFilterDate} onChange={(event) => setReportFilterDate(event.target.value)} className="sm:min-w-[190px]" /><Button type="button" variant="outline" iconName="RotateCcw" onClick={() => setReportFilterDate("")}>Mostrar todos</Button></div></div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/60"><tr><th className="p-4 text-left text-xs uppercase text-muted-foreground">Data</th><th className="p-4 text-left text-xs uppercase text-muted-foreground">Origem</th><th className="p-4 text-left text-xs uppercase text-muted-foreground">WhatsApp</th><th className="p-4 text-left text-xs uppercase text-muted-foreground">E-mail</th><th className="p-4 text-left text-xs uppercase text-muted-foreground">Arquivos e detalhes</th></tr></thead>
            <tbody>
              {visibleReports.length === 0 ? <tr><td colSpan={5} className="p-8 text-center text-muted-foreground">Nenhum relatório encontrado para a data escolhida.</td></tr> : visibleReports.map((report) => (
                <React.Fragment key={report.id}>
                  <tr className="border-b border-border">
                    <td className="p-4">{String(report.data || "").split("-").reverse().join("/")}</td>
                    <td className="p-4">{report.source === "scheduler" ? "Fechamento automático 19:00" : "Fechamento manual"}</td>
                    <td className="p-4"><div>{whatsappStatusLabel(report.whatsappStatus)}</div>{report.whatsappMessageId ? <div className="mt-1 text-xs text-muted-foreground">ID: {report.whatsappMessageId}</div> : null}{report.whatsappProviderStatus ? <div className="mt-1 text-xs text-muted-foreground">Provedor: {report.whatsappProviderStatus}</div> : null}{Array.isArray(report.errors) && report.errors.some((item) => String(item).startsWith("WhatsApp:")) ? <div className="mt-1 text-xs text-error">{report.errors.find((item) => String(item).startsWith("WhatsApp:"))}</div> : null}</td>
                    <td className="p-4">{report.emailStatus}</td>
                    <td className="p-4"><div className="flex flex-wrap gap-2">{report.dashboardPdfPath ? <a className="text-primary underline" href={`${apiBase}/api/relatorios-diarios/${report.id}/arquivo/dashboard-pdf`} target="_blank" rel="noreferrer">PDF geral</a> : null}{report.directorPdfPath ? <a className="text-primary underline" href={`${apiBase}/api/relatorios-diarios/${report.id}/arquivo/director-pdf`} target="_blank" rel="noreferrer">Diretoria</a> : null}{report.stockPdfPath ? <a className="text-primary underline" href={`${apiBase}/api/relatorios-diarios/${report.id}/arquivo/stock-pdf`} target="_blank" rel="noreferrer">Estoques</a> : null}{(report.pcpSummaryPdfPath || report.pcpPdfPath) ? <a className="text-primary underline" href={`${apiBase}/api/relatorios-diarios/${report.id}/arquivo/pcp-summary-pdf`} target="_blank" rel="noreferrer">PCP resumido</a> : null}{(report.pcpDetailedPdfPath || report.pcpPdfPath) ? <a className="text-primary underline" href={`${apiBase}/api/relatorios-diarios/${report.id}/arquivo/pcp-detailed-pdf`} target="_blank" rel="noreferrer">PCP detalhado</a> : null}<button type="button" onClick={() => setExpandedReportId((current) => current === report.id ? "" : report.id)} className="font-body-semibold text-primary underline">{expandedReportId === report.id ? "Ocultar detalhes" : "Ver detalhes"}</button></div></td>
                  </tr>
                  {expandedReportId === report.id ? <tr className="border-b border-border bg-muted/10"><td colSpan={5} className="p-4"><PcpReportHistoryDetails sections={report.pcpDetails || []} /></td></tr> : null}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function ProductionSection({
  products,
  records,
  summary,
  filteredTotals,
  charts,
  period,
  setPeriod,
  form,
  setForm,
  editingId,
  setEditingId,
  search,
  setSearch,
  loading,
  saving,
  setSaving,
  message,
  setMessage,
  reload,
}) {
  useEffect(() => {
    const previsao = Number(form.previsaoQuantidade || 0);
    const realizado = Number(form.realizadoQuantidade || 0);
    if (!editingId && previsao > 0 && realizado >= 0 && form.realizadoQuantidade !== "") {
      setForm((prev) => ({ ...prev, perdaQuantidade: String(Math.max(0, previsao - realizado)) }));
    }
  }, [editingId, form.previsaoQuantidade, form.realizadoQuantidade, setForm]);

  const filteredRecords = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return records;
    return records.filter((item) => [item.produtoNome, item.motivoPerda, item.observacao].some((value) => String(value || "").toLowerCase().includes(term)));
  }, [records, search]);

  const resetForm = () => {
    setForm({ ...blankProductionForm, data: today() });
    setEditingId(null);
  };

  const submitRecord = async (event) => {
    event.preventDefault();
    setSaving(true);
    setMessage({ type: "", text: "" });
    try {
      const payload = {
        data: form.data,
        produtoNome: form.produtoNome,
        previsaoQuantidade: Number(form.previsaoQuantidade || 0),
        realizadoQuantidade: Number(form.realizadoQuantidade || 0),
        perdaQuantidade: Number(form.perdaQuantidade || 0),
        motivoPerda: form.motivoPerda,
        observacao: form.observacao,
      };
      if (editingId) {
        await axios.put(`${apiBase}/api/controle-producao/${editingId}`, payload);
        setMessage({ type: "success", text: "Produção atualizada com sucesso." });
      } else {
        await axios.post(`${apiBase}/api/controle-producao`, payload);
        setMessage({ type: "success", text: "Produção cadastrada com sucesso." });
      }
      resetForm();
      await reload();
    } catch (error) {
      setMessage({ type: "error", text: error?.response?.data?.message || error?.message || "Erro ao salvar produção." });
    } finally {
      setSaving(false);
    }
  };

  const editRecord = (record) => {
    setEditingId(record.id);
    setForm({
      data: record.data || today(),
      produtoNome: record.produtoNome || "",
      previsaoQuantidade: record.previsaoQuantidade || "",
      realizadoQuantidade: record.realizadoQuantidade || "",
      perdaQuantidade: record.perdaQuantidade || "",
      motivoPerda: record.motivoPerda || "",
      observacao: record.observacao || "",
    });
    document.getElementById("pcp-producao-form")?.scrollIntoView({ behavior: "smooth" });
  };

  const deleteRecord = async (record) => {
    if (!window.confirm(`Remover produção de ${record.produtoNome} em ${formatDate(record.data)}?`)) return;
    setSaving(true);
    try {
      await axios.delete(`${apiBase}/api/controle-producao/${record.id}`);
      setMessage({ type: "success", text: "Registro removido com sucesso." });
      await reload();
    } catch (error) {
      setMessage({ type: "error", text: error?.response?.data?.message || error?.message || "Erro ao remover registro." });
    } finally {
      setSaving(false);
    }
  };

  const lossPieData = charts.lossReasons?.length ? charts.lossReasons : [{ motivo: "Sem perdas no período", quantidade: 1, empty: true }];
  const [productDraft, setProductDraft] = useState("");
  const [editingProductOriginal, setEditingProductOriginal] = useState(null);
  const [productSaving, setProductSaving] = useState(false);

  const resetProductForm = () => {
    setProductDraft("");
    setEditingProductOriginal(null);
  };

  const selectProduct = (product) => {
    setForm((prev) => ({ ...prev, produtoNome: product }));
    document.getElementById("pcp-producao-form")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const editProduct = (product) => {
    setProductDraft(product);
    setEditingProductOriginal(product);
    document.getElementById("pcp-product-form")?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const submitProduct = async (event) => {
    event.preventDefault();
    setProductSaving(true);
    setMessage({ type: "", text: "" });
    try {
      const payload = { nome: productDraft };
      if (editingProductOriginal) {
        await axios.put(`${apiBase}/api/controle-producao/produtos/${encodeURIComponent(editingProductOriginal)}`, payload);
        if (form.produtoNome === editingProductOriginal) setForm((prev) => ({ ...prev, produtoNome: productDraft }));
        setMessage({ type: "success", text: "Produto atualizado com sucesso." });
      } else {
        await axios.post(`${apiBase}/api/controle-producao/produtos`, payload);
        setMessage({ type: "success", text: "Produto cadastrado com sucesso." });
      }
      resetProductForm();
      await reload();
    } catch (error) {
      setMessage({ type: "error", text: error?.response?.data?.message || error?.message || "Erro ao salvar produto." });
    } finally {
      setProductSaving(false);
    }
  };

  const deleteProduct = async (product) => {
    if (!window.confirm(`Remover o produto ${product} da lista de produtos cadastrados? Os registros antigos continuam no histórico.`)) return;
    setProductSaving(true);
    setMessage({ type: "", text: "" });
    try {
      await axios.delete(`${apiBase}/api/controle-producao/produtos/${encodeURIComponent(product)}`);
      if (form.produtoNome === product) setForm((prev) => ({ ...prev, produtoNome: "" }));
      if (editingProductOriginal === product) resetProductForm();
      setMessage({ type: "success", text: "Produto removido com sucesso." });
      await reload();
    } catch (error) {
      setMessage({ type: "error", text: error?.response?.data?.message || error?.message || "Erro ao remover produto." });
    } finally {
      setProductSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {message.text ? (
        <div className={`rounded-xl border px-4 py-3 text-sm flex gap-2 ${message.type === "error" ? "bg-error/10 text-error border-error/20" : "bg-success/10 text-success border-success/20"}`}>
          <Icon name={message.type === "error" ? "AlertCircle" : "CheckCircle2"} size={18} />
          {message.text}
        </div>
      ) : null}

      <section className="bg-card rounded-xl border border-border shadow-card p-6 overflow-hidden relative">
        <div className="absolute top-0 right-0 w-40 h-40 bg-[#F29F05]/10 rounded-full blur-3xl -mr-16 -mt-16" />
        <div className="relative z-10 flex flex-col xl:flex-row xl:items-start xl:justify-between gap-5 mb-5">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-[#F29F05]/25 bg-[#F29F05]/10 px-3 py-1 text-xs text-[#8B5E00] mb-3">
              <Icon name="PackageCheck" size={14} />
              Produtos do PCP
            </div>
            <h2 className="text-xl font-heading-semibold text-card-foreground">Produtos cadastrados</h2>
            <p className="text-sm text-muted-foreground mt-1">Adicione, edite, remova ou selecione os produtos usados nos registros de produção.</p>
          </div>
          <form id="pcp-product-form" onSubmit={submitProduct} className="w-full xl:w-[460px] rounded-xl border border-[#eadfd6] bg-[#fff7ed] p-4">
            <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3 items-end">
              <Input
                label={editingProductOriginal ? "Editar produto" : "Novo produto"}
                placeholder="Ex: BROWNIE - CREME DE AVELA"
                value={productDraft}
                onChange={(e) => setProductDraft(e.target.value)}
                required
              />
              <Button type="submit" loading={productSaving} iconName={editingProductOriginal ? "Save" : "Plus"}>
                {editingProductOriginal ? "Salvar" : "Adicionar"}
              </Button>
            </div>
            {editingProductOriginal ? (
              <button type="button" onClick={resetProductForm} className="text-xs text-muted-foreground hover:text-card-foreground mt-3">
                Cancelar edição de {editingProductOriginal}
              </button>
            ) : null}
          </form>
        </div>

        {products.length === 0 ? (
          <div className="relative z-10 rounded-xl border border-dashed border-border bg-muted/30 p-6 text-center text-sm text-muted-foreground">
            Nenhum produto cadastrado ainda. Use o campo acima para adicionar o primeiro produto.
          </div>
        ) : (
          <div className="relative z-10 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-3">
            {products.map((product) => {
              const selected = form.produtoNome === product;
              return (
                <div
                  key={product}
                  className={`rounded-xl border p-4 transition-smooth ${
                    selected
                      ? "border-primary bg-primary text-primary-foreground shadow-sm"
                      : "border-[#eadfd6] bg-[#fffaf1] hover:bg-[#fff3da] hover:border-[#F29F05]/50 text-card-foreground"
                  }`}
                >
                  <button type="button" onClick={() => selectProduct(product)} className="w-full text-left" title={`Selecionar ${product}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="text-sm font-body-semibold leading-snug break-words">{product}</div>
                      <Icon name={selected ? "CheckCircle2" : "MousePointerClick"} size={17} className={selected ? "text-primary-foreground" : "text-[#D97706]"} />
                    </div>
                    <div className={`mt-3 text-xs ${selected ? "text-primary-foreground/80" : "text-muted-foreground"}`}>
                      Clique para lançar produção
                    </div>
                  </button>
                  <div className={`mt-4 pt-3 border-t flex items-center justify-end gap-1 ${selected ? "border-white/20" : "border-[#eadfd6]"}`}>
                    <button type="button" onClick={() => editProduct(product)} className={`p-2 rounded-lg transition-smooth ${selected ? "hover:bg-white/15 text-primary-foreground" : "hover:bg-white text-muted-foreground hover:text-card-foreground"}`} title="Editar produto">
                      <Icon name="Edit3" size={15} />
                    </button>
                    <button type="button" onClick={() => deleteProduct(product)} className={`p-2 rounded-lg transition-smooth ${selected ? "hover:bg-white/15 text-primary-foreground" : "hover:bg-error/10 text-error"}`} title="Remover produto">
                      <Icon name="Trash2" size={15} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      <section className="grid grid-cols-1 xl:grid-cols-[0.95fr_1.05fr] gap-6">
        <form id="pcp-producao-form" onSubmit={submitRecord} className="bg-card rounded-xl border border-border shadow-card p-6 space-y-5">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h2 className="text-xl font-heading-semibold text-card-foreground">{editingId ? "Editar registro de produção" : "Cadastrar produção do dia"}</h2>
              <p className="text-sm text-muted-foreground mt-1">Ex: previsão 5.000, realizado 4.800, perdas 200 e motivo da perda.</p>
            </div>
            {editingId ? <Button type="button" variant="ghost" size="sm" onClick={resetForm}>Cancelar</Button> : null}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input type="date" label="Data" value={form.data} onChange={(e) => setForm((p) => ({ ...p, data: e.target.value }))} required />
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Produto *</label>
              <input
                list="produtos-producao"
                value={form.produtoNome}
                onChange={(e) => setForm((p) => ({ ...p, produtoNome: e.target.value }))}
                placeholder="Ex: BROWNIE - CREME DE AVELA"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                required
              />
              <datalist id="produtos-producao">
                {products.map((product) => (
                  <option key={product} value={product} />
                ))}
              </datalist>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Input type="number" min="1" step="1" label="Previsão" placeholder="5000" value={form.previsaoQuantidade} onChange={(e) => setForm((p) => ({ ...p, previsaoQuantidade: e.target.value }))} required />
            <Input type="number" min="0" step="1" label="Realizado" placeholder="4800" value={form.realizadoQuantidade} onChange={(e) => setForm((p) => ({ ...p, realizadoQuantidade: e.target.value }))} required />
            <Input type="number" min="0" step="1" label="Perdas" placeholder="200" value={form.perdaQuantidade} onChange={(e) => setForm((p) => ({ ...p, perdaQuantidade: e.target.value }))} />
          </div>

          <Input label="Motivo da perda" placeholder="Ex: falha na máquina, corte errado, passou do ponto" value={form.motivoPerda} onChange={(e) => setForm((p) => ({ ...p, motivoPerda: e.target.value }))} />
          <Input label="Observação" placeholder="Observação interna opcional" value={form.observacao} onChange={(e) => setForm((p) => ({ ...p, observacao: e.target.value }))} />

          <div className="grid grid-cols-1 gap-3 rounded-xl border border-border bg-muted/40 p-4 text-sm sm:grid-cols-3">
            <div>
              <p className="text-muted-foreground text-xs">Diferença</p>
              <p className="font-data text-card-foreground mt-1">{formatNumber(Math.max(0, Number(form.previsaoQuantidade || 0) - Number(form.realizadoQuantidade || 0)))}</p>
            </div>
            <div>
              <p className="text-muted-foreground text-xs">Eficiência</p>
              <p className="font-data text-card-foreground mt-1">{formatPercent(Number(form.previsaoQuantidade || 0) > 0 ? (Number(form.realizadoQuantidade || 0) / Number(form.previsaoQuantidade || 0)) * 100 : 0)}</p>
            </div>
            <div>
              <p className="text-muted-foreground text-xs">Perda</p>
              <p className="font-data text-card-foreground mt-1">{formatPercent(Number(form.previsaoQuantidade || 0) > 0 ? (Number(form.perdaQuantidade || 0) / Number(form.previsaoQuantidade || 0)) * 100 : 0)}</p>
            </div>
          </div>

          <Button type="submit" fullWidth loading={saving} iconName={editingId ? "Save" : "Plus"}>{editingId ? "Salvar alterações" : "Cadastrar produção"}</Button>
        </form>

        <div className="bg-card rounded-xl border border-border shadow-card p-6 space-y-5">
          <div className="flex flex-col gap-4">
            <div>
              <h2 className="text-xl font-heading-semibold text-card-foreground">Resumo BI da produção</h2>
              <p className="text-sm text-muted-foreground mt-1">Quantidade de previsão, realizado e perdas por período.</p>
            </div>
            <div className="rounded-xl border border-[#eadfd6] bg-[#fff7ed] p-4">
              <div className="flex items-center gap-2 mb-3">
                <Icon name="CalendarRange" size={17} className="text-[#D97706]" />
                <p className="text-sm font-body-medium text-card-foreground">Filtrar BI por data</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Input type="date" label="De" value={period.de} onChange={(e) => setPeriod((p) => ({ ...p, de: e.target.value }))} />
                <Input type="date" label="Até" value={period.ate} onChange={(e) => setPeriod((p) => ({ ...p, ate: e.target.value }))} />
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-1 gap-4">
            <SummaryBlock label="Hoje" data={summary?.dia} icon="Sun" />
            <SummaryBlock label="Semana" data={summary?.semana} icon="CalendarDays" />
            <SummaryBlock label="Mês" data={summary?.mes} icon="CalendarCheck" />
          </div>
          <div className="rounded-xl border border-[#F29F05]/30 bg-gradient-to-r from-[#5A3528] to-[#8B5E3C] p-4 text-white">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-body-medium">Período filtrado</p>
                <p className="text-xs text-white/75 mt-1">{formatDate(period.de)} até {formatDate(period.ate)}</p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-heading-bold text-[#F29F05]">{formatPercent(filteredTotals?.eficiencia)}</p>
                <p className="text-xs text-white/75">eficiência</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="grid grid-cols-1 xl:grid-cols-[1.4fr_0.8fr] gap-6">
        <div className="bg-card rounded-xl border border-border shadow-card p-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
            <div>
              <h2 className="text-xl font-heading-semibold text-card-foreground">Previsão x realizado x perdas</h2>
              <p className="text-sm text-muted-foreground mt-1">Visão diária do período selecionado.</p>
            </div>
            <div className="rounded-xl border border-[#eadfd6] bg-[#fff7ed] px-4 py-3 text-sm text-muted-foreground">
              <span className="font-body-medium text-card-foreground">Filtro ativo:</span> {formatDate(period.de)} até {formatDate(period.ate)}
            </div>
          </div>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={charts.daily || []}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={chartColors.light} />
                <XAxis dataKey="dia" tick={{ fontSize: 12, fill: chartColors.axis }} axisLine={{ stroke: chartColors.light }} tickLine={{ stroke: chartColors.light }} />
                <YAxis tick={{ fontSize: 12, fill: chartColors.axis }} axisLine={{ stroke: chartColors.light }} tickLine={{ stroke: chartColors.light }} />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Bar dataKey="previsao" name="Previsão" fill={chartColors.brown} radius={[8, 8, 0, 0]} />
                <Bar dataKey="realizado" name="Realizado" fill={chartColors.orange} radius={[8, 8, 0, 0]} />
                <Bar dataKey="perdas" name="Perdas" fill={chartColors.amber} radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-card rounded-xl border border-border shadow-card p-6">
          <div className="mb-4">
            <h2 className="text-xl font-heading-semibold text-card-foreground">Motivos das perdas</h2>
            <p className="text-sm text-muted-foreground mt-1">Principais motivos no período.</p>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={lossPieData} dataKey="quantidade" nameKey="motivo" innerRadius={55} outerRadius={90} paddingAngle={2}>
                  {lossPieData.map((entry, index) => (
                    <Cell key={`${entry.motivo}-${index}`} fill={entry.empty ? "#EADFD6" : chartColors.pie[index % chartColors.pie.length]} opacity={entry.empty ? 0.6 : 1} />
                  ))}
                </Pie>
                <Tooltip formatter={(value, name) => [formatNumber(value), name]} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="space-y-2 mt-2">
            {(charts.lossReasons || []).slice(0, 5).map((item) => (
              <div key={item.motivo} className="flex items-center justify-between gap-3 rounded-lg bg-muted/40 px-3 py-2 text-sm">
                <span className="text-card-foreground truncate">{item.motivo}</span>
                <span className="font-data text-muted-foreground">{formatNumber(item.quantidade)}</span>
              </div>
            ))}
            {(!charts.lossReasons || charts.lossReasons.length === 0) ? <p className="text-sm text-muted-foreground text-center py-4">Nenhuma perda registrada no período.</p> : null}
          </div>
        </div>
      </section>

      <section className="bg-card rounded-xl border border-border shadow-card p-6">
        <div className="mb-6">
          <h2 className="text-xl font-heading-semibold text-card-foreground">Ranking por produto</h2>
          <p className="text-sm text-muted-foreground mt-1">Produtos com maior quantidade realizada no período.</p>
        </div>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={charts.products || []} layout="vertical" margin={{ left: 30 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke={chartColors.light} />
              <XAxis type="number" tick={{ fontSize: 12, fill: chartColors.axis }} axisLine={{ stroke: chartColors.light }} tickLine={{ stroke: chartColors.light }} />
              <YAxis type="category" dataKey="produto" tick={{ fontSize: 11, fill: chartColors.axis }} width={210} axisLine={{ stroke: chartColors.light }} tickLine={{ stroke: chartColors.light }} />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Bar dataKey="realizado" name="Realizado" fill={chartColors.orange} radius={[0, 8, 8, 0]} />
              <Bar dataKey="perdas" name="Perdas" fill={chartColors.amber} radius={[0, 8, 8, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </section>

      <section className="bg-card rounded-xl border border-border shadow-card overflow-hidden">
        <div className="p-6 border-b border-border flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h2 className="text-xl font-heading-semibold text-card-foreground">Histórico manual de produção</h2>
            <p className="text-sm text-muted-foreground mt-1">Registros de produção, previsão, perdas e motivos.</p>
          </div>
          <Input type="search" placeholder="Buscar produto ou motivo..." value={search} onChange={(e) => setSearch(e.target.value)} className="lg:w-72" />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/60">
              <tr>
                <th className="p-4 text-left text-xs text-muted-foreground uppercase">Data</th>
                <th className="p-4 text-left text-xs text-muted-foreground uppercase">Produto</th>
                <th className="p-4 text-left text-xs text-muted-foreground uppercase">Previsão</th>
                <th className="p-4 text-left text-xs text-muted-foreground uppercase">Realizado</th>
                <th className="p-4 text-left text-xs text-muted-foreground uppercase">Perdas</th>
                <th className="p-4 text-left text-xs text-muted-foreground uppercase">Motivo</th>
                <th className="p-4 text-left text-xs text-muted-foreground uppercase">Status</th>
                <th className="p-4 text-right text-xs text-muted-foreground uppercase">Ações</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="p-8 text-center text-sm text-muted-foreground">Carregando...</td></tr>
              ) : filteredRecords.length === 0 ? (
                <tr><td colSpan={8} className="p-8 text-center text-sm text-muted-foreground">Nenhum registro de produção encontrado no período filtrado.</td></tr>
              ) : filteredRecords.map((record) => {
                const st = statusInfo[record.status] || statusInfo.abaixo;
                return (
                  <tr key={record.id} className="border-b border-border hover:bg-accent/30 transition-smooth">
                    <td className="p-4 text-sm text-muted-foreground whitespace-nowrap">{formatDate(record.data)}</td>
                    <td className="p-4 min-w-[260px]">
                      <div className="font-body-medium text-card-foreground">{record.produtoNome}</div>
                      {record.observacao ? <div className="text-xs text-muted-foreground mt-1">{record.observacao}</div> : null}
                      <div className="text-xs text-muted-foreground mt-1">Atualizado: {formatDateTime(record.updatedAt)}</div>
                    </td>
                    <td className="p-4 font-data text-card-foreground">{formatNumber(record.previsaoQuantidade)}</td>
                    <td className="p-4 font-data text-card-foreground">{formatNumber(record.realizadoQuantidade)} <span className="text-xs text-muted-foreground">({formatPercent(record.eficiencia)})</span></td>
                    <td className="p-4 font-data text-card-foreground">{formatNumber(record.perdaQuantidade)} <span className="text-xs text-muted-foreground">({formatPercent(record.perdaPercentual)})</span></td>
                    <td className="p-4 text-sm text-muted-foreground min-w-[220px]">{record.motivoPerda || "-"}</td>
                    <td className="p-4"><span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-body-medium ${st.cls}`}><Icon name={st.icon} size={13} />{st.label}</span></td>
                    <td className="p-4">
                      <div className="flex justify-end gap-1">
                        <button type="button" onClick={() => editRecord(record)} className="p-2 rounded-lg text-muted-foreground hover:text-card-foreground hover:bg-accent" title="Editar"><Icon name="Edit3" size={16} /></button>
                        <button type="button" onClick={() => deleteRecord(record)} className="p-2 rounded-lg text-error hover:bg-error/10" title="Remover"><Icon name="Trash2" size={16} /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function TemperatureSection({
  configs,
  records,
  summary,
  filteredTotals,
  charts,
  alerts,
  period,
  setPeriod,
  tempForm,
  setTempForm,
  tempEditingId,
  setTempEditingId,
  configForm,
  setConfigForm,
  configEditingId,
  setConfigEditingId,
  search,
  setSearch,
  loading,
  saving,
  setSaving,
  message,
  setMessage,
  reload,
}) {
  const filteredRecords = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return records;
    return records.filter((item) =>
      [item.localNome, item.objetoAmbiente, item.responsavel, item.observacao].some((value) => String(value || "").toLowerCase().includes(term))
    );
  }, [records, search]);

  const configOptions = configs.map((item) => item.localNome);
  const statusPieData = charts.status?.length ? charts.status : [{ nome: "Sem medições", quantidade: 1, empty: true }];

  const resetMeasurementForm = () => {
    setTempEditingId(null);
    setTempForm({ ...blankTemperatureForm, data: today(), horario: nowTime() });
  };

  const resetConfigForm = () => {
    setConfigEditingId(null);
    setConfigForm({ ...blankConfigForm, ativo: true });
  };

  const submitMeasurement = async (event) => {
    event.preventDefault();
    setSaving(true);
    setMessage({ type: "", text: "" });
    try {
      const payload = {
        data: tempForm.data,
        horario: tempForm.horario,
        localNome: tempForm.localNome,
        objetoAmbiente: tempForm.objetoAmbiente,
        responsavel: tempForm.responsavel,
        temperatura: Number(tempForm.temperatura || 0),
        observacao: tempForm.observacao,
      };
      if (tempEditingId) {
        await axios.put(`${apiBase}/api/controle-producao/temperatura-umidade/${tempEditingId}`, payload);
        setMessage({ type: "success", text: "Medição atualizada com sucesso." });
      } else {
        await axios.post(`${apiBase}/api/controle-producao/temperatura-umidade`, payload);
        setMessage({ type: "success", text: "Medição cadastrada com sucesso." });
      }
      resetMeasurementForm();
      await reload();
    } catch (error) {
      setMessage({ type: "error", text: error?.response?.data?.message || error?.message || "Erro ao salvar medição." });
    } finally {
      setSaving(false);
    }
  };

  const submitConfig = async (event) => {
    event.preventDefault();
    setSaving(true);
    setMessage({ type: "", text: "" });
    try {
      const payload = {
        localNome: configForm.localNome,
        temperaturaMinima: Number(configForm.temperaturaMinima || 0),
        temperaturaMaxima: Number(configForm.temperaturaMaxima || 0),
        ativo: !!configForm.ativo,
      };
      if (configEditingId) {
        await axios.put(`${apiBase}/api/controle-producao/temperatura-umidade/configuracoes/${configEditingId}`, payload);
        setMessage({ type: "success", text: "Configuração atualizada com sucesso." });
      } else {
        await axios.post(`${apiBase}/api/controle-producao/temperatura-umidade/configuracoes`, payload);
        setMessage({ type: "success", text: "Configuração cadastrada com sucesso." });
      }
      resetConfigForm();
      await reload();
    } catch (error) {
      setMessage({ type: "error", text: error?.response?.data?.message || error?.message || "Erro ao salvar configuração." });
    } finally {
      setSaving(false);
    }
  };

  const editConfig = (config) => {
    setConfigEditingId(config.id);
    setConfigForm({
      localNome: config.localNome || "",
      temperaturaMinima: config.temperaturaMinima ?? "",
      temperaturaMaxima: config.temperaturaMaxima ?? "",
      umidadeMinima: config.umidadeMinima ?? "",
      umidadeMaxima: config.umidadeMaxima ?? "",
      ativo: config.ativo !== false,
    });
    document.getElementById("pcp-config-form")?.scrollIntoView({ behavior: "smooth" });
  };

  const deleteConfig = async (config) => {
    if (!window.confirm(`Remover a configuração de ${config.localNome}?`)) return;
    setSaving(true);
    try {
      await axios.delete(`${apiBase}/api/controle-producao/temperatura-umidade/configuracoes/${config.id}`);
      setMessage({ type: "success", text: "Configuração removida com sucesso." });
      await reload();
    } catch (error) {
      setMessage({ type: "error", text: error?.response?.data?.message || error?.message || "Erro ao remover configuração." });
    } finally {
      setSaving(false);
    }
  };

  const editMeasurement = (record) => {
    setTempEditingId(record.id);
    setTempForm({
      data: record.data || today(),
      horario: record.horario || nowTime(),
      localNome: record.localNome || "",
      objetoAmbiente: record.objetoAmbiente || "",
      responsavel: record.responsavel || "",
      temperatura: record.temperatura ?? "",
      umidade: record.umidade ?? "",
      observacao: record.observacao || "",
    });
    document.getElementById("pcp-temp-form")?.scrollIntoView({ behavior: "smooth" });
  };

  const deleteMeasurement = async (record) => {
    if (!window.confirm(`Remover a medição de ${record.localNome} em ${formatDate(record.data)} ${record.horario}?`)) return;
    setSaving(true);
    try {
      await axios.delete(`${apiBase}/api/controle-producao/temperatura-umidade/${record.id}`);
      setMessage({ type: "success", text: "Medição removida com sucesso." });
      await reload();
    } catch (error) {
      setMessage({ type: "error", text: error?.response?.data?.message || error?.message || "Erro ao remover medição." });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {message.text ? (
        <div className={`rounded-xl border px-4 py-3 text-sm flex gap-2 ${message.type === "error" ? "bg-error/10 text-error border-error/20" : "bg-success/10 text-success border-success/20"}`}>
          <Icon name={message.type === "error" ? "AlertCircle" : "CheckCircle2"} size={18} />
          {message.text}
        </div>
      ) : null}

      <section className="grid grid-cols-1 xl:grid-cols-[0.95fr_1.05fr] gap-6">
        <form id="pcp-temp-form" onSubmit={submitMeasurement} className="bg-card rounded-xl border border-border shadow-card p-6 space-y-5">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h2 className="text-xl font-heading-semibold text-card-foreground">{tempEditingId ? "Editar medição" : "Registrar medição diária"}</h2>
              <p className="text-sm text-muted-foreground mt-1">Registre dia, horário, ambiente/objeto, responsável e temperatura.</p>
            </div>
            {tempEditingId ? <Button type="button" variant="ghost" size="sm" onClick={resetMeasurementForm}>Cancelar</Button> : null}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input type="date" label="Data" value={tempForm.data} onChange={(e) => setTempForm((p) => ({ ...p, data: e.target.value }))} required />
            <Input type="time" label="Horário" value={tempForm.horario} onChange={(e) => setTempForm((p) => ({ ...p, horario: e.target.value }))} required />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Ambiente / local *</label>
              <input
                list="pcp-temp-configs"
                value={tempForm.localNome}
                onChange={(e) => setTempForm((p) => ({ ...p, localNome: e.target.value }))}
                placeholder="Ex: CAMARA FRIA"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                required
              />
              <datalist id="pcp-temp-configs">
                {configOptions.map((item) => <option key={item} value={item} />)}
              </datalist>
            </div>
            <Input label="Objeto / ambiente" placeholder="Ex: freezers, bancada, sala 1" value={tempForm.objetoAmbiente} onChange={(e) => setTempForm((p) => ({ ...p, objetoAmbiente: e.target.value }))} />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input type="number" step="0.1" label="Temperatura (°C)" value={tempForm.temperatura} onChange={(e) => setTempForm((p) => ({ ...p, temperatura: e.target.value }))} required />
            <Input label="Responsável" placeholder="Nome do responsável" value={tempForm.responsavel} onChange={(e) => setTempForm((p) => ({ ...p, responsavel: e.target.value }))} />
          </div>

          <Input label="Observação" placeholder="Ex: porta aberta, manutenção, variação momentânea" value={tempForm.observacao} onChange={(e) => setTempForm((p) => ({ ...p, observacao: e.target.value }))} />

          <Button type="submit" fullWidth loading={saving} iconName={tempEditingId ? "Save" : "Plus"}>{tempEditingId ? "Salvar medição" : "Cadastrar medição"}</Button>
        </form>

        <div className="bg-card rounded-xl border border-border shadow-card p-6 space-y-5">
          <div className="flex flex-col gap-4">
            <div>
              <h2 className="text-xl font-heading-semibold text-card-foreground">Resumo de conformidade</h2>
              <p className="text-sm text-muted-foreground mt-1">0% de conformidade mede as medições fora dos limites mínimos e máximos de temperatura.</p>
            </div>
            <div className="rounded-xl border border-[#eadfd6] bg-[#fff7ed] p-4">
              <div className="flex items-center gap-2 mb-3">
                <Icon name="CalendarRange" size={17} className="text-[#D97706]" />
                <p className="text-sm font-body-medium text-card-foreground">Filtrar medição por data</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Input type="date" label="De" value={period.de} onChange={(e) => setPeriod((p) => ({ ...p, de: e.target.value }))} />
                <Input type="date" label="Até" value={period.ate} onChange={(e) => setPeriod((p) => ({ ...p, ate: e.target.value }))} />
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-1 gap-4">
            <SummaryBlock label="Hoje" data={summary?.dia} icon="Thermometer" mode="temperature" />
            <SummaryBlock label="Semana" data={summary?.semana} icon="CalendarDays" mode="temperature" />
            <SummaryBlock label="Mês" data={summary?.mes} icon="CalendarCheck" mode="temperature" />
          </div>
          <div className="rounded-xl border border-[#F29F05]/30 bg-gradient-to-r from-[#5A3528] to-[#8B5E3C] p-4 text-white">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-body-medium">Período filtrado</p>
                <p className="text-xs text-white/75 mt-1">{formatDate(period.de)} até {formatDate(period.ate)}</p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-heading-bold text-[#F29F05]">{formatPercent(filteredTotals?.conformidade)}</p>
                <p className="text-xs text-white/75">conformidade</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="grid grid-cols-1 xl:grid-cols-[1.05fr_0.95fr] gap-6">
        <form id="pcp-config-form" onSubmit={submitConfig} className="bg-card rounded-xl border border-border shadow-card p-6 space-y-5">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h2 className="text-xl font-heading-semibold text-card-foreground">Configuração de limites</h2>
              <p className="text-sm text-muted-foreground mt-1">Defina a temperatura mínima e máxima fixa para cada ambiente.</p>
            </div>
            {configEditingId ? <Button type="button" variant="ghost" size="sm" onClick={resetConfigForm}>Cancelar</Button> : null}
          </div>

          <Input label="Ambiente / local" placeholder="Ex: CAMARA FRIA" value={configForm.localNome} onChange={(e) => setConfigForm((p) => ({ ...p, localNome: e.target.value }))} required />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input type="number" step="0.1" label="Temperatura mínima (°C)" value={configForm.temperaturaMinima} onChange={(e) => setConfigForm((p) => ({ ...p, temperaturaMinima: e.target.value }))} required />
            <Input type="number" step="0.1" label="Temperatura máxima (°C)" value={configForm.temperaturaMaxima} onChange={(e) => setConfigForm((p) => ({ ...p, temperaturaMaxima: e.target.value }))} required />
          </div>

          <label className="inline-flex items-center gap-2 text-sm text-card-foreground">
            <input type="checkbox" checked={!!configForm.ativo} onChange={(e) => setConfigForm((p) => ({ ...p, ativo: e.target.checked }))} />
            Configuração ativa
          </label>

          <Button type="submit" fullWidth loading={saving} iconName={configEditingId ? "Save" : "Plus"}>{configEditingId ? "Salvar configuração" : "Cadastrar configuração"}</Button>
        </form>

        <div className="bg-card rounded-xl border border-border shadow-card overflow-hidden">
          <div className="p-6 border-b border-border">
            <h2 className="text-xl font-heading-semibold text-card-foreground">Ambientes parametrizados</h2>
            <p className="text-sm text-muted-foreground mt-1">Lista com os limites mínimos e máximos cadastrados.</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/60">
                <tr>
                  <th className="p-4 text-left text-xs text-muted-foreground uppercase">Ambiente</th>
                  <th className="p-4 text-left text-xs text-muted-foreground uppercase">Temperatura</th>
                  <th className="p-4 text-left text-xs text-muted-foreground uppercase">Status</th>
                  <th className="p-4 text-right text-xs text-muted-foreground uppercase">Ações</th>
                </tr>
              </thead>
              <tbody>
                {configs.length === 0 ? (
                  <tr><td colSpan={5} className="p-8 text-center text-sm text-muted-foreground">Nenhuma configuração cadastrada.</td></tr>
                ) : configs.map((config) => (
                  <tr key={config.id} className="border-b border-border hover:bg-accent/30 transition-smooth">
                    <td className="p-4 font-body-medium text-card-foreground">{config.localNome}</td>
                    <td className="p-4 text-sm text-muted-foreground">{formatDecimal(config.temperaturaMinima)}°C até {formatDecimal(config.temperaturaMaxima)}°C</td>
                    <td className="p-4">{config.ativo ? <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-body-medium bg-success/10 text-success border-success/20"><Icon name="CheckCircle2" size={13} />Ativo</span> : <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-body-medium bg-muted text-muted-foreground border-border"><Icon name="CircleOff" size={13} />Inativo</span>}</td>
                    <td className="p-4">
                      <div className="flex justify-end gap-1">
                        <button type="button" onClick={() => editConfig(config)} className="p-2 rounded-lg text-muted-foreground hover:text-card-foreground hover:bg-accent" title="Editar"><Icon name="Edit3" size={16} /></button>
                        <button type="button" onClick={() => deleteConfig(config)} className="p-2 rounded-lg text-error hover:bg-error/10" title="Remover"><Icon name="Trash2" size={16} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="grid grid-cols-1 xl:grid-cols-[1.15fr_0.85fr] gap-6">
        <div className="bg-card rounded-xl border border-border shadow-card p-6">
          <div className="mb-6">
            <h2 className="text-xl font-heading-semibold text-card-foreground">Conformidade por dia</h2>
            <p className="text-sm text-muted-foreground mt-1">Quantidade de medições em conformidade e em alerta no período.</p>
          </div>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={charts.daily || []}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={chartColors.light} />
                <XAxis dataKey="dia" tick={{ fontSize: 12, fill: chartColors.axis }} axisLine={{ stroke: chartColors.light }} tickLine={{ stroke: chartColors.light }} />
                <YAxis tick={{ fontSize: 12, fill: chartColors.axis }} axisLine={{ stroke: chartColors.light }} tickLine={{ stroke: chartColors.light }} />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Bar dataKey="conforme" name="Dentro do padrão" fill={chartColors.orange} radius={[8, 8, 0, 0]} />
                <Bar dataKey="alerta" name="Alertas" fill={chartColors.brown} radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-card rounded-xl border border-border shadow-card p-6">
          <div className="mb-4">
            <h2 className="text-xl font-heading-semibold text-card-foreground">Distribuição dos status</h2>
            <p className="text-sm text-muted-foreground mt-1">Resumo das medições dentro ou fora do padrão.</p>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={statusPieData} dataKey="quantidade" nameKey="nome" innerRadius={55} outerRadius={90} paddingAngle={2}>
                  {statusPieData.map((entry, index) => (
                    <Cell key={`${entry.nome}-${index}`} fill={entry.empty ? "#EADFD6" : chartColors.pie[index % chartColors.pie.length]} opacity={entry.empty ? 0.6 : 1} />
                  ))}
                </Pie>
                <Tooltip formatter={(value, name) => [formatNumber(value), name]} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="space-y-2 mt-2">
            {(charts.status || []).map((item) => (
              <div key={item.nome} className="flex items-center justify-between gap-3 rounded-lg bg-muted/40 px-3 py-2 text-sm">
                <span className="text-card-foreground truncate">{item.nome}</span>
                <span className="font-data text-muted-foreground">{formatNumber(item.quantidade)}</span>
              </div>
            ))}
            {(!charts.status || charts.status.length === 0) ? <p className="text-sm text-muted-foreground text-center py-4">Nenhuma medição no período.</p> : null}
          </div>
        </div>
      </section>

      <section className="grid grid-cols-1 xl:grid-cols-[0.9fr_1.1fr] gap-6">
        <div className="bg-card rounded-xl border border-border shadow-card p-6">
          <div className="mb-6">
            <h2 className="text-xl font-heading-semibold text-card-foreground">Ambientes com mais alertas</h2>
            <p className="text-sm text-muted-foreground mt-1">Ambientes que mais apresentaram medições fora do padrão.</p>
          </div>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={charts.locations || []} layout="vertical" margin={{ left: 25 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke={chartColors.light} />
                <XAxis type="number" tick={{ fontSize: 12, fill: chartColors.axis }} axisLine={{ stroke: chartColors.light }} tickLine={{ stroke: chartColors.light }} />
                <YAxis type="category" dataKey="local" tick={{ fontSize: 11, fill: chartColors.axis }} width={180} axisLine={{ stroke: chartColors.light }} tickLine={{ stroke: chartColors.light }} />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Bar dataKey="alertas" name="Alertas" fill={chartColors.brown} radius={[0, 8, 8, 0]} />
                <Bar dataKey="conformes" name="Dentro do padrão" fill={chartColors.orange} radius={[0, 8, 8, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-card rounded-xl border border-border shadow-card overflow-hidden">
          <div className="p-6 border-b border-border">
            <h2 className="text-xl font-heading-semibold text-card-foreground">Quadro de alertas</h2>
            <p className="text-sm text-muted-foreground mt-1">Lista medições abaixo ou acima do mínimo e do máximo.</p>
          </div>
          <div className="divide-y divide-border">
            {alerts.length === 0 ? (
              <div className="p-8 text-center text-sm text-muted-foreground">Nenhum alerta no período filtrado.</div>
            ) : alerts.map((alert) => (
              <div key={alert.id} className="p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-body-medium text-card-foreground">{alert.localNome}</span>
                    <span className="text-xs text-muted-foreground">{formatDate(alert.data)} às {alert.horario}</span>
                  </div>
                  <div className="text-sm text-muted-foreground mt-1">
                    Temp.: <span className="text-card-foreground font-data">{formatDecimal(alert.temperatura)}°C</span>
                    {alert.faixa ? <span> (limite {formatDecimal(alert.faixa.temperaturaMinima)}°C a {formatDecimal(alert.faixa.temperaturaMaxima)}°C)</span> : null}
                  </div>
                  {alert.observacao ? <div className="text-xs text-muted-foreground mt-1">Obs.: {alert.observacao}</div> : null}
                </div>
                <div className="flex gap-2 items-center flex-wrap">
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-body-medium bg-error/10 text-error border-error/20"><Icon name="AlertTriangle" size={13} />Fora do padrão</span>
                  <button type="button" onClick={() => editMeasurement(alert)} className="p-2 rounded-lg text-muted-foreground hover:text-card-foreground hover:bg-accent" title="Editar"><Icon name="Edit3" size={16} /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-card rounded-xl border border-border shadow-card overflow-hidden">
        <div className="p-6 border-b border-border flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h2 className="text-xl font-heading-semibold text-card-foreground">Histórico de temperatura e umidade</h2>
            <p className="text-sm text-muted-foreground mt-1">Registros manuais com data, horário, ambiente, medição e responsável.</p>
          </div>
          <Input type="search" placeholder="Buscar ambiente, responsável ou observação..." value={search} onChange={(e) => setSearch(e.target.value)} className="lg:w-80" />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/60">
              <tr>
                <th className="p-4 text-left text-xs text-muted-foreground uppercase">Data</th>
                <th className="p-4 text-left text-xs text-muted-foreground uppercase">Ambiente</th>
                <th className="p-4 text-left text-xs text-muted-foreground uppercase">Medição</th>
                <th className="p-4 text-left text-xs text-muted-foreground uppercase">Responsável</th>
                <th className="p-4 text-left text-xs text-muted-foreground uppercase">Status</th>
                <th className="p-4 text-right text-xs text-muted-foreground uppercase">Ações</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="p-8 text-center text-sm text-muted-foreground">Carregando...</td></tr>
              ) : filteredRecords.length === 0 ? (
                <tr><td colSpan={6} className="p-8 text-center text-sm text-muted-foreground">Nenhuma medição encontrada.</td></tr>
              ) : filteredRecords.map((record) => {
                const st = temperatureStatusInfo[record.statusGeral] || temperatureStatusInfo.sem_parametro;
                return (
                  <tr key={record.id} className="border-b border-border hover:bg-accent/30 transition-smooth">
                    <td className="p-4 text-sm text-muted-foreground whitespace-nowrap">
                      <div>{formatDate(record.data)}</div>
                      <div className="text-xs">{record.horario}</div>
                    </td>
                    <td className="p-4 min-w-[260px]">
                      <div className="font-body-medium text-card-foreground">{record.localNome}</div>
                      {record.observacao ? <div className="text-xs text-muted-foreground mt-1">{record.observacao}</div> : null}
                    </td>
                    <td className="p-4 min-w-[240px]">
                      <div className="font-data text-card-foreground">{formatDecimal(record.temperatura)}°C</div>
                      {record.faixa ? <div className="text-xs text-muted-foreground mt-1">Limites: {formatDecimal(record.faixa.temperaturaMinima)}°C a {formatDecimal(record.faixa.temperaturaMaxima)}°C</div> : null}
                    </td>
                    <td className="p-4 text-sm text-muted-foreground">{record.responsavel || "-"}</td>
                    <td className="p-4"><span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-body-medium ${st.cls}`}><Icon name={st.icon} size={13} />{st.label}</span></td>
                    <td className="p-4">
                      <div className="flex justify-end gap-1">
                        <button type="button" onClick={() => editMeasurement(record)} className="p-2 rounded-lg text-muted-foreground hover:text-card-foreground hover:bg-accent" title="Editar"><Icon name="Edit3" size={16} /></button>
                        <button type="button" onClick={() => deleteMeasurement(record)} className="p-2 rounded-lg text-error hover:bg-error/10" title="Remover"><Icon name="Trash2" size={16} /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}


function HumidityUrSection({
  configs,
  records,
  summary,
  filteredTotals,
  charts,
  alerts,
  period,
  setPeriod,
  form,
  setForm,
  editingId,
  setEditingId,
  configForm,
  setConfigForm,
  configEditingId,
  setConfigEditingId,
  search,
  setSearch,
  loading,
  saving,
  setSaving,
  message,
  setMessage,
  reload,
}) {
  const filteredRecords = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return records;
    return records.filter((item) =>
      [item.localNome, item.objetoAmbiente, item.responsavel, item.observacao].some((value) => String(value || "").toLowerCase().includes(term))
    );
  }, [records, search]);

  const configOptions = configs.map((item) => item.localNome);
  const statusPieData = charts.status?.length ? charts.status : [{ nome: "Sem medições", quantidade: 1, empty: true }];

  const resetForm = () => {
    setEditingId(null);
    setForm({ ...blankHumidityUrForm, data: today(), horario: nowTime() });
  };

  const resetConfigForm = () => {
    setConfigEditingId(null);
    setConfigForm({ ...blankHumidityUrConfigForm, ativo: true });
  };

  const submitMeasurement = async (event) => {
    event.preventDefault();
    setSaving(true);
    setMessage({ type: "", text: "" });
    try {
      const payload = {
        data: form.data,
        horario: form.horario,
        localNome: form.localNome,
        responsavel: form.responsavel,
        umidade: Number(form.umidade || 0),
        observacao: form.observacao,
      };
      if (editingId) {
        await axios.put(`${apiBase}/api/controle-producao/umidade-ur/${editingId}`, payload);
        setMessage({ type: "success", text: "Medição de UR atualizada com sucesso." });
      } else {
        await axios.post(`${apiBase}/api/controle-producao/umidade-ur`, payload);
        setMessage({ type: "success", text: "Medição de UR cadastrada com sucesso." });
      }
      resetForm();
      await reload();
    } catch (error) {
      setMessage({ type: "error", text: error?.response?.data?.message || error?.message || "Erro ao salvar medição de UR." });
    } finally {
      setSaving(false);
    }
  };

  const submitConfig = async (event) => {
    event.preventDefault();
    setSaving(true);
    setMessage({ type: "", text: "" });
    try {
      const payload = {
        localNome: configForm.localNome,
        umidadeMinima: Number(configForm.umidadeMinima || 0),
        umidadeMaxima: Number(configForm.umidadeMaxima || 0),
        ativo: !!configForm.ativo,
      };
      if (configEditingId) {
        await axios.put(`${apiBase}/api/controle-producao/umidade-ur/configuracoes/${configEditingId}`, payload);
        setMessage({ type: "success", text: "Configuração de UR atualizada com sucesso." });
      } else {
        await axios.post(`${apiBase}/api/controle-producao/umidade-ur/configuracoes`, payload);
        setMessage({ type: "success", text: "Configuração de UR cadastrada com sucesso." });
      }
      resetConfigForm();
      await reload();
    } catch (error) {
      setMessage({ type: "error", text: error?.response?.data?.message || error?.message || "Erro ao salvar configuração de UR." });
    } finally {
      setSaving(false);
    }
  };

  const editConfig = (config) => {
    setConfigEditingId(config.id);
    setConfigForm({
      localNome: config.localNome || "",
      umidadeMinima: config.umidadeMinima ?? "",
      umidadeMaxima: config.umidadeMaxima ?? "",
      ativo: config.ativo !== false,
    });
    document.getElementById("pcp-ur-config-form")?.scrollIntoView({ behavior: "smooth" });
  };

  const deleteConfig = async (config) => {
    if (!window.confirm(`Remover a configuração de UR de ${config.localNome}?`)) return;
    setSaving(true);
    try {
      await axios.delete(`${apiBase}/api/controle-producao/umidade-ur/configuracoes/${config.id}`);
      setMessage({ type: "success", text: "Configuração de UR removida com sucesso." });
      await reload();
    } catch (error) {
      setMessage({ type: "error", text: error?.response?.data?.message || error?.message || "Erro ao remover configuração de UR." });
    } finally {
      setSaving(false);
    }
  };

  const editMeasurement = (record) => {
    setEditingId(record.id);
    setForm({
      data: record.data || today(),
      horario: record.horario || nowTime(),
      localNome: record.localNome || "",
      objetoAmbiente: record.objetoAmbiente || "",
      responsavel: record.responsavel || "",
      umidade: record.umidade ?? "",
      observacao: record.observacao || "",
    });
    document.getElementById("pcp-ur-form")?.scrollIntoView({ behavior: "smooth" });
  };

  const deleteMeasurement = async (record) => {
    if (!window.confirm(`Remover a medição de UR de ${record.localNome} em ${formatDate(record.data)} ${record.horario}?`)) return;
    setSaving(true);
    try {
      await axios.delete(`${apiBase}/api/controle-producao/umidade-ur/${record.id}`);
      setMessage({ type: "success", text: "Medição de UR removida com sucesso." });
      await reload();
    } catch (error) {
      setMessage({ type: "error", text: error?.response?.data?.message || error?.message || "Erro ao remover medição de UR." });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {message.text ? (
        <div className={`rounded-xl border px-4 py-3 text-sm flex gap-2 ${message.type === "error" ? "bg-error/10 text-error border-error/20" : "bg-success/10 text-success border-success/20"}`}>
          <Icon name={message.type === "error" ? "AlertCircle" : "CheckCircle2"} size={18} />
          {message.text}
        </div>
      ) : null}

      <section className="grid grid-cols-1 xl:grid-cols-[0.95fr_1.05fr] gap-6">
        <form id="pcp-ur-form" onSubmit={submitMeasurement} className="bg-card rounded-xl border border-border shadow-card p-6 space-y-5">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h2 className="text-xl font-heading-semibold text-card-foreground">{editingId ? "Editar medição de umidade (UR)" : "Registrar umidade (UR)"}</h2>
              <p className="text-sm text-muted-foreground mt-1">Registre a umidade relativa por dia, horário e responsável.</p>
            </div>
            {editingId ? <Button type="button" variant="ghost" size="sm" onClick={resetForm}>Cancelar</Button> : null}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input type="date" label="Data" value={form.data} onChange={(e) => setForm((p) => ({ ...p, data: e.target.value }))} required />
            <Input type="time" label="Horário" value={form.horario} onChange={(e) => setForm((p) => ({ ...p, horario: e.target.value }))} required />
          </div>

          <div className="grid grid-cols-1 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Ambiente parametrizado *</label>
              <input
                list="pcp-ur-configs"
                value={form.localNome}
                onChange={(e) => setForm((p) => ({ ...p, localNome: e.target.value }))}
                placeholder="Ex: SALA DE FORNEAMENTO"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                required
              />
              <datalist id="pcp-ur-configs">
                {configOptions.map((item) => <option key={item} value={item} />)}
              </datalist>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input type="number" step="0.1" label="Umidade relativa - UR (%)" value={form.umidade} onChange={(e) => setForm((p) => ({ ...p, umidade: e.target.value }))} required />
            <Input label="Responsável" placeholder="Nome do responsável" value={form.responsavel} onChange={(e) => setForm((p) => ({ ...p, responsavel: e.target.value }))} />
          </div>

          <Input label="Observação" placeholder="Ex: sala aberta, alteração no processo, ar condicionado" value={form.observacao} onChange={(e) => setForm((p) => ({ ...p, observacao: e.target.value }))} />
          <Button type="submit" fullWidth loading={saving} iconName={editingId ? "Save" : "Plus"}>{editingId ? "Salvar medição" : "Cadastrar medição"}</Button>
        </form>

        <div className="bg-card rounded-xl border border-border shadow-card p-6 space-y-5">
          <div>
            <h2 className="text-xl font-heading-semibold text-card-foreground">Resumo de umidade (UR)</h2>
            <p className="text-sm text-muted-foreground mt-1">Conformidade baseada nos limites de umidade relativa mínima e máxima.</p>
          </div>
          <div className="rounded-xl border border-[#eadfd6] bg-[#fff7ed] p-4">
            <div className="flex items-center gap-2 mb-3">
              <Icon name="CalendarRange" size={17} className="text-[#D97706]" />
              <p className="text-sm font-body-medium text-card-foreground">Filtrar UR por data</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Input type="date" label="De" value={period.de} onChange={(e) => setPeriod((p) => ({ ...p, de: e.target.value }))} />
              <Input type="date" label="Até" value={period.ate} onChange={(e) => setPeriod((p) => ({ ...p, ate: e.target.value }))} />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-1 gap-4">
            <SummaryBlock label="Hoje" data={summary?.dia} icon="Droplets" mode="humidityUr" />
            <SummaryBlock label="Semana" data={summary?.semana} icon="CalendarDays" mode="humidityUr" />
            <SummaryBlock label="Mês" data={summary?.mes} icon="CalendarCheck" mode="humidityUr" />
          </div>
          <div className="rounded-xl border border-[#F29F05]/30 bg-gradient-to-r from-[#5A3528] to-[#8B5E3C] p-4 text-white">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-body-medium">Período filtrado</p>
                <p className="text-xs text-white/75 mt-1">{formatDate(period.de)} até {formatDate(period.ate)}</p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-heading-bold text-[#F29F05]">{formatPercent(filteredTotals?.conformidade)}</p>
                <p className="text-xs text-white/75">conformidade</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="grid grid-cols-1 xl:grid-cols-[1.05fr_0.95fr] gap-6">
        <form id="pcp-ur-config-form" onSubmit={submitConfig} className="bg-card rounded-xl border border-border shadow-card p-6 space-y-5">
          <div>
            <h2 className="text-xl font-heading-semibold text-card-foreground">Ambientes parametrizados de UR</h2>
            <p className="text-sm text-muted-foreground mt-1">Por padrão: Sala de forneamento e Sala de manipulação. Você pode editar os limites.</p>
          </div>
          <Input label="Ambiente / local" placeholder="Ex: SALA DE FORNEAMENTO" value={configForm.localNome} onChange={(e) => setConfigForm((p) => ({ ...p, localNome: e.target.value }))} required />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input type="number" step="0.1" label="UR mínima (%)" value={configForm.umidadeMinima} onChange={(e) => setConfigForm((p) => ({ ...p, umidadeMinima: e.target.value }))} required />
            <Input type="number" step="0.1" label="UR máxima (%)" value={configForm.umidadeMaxima} onChange={(e) => setConfigForm((p) => ({ ...p, umidadeMaxima: e.target.value }))} required />
          </div>
          <label className="inline-flex items-center gap-2 text-sm text-card-foreground">
            <input type="checkbox" checked={!!configForm.ativo} onChange={(e) => setConfigForm((p) => ({ ...p, ativo: e.target.checked }))} />
            Configuração ativa
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Button type="submit" loading={saving} iconName={configEditingId ? "Save" : "Plus"}>{configEditingId ? "Salvar configuração" : "Cadastrar configuração"}</Button>
            {configEditingId ? <Button type="button" variant="outline" onClick={resetConfigForm}>Cancelar edição</Button> : null}
          </div>
        </form>

        <div className="bg-card rounded-xl border border-border shadow-card overflow-hidden">
          <div className="p-6 border-b border-border">
            <h2 className="text-xl font-heading-semibold text-card-foreground">Configurações de UR</h2>
            <p className="text-sm text-muted-foreground mt-1">Limites mínimos e máximos por ambiente.</p>
          </div>
          <div className="divide-y divide-border">
            {configs.map((config) => (
              <div key={config.id} className="p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 hover:bg-accent/30 transition-smooth">
                <div>
                  <div className="font-body-medium text-card-foreground">{config.localNome}</div>
                  <div className="text-sm text-muted-foreground mt-1">UR: {formatDecimal(config.umidadeMinima)}% até {formatDecimal(config.umidadeMaxima)}%</div>
                </div>
                <div className="flex items-center gap-1">
                  <button type="button" onClick={() => editConfig(config)} className="p-2 rounded-lg text-muted-foreground hover:text-card-foreground hover:bg-accent" title="Editar"><Icon name="Edit3" size={16} /></button>
                  <button type="button" onClick={() => deleteConfig(config)} className="p-2 rounded-lg text-error hover:bg-error/10" title="Remover"><Icon name="Trash2" size={16} /></button>
                </div>
              </div>
            ))}
            {configs.length === 0 ? <div className="p-8 text-center text-sm text-muted-foreground">Nenhum ambiente parametrizado.</div> : null}
          </div>
        </div>
      </section>

      <section className="grid grid-cols-1 xl:grid-cols-[1.15fr_0.85fr] gap-6">
        <div className="bg-card rounded-xl border border-border shadow-card p-6">
          <div className="mb-6">
            <h2 className="text-xl font-heading-semibold text-card-foreground">Conformidade de UR por dia</h2>
            <p className="text-sm text-muted-foreground mt-1">Quantidade de medições dentro e fora do limite.</p>
          </div>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={charts.daily || []}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={chartColors.light} />
                <XAxis dataKey="dia" tick={{ fontSize: 12, fill: chartColors.axis }} axisLine={{ stroke: chartColors.light }} tickLine={{ stroke: chartColors.light }} />
                <YAxis tick={{ fontSize: 12, fill: chartColors.axis }} axisLine={{ stroke: chartColors.light }} tickLine={{ stroke: chartColors.light }} />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Bar dataKey="conforme" name="Dentro do padrão" fill={chartColors.orange} radius={[8, 8, 0, 0]} />
                <Bar dataKey="alerta" name="Alertas" fill={chartColors.brown} radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-card rounded-xl border border-border shadow-card p-6">
          <div className="mb-4">
            <h2 className="text-xl font-heading-semibold text-card-foreground">Status das medições</h2>
            <p className="text-sm text-muted-foreground mt-1">Distribuição das medições de UR.</p>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={statusPieData} dataKey="quantidade" nameKey="nome" innerRadius={55} outerRadius={90} paddingAngle={2}>
                  {statusPieData.map((entry, index) => (
                    <Cell key={`${entry.nome}-${index}`} fill={entry.empty ? "#EADFD6" : chartColors.pie[index % chartColors.pie.length]} opacity={entry.empty ? 0.6 : 1} />
                  ))}
                </Pie>
                <Tooltip formatter={(value, name) => [formatNumber(value), name]} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </section>

      <section className="bg-card rounded-xl border border-border shadow-card overflow-hidden">
        <div className="p-6 border-b border-border flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h2 className="text-xl font-heading-semibold text-card-foreground">Histórico de umidade (UR)</h2>
            <p className="text-sm text-muted-foreground mt-1">Registros de umidade relativa por ambiente.</p>
          </div>
          <Input type="search" placeholder="Buscar ambiente, responsável ou observação..." value={search} onChange={(e) => setSearch(e.target.value)} className="lg:w-80" />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/60">
              <tr>
                <th className="p-4 text-left text-xs text-muted-foreground uppercase">Data</th>
                <th className="p-4 text-left text-xs text-muted-foreground uppercase">Ambiente</th>
                <th className="p-4 text-left text-xs text-muted-foreground uppercase">UR</th>
                <th className="p-4 text-left text-xs text-muted-foreground uppercase">Responsável</th>
                <th className="p-4 text-left text-xs text-muted-foreground uppercase">Status</th>
                <th className="p-4 text-right text-xs text-muted-foreground uppercase">Ações</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="p-8 text-center text-sm text-muted-foreground">Carregando...</td></tr>
              ) : filteredRecords.length === 0 ? (
                <tr><td colSpan={6} className="p-8 text-center text-sm text-muted-foreground">Nenhuma medição de UR encontrada.</td></tr>
              ) : filteredRecords.map((record) => {
                const st = temperatureStatusInfo[record.statusGeral] || temperatureStatusInfo.sem_parametro;
                return (
                  <tr key={record.id} className="border-b border-border hover:bg-accent/30 transition-smooth">
                    <td className="p-4 text-sm text-muted-foreground whitespace-nowrap"><div>{formatDate(record.data)}</div><div className="text-xs">{record.horario}</div></td>
                    <td className="p-4 min-w-[260px]"><div className="font-body-medium text-card-foreground">{record.localNome}</div><div className="text-xs text-muted-foreground mt-1">{record.objetoAmbiente || "Sem objeto/ambiente informado"}</div>{record.observacao ? <div className="text-xs text-muted-foreground mt-1">{record.observacao}</div> : null}</td>
                    <td className="p-4 min-w-[220px]"><div className="font-data text-card-foreground">{formatDecimal(record.umidade)}%</div>{record.faixa ? <div className="text-xs text-muted-foreground mt-1">Limite: {formatDecimal(record.faixa.umidadeMinima)}% a {formatDecimal(record.faixa.umidadeMaxima)}%</div> : null}</td>
                    <td className="p-4 text-sm text-muted-foreground">{record.responsavel || "-"}</td>
                    <td className="p-4"><span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-body-medium ${st.cls}`}><Icon name={st.icon} size={13} />{st.label}</span></td>
                    <td className="p-4"><div className="flex justify-end gap-1"><button type="button" onClick={() => editMeasurement(record)} className="p-2 rounded-lg text-muted-foreground hover:text-card-foreground hover:bg-accent" title="Editar"><Icon name="Edit3" size={16} /></button><button type="button" onClick={() => deleteMeasurement(record)} className="p-2 rounded-lg text-error hover:bg-error/10" title="Remover"><Icon name="Trash2" size={16} /></button></div></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}


function HygieneSection({
  records,
  summary,
  filteredTotals,
  charts,
  alerts,
  period,
  setPeriod,
  form,
  setForm,
  editingId,
  setEditingId,
  search,
  setSearch,
  loading,
  saving,
  setSaving,
  message,
  setMessage,
  reload,
}) {
  const filteredRecords = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return records;
    return records.filter((item) =>
      [item.funcionarioNome, item.responsavel, item.observacao, ...(item.naoConformes || [])]
        .some((value) => String(value || "").toLowerCase().includes(term))
    );
  }, [records, search]);

  const resetForm = () => {
    setEditingId(null);
    setForm({ ...blankHygieneForm, data: today(), itens: hygieneItems.reduce((acc, item) => ({ ...acc, [item.key]: "C" }), {}) });
  };

  const setItemStatus = (key, value) => {
    setForm((prev) => ({ ...prev, itens: { ...(prev.itens || {}), [key]: value } }));
  };

  const submitRecord = async (event) => {
    event.preventDefault();
    setSaving(true);
    setMessage({ type: "", text: "" });
    try {
      const payload = {
        data: form.data,
        funcionarioNome: form.funcionarioNome,
        responsavel: form.responsavel,
        observacao: form.observacao,
        itens: form.itens || {},
      };
      if (editingId) {
        await axios.put(`${apiBase}/api/controle-producao/higiene-pessoal/${editingId}`, payload);
        setMessage({ type: "success", text: "Registro de higiene atualizado com sucesso." });
      } else {
        await axios.post(`${apiBase}/api/controle-producao/higiene-pessoal`, payload);
        setMessage({ type: "success", text: "Registro de higiene cadastrado com sucesso." });
      }
      resetForm();
      await reload();
    } catch (error) {
      setMessage({ type: "error", text: error?.response?.data?.message || error?.message || "Erro ao salvar registro de higiene." });
    } finally {
      setSaving(false);
    }
  };

  const editRecord = (record) => {
    setEditingId(record.id);
    setForm({
      data: record.data || today(),
      funcionarioNome: record.funcionarioNome || "",
      responsavel: record.responsavel || "",
      observacao: record.observacao || "",
      itens: { ...blankHygieneForm.itens, ...(record.itens || {}) },
    });
    document.getElementById("pcp-higiene-form")?.scrollIntoView({ behavior: "smooth" });
  };

  const deleteRecord = async (record) => {
    if (!window.confirm(`Remover registro de higiene de ${record.funcionarioNome} em ${formatDate(record.data)}?`)) return;
    setSaving(true);
    try {
      await axios.delete(`${apiBase}/api/controle-producao/higiene-pessoal/${record.id}`);
      setMessage({ type: "success", text: "Registro removido com sucesso." });
      await reload();
    } catch (error) {
      setMessage({ type: "error", text: error?.response?.data?.message || error?.message || "Erro ao remover registro." });
    } finally {
      setSaving(false);
    }
  };

  const statusPieData = [
    { nome: "Conformes", quantidade: filteredTotals?.registrosConformes || 0 },
    { nome: "Não conformes", quantidade: filteredTotals?.registrosNaoConformes || 0 },
  ].filter((item) => item.quantidade > 0);
  const pieData = statusPieData.length ? statusPieData : [{ nome: "Sem registros", quantidade: 1, empty: true }];

  return (
    <div className="space-y-6">
      {message.text ? (
        <div className={`rounded-xl border px-4 py-3 text-sm flex gap-2 ${message.type === "error" ? "bg-error/10 text-error border-error/20" : "bg-success/10 text-success border-success/20"}`}>
          <Icon name={message.type === "error" ? "AlertCircle" : "CheckCircle2"} size={18} />
          {message.text}
        </div>
      ) : null}

      <section className="grid grid-cols-1 xl:grid-cols-[1fr_1.05fr] gap-6">
        <form id="pcp-higiene-form" onSubmit={submitRecord} className="bg-card rounded-xl border border-border shadow-card p-6 space-y-5">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h2 className="text-xl font-heading-semibold text-card-foreground">{editingId ? "Editar registro de higiene" : "Registrar higiene pessoal"}</h2>
              <p className="text-sm text-muted-foreground mt-1">Informe o funcionário, data, responsável e marque C ou NC para cada item.</p>
            </div>
            {editingId ? <Button type="button" variant="ghost" size="sm" onClick={resetForm}>Cancelar</Button> : null}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input type="date" label="Data" value={form.data} onChange={(e) => setForm((p) => ({ ...p, data: e.target.value }))} required />
            <Input label="Funcionário" placeholder="Nome do funcionário" value={form.funcionarioNome} onChange={(e) => setForm((p) => ({ ...p, funcionarioNome: e.target.value }))} required />
            <Input label="Responsável" placeholder="Responsável pela verificação" value={form.responsavel} onChange={(e) => setForm((p) => ({ ...p, responsavel: e.target.value }))} />
          </div>

          <div className="rounded-xl border border-[#eadfd6] bg-[#fff7ed] p-4">
            <div className="flex items-center gap-2 mb-4">
              <Icon name="ClipboardCheck" size={17} className="text-[#D97706]" />
              <p className="text-sm font-body-medium text-card-foreground">Itens de verificação</p>
              <span className="text-xs text-muted-foreground">C = Conforme | NC = Não conforme</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {hygieneItems.map((item) => {
                const value = form.itens?.[item.key] || "C";
                return (
                  <div key={item.key} className="rounded-lg border border-[#eadfd6] bg-white/80 p-3 flex items-center justify-between gap-3">
                    <span className="text-sm font-body-medium text-card-foreground">{item.label}</span>
                    <div className="inline-flex rounded-lg border border-[#eadfd6] overflow-hidden bg-[#fffaf1]">
                      <button type="button" onClick={() => setItemStatus(item.key, "C")} className={cn("px-3 py-1.5 text-xs font-body-semibold", value === "C" ? "bg-success text-success-foreground" : "text-muted-foreground hover:bg-muted")}>C</button>
                      <button type="button" onClick={() => setItemStatus(item.key, "NC")} className={cn("px-3 py-1.5 text-xs font-body-semibold", value === "NC" ? "bg-error text-error-foreground" : "text-muted-foreground hover:bg-muted")}>NC</button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <Input label="Observação" placeholder="Ex: colaborador sem touca, máscara substituída, orientação realizada" value={form.observacao} onChange={(e) => setForm((p) => ({ ...p, observacao: e.target.value }))} />
          <Button type="submit" fullWidth loading={saving} iconName={editingId ? "Save" : "Plus"}>{editingId ? "Salvar registro" : "Cadastrar registro"}</Button>
        </form>

        <div className="bg-card rounded-xl border border-border shadow-card p-6 space-y-5">
          <div>
            <h2 className="text-xl font-heading-semibold text-card-foreground">Resumo de higiene pessoal</h2>
            <p className="text-sm text-muted-foreground mt-1">Conformidade dos EPIs e ausência de barba/adornos.</p>
          </div>
          <div className="rounded-xl border border-[#eadfd6] bg-[#fff7ed] p-4">
            <div className="flex items-center gap-2 mb-3">
              <Icon name="CalendarRange" size={17} className="text-[#D97706]" />
              <p className="text-sm font-body-medium text-card-foreground">Filtrar por data</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Input type="date" label="De" value={period.de} onChange={(e) => setPeriod((p) => ({ ...p, de: e.target.value }))} />
              <Input type="date" label="Até" value={period.ate} onChange={(e) => setPeriod((p) => ({ ...p, ate: e.target.value }))} />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-1 gap-4">
            <SummaryBlock label="Hoje" data={summary?.dia} icon="UserCheck" mode="hygiene" />
            <SummaryBlock label="Semana" data={summary?.semana} icon="CalendarDays" mode="hygiene" />
            <SummaryBlock label="Mês" data={summary?.mes} icon="CalendarCheck" mode="hygiene" />
          </div>
          <div className="rounded-xl border border-[#F29F05]/30 bg-gradient-to-r from-[#5A3528] to-[#8B5E3C] p-4 text-white">
            <div className="flex items-center justify-between gap-3">
              <div><p className="text-sm font-body-medium">Período filtrado</p><p className="text-xs text-white/75 mt-1">{formatDate(period.de)} até {formatDate(period.ate)}</p></div>
              <div className="text-right"><p className="text-2xl font-heading-bold text-[#F29F05]">{formatPercent(filteredTotals?.conformidade)}</p><p className="text-xs text-white/75">conformidade</p></div>
            </div>
          </div>
        </div>
      </section>

      <section className="grid grid-cols-1 xl:grid-cols-[1.2fr_0.8fr] gap-6">
        <div className="bg-card rounded-xl border border-border shadow-card p-6">
          <div className="mb-6"><h2 className="text-xl font-heading-semibold text-card-foreground">Conformidade por dia</h2><p className="text-sm text-muted-foreground mt-1">Registros conformes e não conformes por período.</p></div>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={charts.daily || []}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={chartColors.light} />
                <XAxis dataKey="dia" tick={{ fontSize: 12, fill: chartColors.axis }} axisLine={{ stroke: chartColors.light }} tickLine={{ stroke: chartColors.light }} />
                <YAxis tick={{ fontSize: 12, fill: chartColors.axis }} axisLine={{ stroke: chartColors.light }} tickLine={{ stroke: chartColors.light }} />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Bar dataKey="conformes" name="Conformes" fill={chartColors.orange} radius={[8, 8, 0, 0]} />
                <Bar dataKey="naoConformes" name="Não conformes" fill={chartColors.brown} radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="bg-card rounded-xl border border-border shadow-card p-6">
          <div className="mb-4"><h2 className="text-xl font-heading-semibold text-card-foreground">Status dos registros</h2><p className="text-sm text-muted-foreground mt-1">Distribuição de registros conformes e não conformes.</p></div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={pieData} dataKey="quantidade" nameKey="nome" innerRadius={55} outerRadius={90} paddingAngle={2}>
                  {pieData.map((entry, index) => <Cell key={`${entry.nome}-${index}`} fill={entry.empty ? "#EADFD6" : chartColors.pie[index % chartColors.pie.length]} opacity={entry.empty ? 0.6 : 1} />)}
                </Pie>
                <Tooltip formatter={(value, name) => [formatNumber(value), name]} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </section>

      <section className="grid grid-cols-1 xl:grid-cols-[0.95fr_1.05fr] gap-6">
        <div className="bg-card rounded-xl border border-border shadow-card p-6">
          <div className="mb-6"><h2 className="text-xl font-heading-semibold text-card-foreground">Itens com mais não conformidades</h2><p className="text-sm text-muted-foreground mt-1">Ranking dos itens que mais precisam de atenção.</p></div>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={charts.items || []} layout="vertical" margin={{ left: 25 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke={chartColors.light} />
                <XAxis type="number" tick={{ fontSize: 12, fill: chartColors.axis }} axisLine={{ stroke: chartColors.light }} tickLine={{ stroke: chartColors.light }} />
                <YAxis type="category" dataKey="item" tick={{ fontSize: 11, fill: chartColors.axis }} width={150} axisLine={{ stroke: chartColors.light }} tickLine={{ stroke: chartColors.light }} />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Bar dataKey="naoConformes" name="Não conformes" fill={chartColors.brown} radius={[0, 8, 8, 0]} />
                <Bar dataKey="conformes" name="Conformes" fill={chartColors.orange} radius={[0, 8, 8, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-card rounded-xl border border-border shadow-card overflow-hidden">
          <div className="p-6 border-b border-border"><h2 className="text-xl font-heading-semibold text-card-foreground">Quadro de não conformidades</h2><p className="text-sm text-muted-foreground mt-1">Registros com algum item NC.</p></div>
          <div className="divide-y divide-border">
            {alerts.length === 0 ? <div className="p-8 text-center text-sm text-muted-foreground">Nenhuma não conformidade no período.</div> : alerts.map((alert) => (
              <div key={alert.id} className="p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div><div className="flex items-center gap-2 flex-wrap"><span className="font-body-medium text-card-foreground">{alert.funcionarioNome}</span><span className="text-xs text-muted-foreground">{formatDate(alert.data)}</span></div><div className="text-sm text-muted-foreground mt-1">NC: {(alert.naoConformes || []).join(", ")}</div>{alert.responsavel ? <div className="text-xs text-muted-foreground mt-1">Responsável: {alert.responsavel}</div> : null}</div>
                <div className="flex gap-2 items-center flex-wrap"><span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-body-medium bg-error/10 text-error border-error/20"><Icon name="AlertTriangle" size={13} />Não conforme</span><button type="button" onClick={() => editRecord(alert)} className="p-2 rounded-lg text-muted-foreground hover:text-card-foreground hover:bg-accent" title="Editar"><Icon name="Edit3" size={16} /></button></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-card rounded-xl border border-border shadow-card overflow-hidden">
        <div className="p-6 border-b border-border flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div><h2 className="text-xl font-heading-semibold text-card-foreground">Histórico de higiene pessoal</h2><p className="text-sm text-muted-foreground mt-1">Funcionário, data, itens conformes/não conformes e responsável.</p></div>
          <Input type="search" placeholder="Buscar funcionário, responsável ou item..." value={search} onChange={(e) => setSearch(e.target.value)} className="lg:w-80" />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/60">
              <tr>
                <th className="p-4 text-left text-xs text-muted-foreground uppercase">Data</th>
                <th className="p-4 text-left text-xs text-muted-foreground uppercase">Funcionário</th>
                <th className="p-4 text-left text-xs text-muted-foreground uppercase">Itens</th>
                <th className="p-4 text-left text-xs text-muted-foreground uppercase">Responsável</th>
                <th className="p-4 text-left text-xs text-muted-foreground uppercase">Status</th>
                <th className="p-4 text-right text-xs text-muted-foreground uppercase">Ações</th>
              </tr>
            </thead>
            <tbody>
              {loading ? <tr><td colSpan={6} className="p-8 text-center text-sm text-muted-foreground">Carregando...</td></tr> : filteredRecords.length === 0 ? <tr><td colSpan={6} className="p-8 text-center text-sm text-muted-foreground">Nenhum registro de higiene encontrado.</td></tr> : filteredRecords.map((record) => {
                const st = hygieneStatusInfo[record.statusGeral] || hygieneStatusInfo.conforme;
                return (
                  <tr key={record.id} className="border-b border-border hover:bg-accent/30 transition-smooth">
                    <td className="p-4 text-sm text-muted-foreground whitespace-nowrap">{formatDate(record.data)}</td>
                    <td className="p-4 min-w-[220px]"><div className="font-body-medium text-card-foreground">{record.funcionarioNome}</div>{record.observacao ? <div className="text-xs text-muted-foreground mt-1">{record.observacao}</div> : null}</td>
                    <td className="p-4 min-w-[320px]"><div className="flex flex-wrap gap-1.5">{hygieneItems.map((item) => <span key={item.key} className={cn("inline-flex items-center rounded-full border px-2 py-0.5 text-xs", record.itens?.[item.key] === "NC" ? "bg-error/10 text-error border-error/20" : "bg-success/10 text-success border-success/20")}>{item.label}: {record.itens?.[item.key] || "C"}</span>)}</div></td>
                    <td className="p-4 text-sm text-muted-foreground min-w-[180px]">{record.responsavel || "-"}</td>
                    <td className="p-4"><span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-body-medium ${st.cls}`}><Icon name={st.icon} size={13} />{st.label}</span></td>
                    <td className="p-4"><div className="flex justify-end gap-1"><button type="button" onClick={() => editRecord(record)} className="p-2 rounded-lg text-muted-foreground hover:text-card-foreground hover:bg-accent" title="Editar"><Icon name="Edit3" size={16} /></button><button type="button" onClick={() => deleteRecord(record)} className="p-2 rounded-lg text-error hover:bg-error/10" title="Remover"><Icon name="Trash2" size={16} /></button></div></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}


function BuildingCleaningSection({
  records,
  summary,
  filteredTotals,
  charts,
  alerts,
  period,
  setPeriod,
  form,
  setForm,
  editingId,
  setEditingId,
  search,
  setSearch,
  loading,
  saving,
  setSaving,
  message,
  setMessage,
  reload,
}) {
  const groupedItems = useMemo(() => {
    return buildingCleaningItems.reduce((acc, item) => {
      const group = item.grupo || "Geral";
      acc[group] = acc[group] || [];
      acc[group].push(item);
      return acc;
    }, {});
  }, []);

  const filteredRecords = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return records;
    return records.filter((item) =>
      [item.responsavel, item.acaoCorretiva, item.observacao, ...(item.naoConformes || [])]
        .some((value) => String(value || "").toLowerCase().includes(term))
    );
  }, [records, search]);

  const resetForm = () => {
    setEditingId(null);
    setForm({ ...blankBuildingCleaningForm, data: today(), itens: buildingCleaningItems.reduce((acc, item) => ({ ...acc, [item.key]: "C" }), {}) });
  };

  const setItemStatus = (key, value) => {
    setForm((prev) => ({ ...prev, itens: { ...(prev.itens || {}), [key]: value } }));
  };

  const submitRecord = async (event) => {
    event.preventDefault();
    setSaving(true);
    setMessage({ type: "", text: "" });
    try {
      const payload = {
        data: form.data,
        responsavel: form.responsavel,
        acaoCorretiva: form.acaoCorretiva,
        observacao: form.observacao,
        itens: form.itens || {},
      };
      if (editingId) {
        await axios.put(`${apiBase}/api/controle-producao/limpeza-edificacao/${editingId}`, payload);
        setMessage({ type: "success", text: "Registro de limpeza atualizado com sucesso." });
      } else {
        await axios.post(`${apiBase}/api/controle-producao/limpeza-edificacao`, payload);
        setMessage({ type: "success", text: "Registro de limpeza cadastrado com sucesso." });
      }
      resetForm();
      await reload();
    } catch (error) {
      setMessage({ type: "error", text: error?.response?.data?.message || error?.message || "Erro ao salvar registro de limpeza." });
    } finally {
      setSaving(false);
    }
  };

  const editRecord = (record) => {
    setEditingId(record.id);
    setForm({
      data: record.data || today(),
      responsavel: record.responsavel || "",
      acaoCorretiva: record.acaoCorretiva || "",
      observacao: record.observacao || "",
      itens: { ...blankBuildingCleaningForm.itens, ...(record.itens || {}) },
    });
    document.getElementById("pcp-limpeza-form")?.scrollIntoView({ behavior: "smooth" });
  };

  const deleteRecord = async (record) => {
    if (!window.confirm(`Remover registro de limpeza em ${formatDate(record.data)}?`)) return;
    setSaving(true);
    try {
      await axios.delete(`${apiBase}/api/controle-producao/limpeza-edificacao/${record.id}`);
      setMessage({ type: "success", text: "Registro removido com sucesso." });
      await reload();
    } catch (error) {
      setMessage({ type: "error", text: error?.response?.data?.message || error?.message || "Erro ao remover registro." });
    } finally {
      setSaving(false);
    }
  };

  const statusPieData = [
    { nome: "Conformes", quantidade: filteredTotals?.registrosConformes || 0 },
    { nome: "Não conformes", quantidade: filteredTotals?.registrosNaoConformes || 0 },
  ].filter((item) => item.quantidade > 0);
  const pieData = statusPieData.length ? statusPieData : [{ nome: "Sem registros", quantidade: 1, empty: true }];

  return (
    <div className="space-y-6">
      {message.text ? (
        <div className={`rounded-xl border px-4 py-3 text-sm flex gap-2 ${message.type === "error" ? "bg-error/10 text-error border-error/20" : "bg-success/10 text-success border-success/20"}`}>
          <Icon name={message.type === "error" ? "AlertCircle" : "CheckCircle2"} size={18} />
          {message.text}
        </div>
      ) : null}

      <section className="grid grid-cols-1 xl:grid-cols-[1fr_1.05fr] gap-6">
        <form id="pcp-limpeza-form" onSubmit={submitRecord} className="bg-card rounded-xl border border-border shadow-card p-6 space-y-5">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h2 className="text-xl font-heading-semibold text-card-foreground">{editingId ? "Editar limpeza da edificação" : "Registrar limpeza da edificação"}</h2>
              <p className="text-sm text-muted-foreground mt-1">Marque C ou NC para bancadas, máquinas, utensílios e estrutura. Informe ação corretiva quando necessário.</p>
            </div>
            {editingId ? <Button type="button" variant="ghost" size="sm" onClick={resetForm}>Cancelar</Button> : null}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input type="date" label="Data" value={form.data} onChange={(e) => setForm((p) => ({ ...p, data: e.target.value }))} required />
            <Input label="Responsável" placeholder="Responsável pela verificação" value={form.responsavel} onChange={(e) => setForm((p) => ({ ...p, responsavel: e.target.value }))} />
          </div>

          <div className="rounded-xl border border-[#eadfd6] bg-[#fff7ed] p-4 space-y-5">
            <div className="flex items-center gap-2">
              <Icon name="ClipboardCheck" size={17} className="text-[#D97706]" />
              <p className="text-sm font-body-medium text-card-foreground">Itens de limpeza da edificação</p>
              <span className="text-xs text-muted-foreground">C = Conforme | NC = Não conforme</span>
            </div>
            {Object.entries(groupedItems).map(([group, items]) => (
              <div key={group} className="space-y-3">
                <div className="inline-flex items-center gap-2 rounded-full bg-[#5A3528]/10 text-[#5A3528] px-3 py-1 text-xs font-body-semibold">
                  <Icon name={group === "Máquinas" ? "Cog" : group === "Estrutura" ? "Building2" : "Sparkles"} size={14} />
                  {group}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {items.map((item) => {
                    const value = form.itens?.[item.key] || "C";
                    return (
                      <div key={item.key} className="rounded-lg border border-[#eadfd6] bg-white/80 p-3 flex items-center justify-between gap-3">
                        <span className="text-sm font-body-medium text-card-foreground">{item.label}</span>
                        <div className="inline-flex rounded-lg border border-[#eadfd6] overflow-hidden bg-[#fffaf1]">
                          <button type="button" onClick={() => setItemStatus(item.key, "C")} className={cn("px-3 py-1.5 text-xs font-body-semibold", value === "C" ? "bg-success text-success-foreground" : "text-muted-foreground hover:bg-muted")}>C</button>
                          <button type="button" onClick={() => setItemStatus(item.key, "NC")} className={cn("px-3 py-1.5 text-xs font-body-semibold", value === "NC" ? "bg-error text-error-foreground" : "text-muted-foreground hover:bg-muted")}>NC</button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          <Input label="Ação corretiva" placeholder="Ex: limpeza refeita, equipamento higienizado, orientação realizada" value={form.acaoCorretiva} onChange={(e) => setForm((p) => ({ ...p, acaoCorretiva: e.target.value }))} />
          <Input label="Observação" placeholder="Observação interna opcional" value={form.observacao} onChange={(e) => setForm((p) => ({ ...p, observacao: e.target.value }))} />
          <Button type="submit" fullWidth loading={saving} iconName={editingId ? "Save" : "Plus"}>{editingId ? "Salvar registro" : "Cadastrar registro"}</Button>
        </form>

        <div className="bg-card rounded-xl border border-border shadow-card p-6 space-y-5">
          <div>
            <h2 className="text-xl font-heading-semibold text-card-foreground">Resumo de limpeza da edificação</h2>
            <p className="text-sm text-muted-foreground mt-1">Conformidade dos itens de limpeza, máquinas e estrutura.</p>
          </div>
          <div className="rounded-xl border border-[#eadfd6] bg-[#fff7ed] p-4">
            <div className="flex items-center gap-2 mb-3">
              <Icon name="CalendarRange" size={17} className="text-[#D97706]" />
              <p className="text-sm font-body-medium text-card-foreground">Filtrar por data</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Input type="date" label="De" value={period.de} onChange={(e) => setPeriod((p) => ({ ...p, de: e.target.value }))} />
              <Input type="date" label="Até" value={period.ate} onChange={(e) => setPeriod((p) => ({ ...p, ate: e.target.value }))} />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-1 gap-4">
            <SummaryBlock label="Hoje" data={summary?.dia} icon="Sparkles" mode="hygiene" />
            <SummaryBlock label="Semana" data={summary?.semana} icon="CalendarDays" mode="hygiene" />
            <SummaryBlock label="Mês" data={summary?.mes} icon="CalendarCheck" mode="hygiene" />
          </div>
          <div className="rounded-xl border border-[#F29F05]/30 bg-gradient-to-r from-[#5A3528] to-[#8B5E3C] p-4 text-white">
            <div className="flex items-center justify-between gap-3">
              <div><p className="text-sm font-body-medium">Período filtrado</p><p className="text-xs text-white/75 mt-1">{formatDate(period.de)} até {formatDate(period.ate)}</p></div>
              <div className="text-right"><p className="text-2xl font-heading-bold text-[#F29F05]">{formatPercent(filteredTotals?.conformidade)}</p><p className="text-xs text-white/75">conformidade</p></div>
            </div>
          </div>
        </div>
      </section>

      <section className="grid grid-cols-1 xl:grid-cols-[1.2fr_0.8fr] gap-6">
        <div className="bg-card rounded-xl border border-border shadow-card p-6">
          <div className="mb-6"><h2 className="text-xl font-heading-semibold text-card-foreground">Conformidade por dia</h2><p className="text-sm text-muted-foreground mt-1">Registros conformes e não conformes por período.</p></div>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={charts.daily || []}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={chartColors.light} />
                <XAxis dataKey="dia" tick={{ fontSize: 12, fill: chartColors.axis }} axisLine={{ stroke: chartColors.light }} tickLine={{ stroke: chartColors.light }} />
                <YAxis tick={{ fontSize: 12, fill: chartColors.axis }} axisLine={{ stroke: chartColors.light }} tickLine={{ stroke: chartColors.light }} />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Bar dataKey="conformes" name="Conformes" fill={chartColors.orange} radius={[8, 8, 0, 0]} />
                <Bar dataKey="naoConformes" name="Não conformes" fill={chartColors.brown} radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="bg-card rounded-xl border border-border shadow-card p-6">
          <div className="mb-4"><h2 className="text-xl font-heading-semibold text-card-foreground">Status dos registros</h2><p className="text-sm text-muted-foreground mt-1">Distribuição de registros conformes e não conformes.</p></div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={pieData} dataKey="quantidade" nameKey="nome" innerRadius={55} outerRadius={90} paddingAngle={2}>
                  {pieData.map((entry, index) => <Cell key={`${entry.nome}-${index}`} fill={entry.empty ? "#EADFD6" : chartColors.pie[index % chartColors.pie.length]} opacity={entry.empty ? 0.6 : 1} />)}
                </Pie>
                <Tooltip formatter={(value, name) => [formatNumber(value), name]} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </section>

      <section className="grid grid-cols-1 xl:grid-cols-[0.95fr_1.05fr] gap-6">
        <div className="bg-card rounded-xl border border-border shadow-card p-6">
          <div className="mb-6"><h2 className="text-xl font-heading-semibold text-card-foreground">Itens com mais não conformidades</h2><p className="text-sm text-muted-foreground mt-1">Ranking dos itens que mais precisam de atenção.</p></div>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={charts.items || []} layout="vertical" margin={{ left: 25 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke={chartColors.light} />
                <XAxis type="number" tick={{ fontSize: 12, fill: chartColors.axis }} axisLine={{ stroke: chartColors.light }} tickLine={{ stroke: chartColors.light }} />
                <YAxis type="category" dataKey="item" tick={{ fontSize: 11, fill: chartColors.axis }} width={170} axisLine={{ stroke: chartColors.light }} tickLine={{ stroke: chartColors.light }} />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Bar dataKey="naoConformes" name="Não conformes" fill={chartColors.brown} radius={[0, 8, 8, 0]} />
                <Bar dataKey="conformes" name="Conformes" fill={chartColors.orange} radius={[0, 8, 8, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-card rounded-xl border border-border shadow-card overflow-hidden">
          <div className="p-6 border-b border-border"><h2 className="text-xl font-heading-semibold text-card-foreground">Quadro de ação corretiva</h2><p className="text-sm text-muted-foreground mt-1">Registros com algum item NC.</p></div>
          <div className="divide-y divide-border">
            {alerts.length === 0 ? <div className="p-8 text-center text-sm text-muted-foreground">Nenhuma não conformidade no período.</div> : alerts.map((alert) => (
              <div key={alert.id} className="p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div><div className="flex items-center gap-2 flex-wrap"><span className="font-body-medium text-card-foreground">{formatDate(alert.data)}</span></div><div className="text-sm text-muted-foreground mt-1">NC: {(alert.naoConformes || []).join(", ")}</div>{alert.acaoCorretiva ? <div className="text-xs text-muted-foreground mt-1">Ação corretiva: {alert.acaoCorretiva}</div> : null}{alert.responsavel ? <div className="text-xs text-muted-foreground mt-1">Responsável: {alert.responsavel}</div> : null}</div>
                <div className="flex gap-2 items-center flex-wrap"><span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-body-medium bg-error/10 text-error border-error/20"><Icon name="AlertTriangle" size={13} />Não conforme</span><button type="button" onClick={() => editRecord(alert)} className="p-2 rounded-lg text-muted-foreground hover:text-card-foreground hover:bg-accent" title="Editar"><Icon name="Edit3" size={16} /></button></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-card rounded-xl border border-border shadow-card overflow-hidden">
        <div className="p-6 border-b border-border flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div><h2 className="text-xl font-heading-semibold text-card-foreground">Histórico de limpeza da edificação</h2><p className="text-sm text-muted-foreground mt-1">Data, itens conformes/não conformes, ação corretiva e responsável.</p></div>
          <Input type="search" placeholder="Buscar responsável, ação corretiva ou item..." value={search} onChange={(e) => setSearch(e.target.value)} className="lg:w-80" />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/60">
              <tr>
                <th className="p-4 text-left text-xs text-muted-foreground uppercase">Data</th>
                <th className="p-4 text-left text-xs text-muted-foreground uppercase">Itens</th>
                <th className="p-4 text-left text-xs text-muted-foreground uppercase">Ação corretiva</th>
                <th className="p-4 text-left text-xs text-muted-foreground uppercase">Responsável</th>
                <th className="p-4 text-left text-xs text-muted-foreground uppercase">Status</th>
                <th className="p-4 text-right text-xs text-muted-foreground uppercase">Ações</th>
              </tr>
            </thead>
            <tbody>
              {loading ? <tr><td colSpan={6} className="p-8 text-center text-sm text-muted-foreground">Carregando...</td></tr> : filteredRecords.length === 0 ? <tr><td colSpan={6} className="p-8 text-center text-sm text-muted-foreground">Nenhum registro de limpeza encontrado.</td></tr> : filteredRecords.map((record) => {
                const st = hygieneStatusInfo[record.statusGeral] || hygieneStatusInfo.conforme;
                return (
                  <tr key={record.id} className="border-b border-border hover:bg-accent/30 transition-smooth">
                    <td className="p-4 text-sm text-muted-foreground whitespace-nowrap">{formatDate(record.data)}</td>
                    <td className="p-4 min-w-[420px]"><div className="flex flex-wrap gap-1.5">{buildingCleaningItems.map((item) => <span key={item.key} className={cn("inline-flex items-center rounded-full border px-2 py-0.5 text-xs", record.itens?.[item.key] === "NC" ? "bg-error/10 text-error border-error/20" : "bg-success/10 text-success border-success/20")}>{item.label}: {record.itens?.[item.key] || "C"}</span>)}</div>{record.observacao ? <div className="text-xs text-muted-foreground mt-2">Obs.: {record.observacao}</div> : null}</td>
                    <td className="p-4 text-sm text-muted-foreground min-w-[220px]">{record.acaoCorretiva || "-"}</td>
                    <td className="p-4 text-sm text-muted-foreground min-w-[180px]">{record.responsavel || "-"}</td>
                    <td className="p-4"><span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-body-medium ${st.cls}`}><Icon name={st.icon} size={13} />{st.label}</span></td>
                    <td className="p-4"><div className="flex justify-end gap-1"><button type="button" onClick={() => editRecord(record)} className="p-2 rounded-lg text-muted-foreground hover:text-card-foreground hover:bg-accent" title="Editar"><Icon name="Edit3" size={16} /></button><button type="button" onClick={() => deleteRecord(record)} className="p-2 rounded-lg text-error hover:bg-error/10" title="Remover"><Icon name="Trash2" size={16} /></button></div></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function ScheduleFields({ prefix, label, form, setForm }) {
  const tipoKey = `${prefix}ScheduleTipo`;
  const intervaloKey = `${prefix}IntervaloDias`;
  const diaSemanaKey = `${prefix}DiaSemana`;
  const diaMesKey = `${prefix}DiaMes`;
  const tipo = form[tipoKey] || "intervalo";

  return (
    <div className="rounded-xl border border-[#eadfd6] bg-[#fff7ed] p-4 space-y-4">
      <div className="flex items-center gap-2">
        <Icon name={prefix === "limpeza" ? "Sparkles" : "Wrench"} size={17} className="text-[#D97706]" />
        <p className="text-sm font-body-semibold text-card-foreground">Cronograma de {label}</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">Tipo de cronograma</label>
          <select
            value={tipo}
            onChange={(e) => setForm((p) => ({ ...p, [tipoKey]: e.target.value }))}
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          >
            <option value="intervalo">A cada X dias</option>
            <option value="semanal">Dia fixo da semana</option>
            <option value="mensal">Dia fixo do mês</option>
          </select>
        </div>
        {tipo === "intervalo" ? (
          <Input
            type="number"
            min="1"
            step="1"
            label="Intervalo em dias"
            value={form[intervaloKey]}
            onChange={(e) => setForm((p) => ({ ...p, [intervaloKey]: e.target.value }))}
            required
          />
        ) : tipo === "semanal" ? (
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">Dia da semana</label>
            <select
              value={form[diaSemanaKey]}
              onChange={(e) => setForm((p) => ({ ...p, [diaSemanaKey]: e.target.value }))}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            >
              {weekDayOptions.map((day) => <option key={day.value} value={day.value}>{day.label}</option>)}
            </select>
          </div>
        ) : (
          <Input
            type="number"
            min="1"
            max="31"
            step="1"
            label="Dia do mês"
            value={form[diaMesKey]}
            onChange={(e) => setForm((p) => ({ ...p, [diaMesKey]: e.target.value }))}
            required
          />
        )}
      </div>
    </div>
  );
}

function EquipmentMaintenanceSection({ configs, records, filteredTotals, charts, alerts, period, setPeriod, form, setForm, editingId, setEditingId, configForm, setConfigForm, configEditingId, setConfigEditingId, search, setSearch, loading, saving, setSaving, message, setMessage, reload }) {
  const filteredRecords = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return records;
    return records.filter((item) => [item.equipamentoNome, item.tipo, item.conformidade, item.responsavel, item.observacao].some((value) => String(value || "").toLowerCase().includes(term)));
  }, [records, search]);

  const equipmentOptions = configs.map((item) => item.equipamentoNome);
  const resetRecordForm = () => { setEditingId(null); setForm({ ...blankEquipmentMaintenanceForm, data: today() }); };
  const resetConfigForm = () => { setConfigEditingId(null); setConfigForm({ ...blankEquipmentMaintenanceConfigForm, ativo: true }); };

  const schedulePayload = (prefix) => {
    const tipo = configForm[`${prefix}ScheduleTipo`] || "intervalo";
    return {
      tipo,
      intervaloDias: Number(configForm[`${prefix}IntervaloDias`] || 1),
      diasSemana: [Number(configForm[`${prefix}DiaSemana`] || 1)],
      diaMes: Number(configForm[`${prefix}DiaMes`] || 1),
    };
  };

  const submitRecord = async (event) => {
    event.preventDefault();
    setSaving(true);
    setMessage({ type: "", text: "" });
    try {
      const payload = { data: form.data, equipamentoNome: form.equipamentoNome, tipo: form.tipo, conformidade: form.conformidade, responsavel: form.responsavel, observacao: form.observacao };
      if (editingId) { await axios.put(`${apiBase}/api/controle-producao/limpeza-manutencao-equipamentos/${editingId}`, payload); setMessage({ type: "success", text: "Registro atualizado com sucesso." }); }
      else { await axios.post(`${apiBase}/api/controle-producao/limpeza-manutencao-equipamentos`, payload); setMessage({ type: "success", text: "Registro cadastrado com sucesso." }); }
      resetRecordForm();
      await reload();
    } catch (error) { setMessage({ type: "error", text: error?.response?.data?.message || error?.message || "Erro ao salvar registro." }); }
    finally { setSaving(false); }
  };

  const submitConfig = async (event) => {
    event.preventDefault();
    setSaving(true);
    setMessage({ type: "", text: "" });
    try {
      const payload = {
        equipamentoNome: configForm.equipamentoNome,
        limpezaSchedule: schedulePayload("limpeza"),
        manutencaoSchedule: schedulePayload("manutencao"),
        ativo: !!configForm.ativo,
      };
      if (configEditingId) { await axios.put(`${apiBase}/api/controle-producao/limpeza-manutencao-equipamentos/configuracoes/${configEditingId}`, payload); setMessage({ type: "success", text: "Cronograma atualizado com sucesso." }); }
      else { await axios.post(`${apiBase}/api/controle-producao/limpeza-manutencao-equipamentos/configuracoes`, payload); setMessage({ type: "success", text: "Equipamento parametrizado com sucesso." }); }
      resetConfigForm();
      await reload();
    } catch (error) { setMessage({ type: "error", text: error?.response?.data?.message || error?.message || "Erro ao salvar parametrização." }); }
    finally { setSaving(false); }
  };

  const editRecord = (record) => {
    setEditingId(record.id);
    setForm({ data: record.data || today(), equipamentoNome: record.equipamentoNome || "", tipo: record.tipo || "limpeza", conformidade: record.conformidade || "C", responsavel: record.responsavel || "", observacao: record.observacao || "" });
    document.getElementById("pcp-equipamento-registro-form")?.scrollIntoView({ behavior: "smooth" });
  };

  const deleteRecord = async (record) => {
    if (!window.confirm(`Remover registro de ${record.tipo === "manutencao" ? "manutenção" : "limpeza"} de ${record.equipamentoNome}?`)) return;
    setSaving(true);
    try { await axios.delete(`${apiBase}/api/controle-producao/limpeza-manutencao-equipamentos/${record.id}`); setMessage({ type: "success", text: "Registro removido com sucesso." }); await reload(); }
    catch (error) { setMessage({ type: "error", text: error?.response?.data?.message || error?.message || "Erro ao remover registro." }); }
    finally { setSaving(false); }
  };

  const editConfig = (config) => {
    const limpezaSchedule = config.limpezaSchedule || { tipo: "intervalo", intervaloDias: config.limpezaIntervaloDias || 7, diasSemana: [5], diaMes: 1 };
    const manutencaoSchedule = config.manutencaoSchedule || { tipo: "intervalo", intervaloDias: config.manutencaoIntervaloDias || 90, diasSemana: [5], diaMes: 1 };
    setConfigEditingId(config.id);
    setConfigForm({
      equipamentoNome: config.equipamentoNome || "",
      limpezaScheduleTipo: limpezaSchedule.tipo || "intervalo",
      limpezaIntervaloDias: String(limpezaSchedule.intervaloDias || config.limpezaIntervaloDias || 7),
      limpezaDiaSemana: String((limpezaSchedule.diasSemana || [5])[0] ?? 5),
      limpezaDiaMes: String(limpezaSchedule.diaMes || 1),
      manutencaoScheduleTipo: manutencaoSchedule.tipo || "intervalo",
      manutencaoIntervaloDias: String(manutencaoSchedule.intervaloDias || config.manutencaoIntervaloDias || 90),
      manutencaoDiaSemana: String((manutencaoSchedule.diasSemana || [5])[0] ?? 5),
      manutencaoDiaMes: String(manutencaoSchedule.diaMes || 1),
      ativo: config.ativo !== false,
    });
    document.getElementById("pcp-equipamento-config-form")?.scrollIntoView({ behavior: "smooth" });
  };

  const deleteConfig = async (config) => {
    if (!window.confirm(`Remover parametrização de ${config.equipamentoNome}?`)) return;
    setSaving(true);
    try { await axios.delete(`${apiBase}/api/controle-producao/limpeza-manutencao-equipamentos/configuracoes/${config.id}`); setMessage({ type: "success", text: "Parametrização removida com sucesso." }); await reload(); }
    catch (error) { setMessage({ type: "error", text: error?.response?.data?.message || error?.message || "Erro ao remover parametrização." }); }
    finally { setSaving(false); }
  };

  const statusBadge = (status) => status === "vencido" ? "bg-error/10 text-error border-error/20" : status === "hoje" ? "bg-[#F29F05]/15 text-[#8B5E00] border-[#F29F05]/25" : status === "pendente" ? "bg-warning/10 text-warning border-warning/20" : status === "proximo" ? "bg-[#F29F05]/15 text-[#8B5E00] border-[#F29F05]/25" : "bg-success/10 text-success border-success/20";
  const statusLabel = (status) => status === "vencido" ? "Vencido" : status === "hoje" ? "Hoje" : status === "pendente" ? "Pendente" : status === "proximo" ? "Próximo" : "Em dia";
  const todayAlerts = alerts.filter((item) => item.status === "hoje");

  return (
    <div className="space-y-6">
      {message.text ? <div className={`rounded-xl border px-4 py-3 text-sm flex gap-2 ${message.type === "error" ? "bg-error/10 text-error border-error/20" : "bg-success/10 text-success border-success/20"}`}><Icon name={message.type === "error" ? "AlertCircle" : "CheckCircle2"} size={18} />{message.text}</div> : null}

      <section className="bg-card rounded-xl border border-[#F29F05]/30 shadow-card p-5">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-xl bg-[#F29F05]/15 text-[#D97706] flex items-center justify-center"><Icon name="BellRing" size={22} /></div>
          <div className="flex-1">
            <h2 className="text-xl font-heading-semibold text-card-foreground">Alertas de hoje</h2>
            <p className="text-sm text-muted-foreground mt-1">Mostra automaticamente o que precisa lavar ou fazer manutenção hoje, conforme o cronograma cadastrado.</p>
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
              {todayAlerts.length === 0 ? <div className="rounded-xl border border-dashed border-border bg-muted/30 p-4 text-sm text-muted-foreground md:col-span-2 xl:col-span-3">Nenhum equipamento programado para hoje.</div> : todayAlerts.map((alert) => (
                <div key={alert.id} className="rounded-xl border border-[#F29F05]/25 bg-[#fff7ed] p-4">
                  <div className="flex items-center justify-between gap-3">
                    <div className="font-body-semibold text-card-foreground">{alert.equipamentoNome}</div>
                    <span className="text-xs rounded-full bg-[#F29F05]/15 text-[#8B5E00] px-2.5 py-1 border border-[#F29F05]/25">Hoje</span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-2">{alert.tipoLabel}: {alert.agendaDescricao}</p>
                  <p className="text-sm font-body-medium text-[#5A3528] mt-2">{alert.alertaTexto}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="grid grid-cols-1 xl:grid-cols-[0.9fr_1.1fr] gap-6">
        <form id="pcp-equipamento-registro-form" onSubmit={submitRecord} className="bg-card rounded-xl border border-border shadow-card p-6 space-y-5">
          <div className="flex items-center justify-between gap-4"><div><h2 className="text-xl font-heading-semibold text-card-foreground">{editingId ? "Editar registro" : "Registrar limpeza ou manutenção"}</h2><p className="text-sm text-muted-foreground mt-1">Informe equipamento, tipo, data, C/NC e responsável.</p></div>{editingId ? <Button type="button" variant="ghost" size="sm" onClick={resetRecordForm}>Cancelar</Button> : null}</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4"><Input type="date" label="Data" value={form.data} onChange={(e) => setForm((p) => ({ ...p, data: e.target.value }))} required /><div className="space-y-2"><label className="text-sm font-medium text-foreground">Tipo *</label><select value={form.tipo} onChange={(e) => setForm((p) => ({ ...p, tipo: e.target.value }))} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" required><option value="limpeza">Limpeza</option><option value="manutencao">Manutenção</option></select></div></div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4"><div className="space-y-2"><label className="text-sm font-medium text-foreground">Equipamento *</label><input list="pcp-equipamentos-lista" value={form.equipamentoNome} onChange={(e) => setForm((p) => ({ ...p, equipamentoNome: e.target.value }))} placeholder="Ex: FORNO 1" className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" required /><datalist id="pcp-equipamentos-lista">{equipmentOptions.map((item) => <option key={item} value={item} />)}</datalist></div><Input label="Responsável" placeholder="Responsável pelo registro" value={form.responsavel} onChange={(e) => setForm((p) => ({ ...p, responsavel: e.target.value }))} /></div>
          <div className="rounded-xl border border-[#eadfd6] bg-[#fff7ed] p-4"><p className="text-sm font-body-medium text-card-foreground mb-3">Status do registro</p><div className="grid grid-cols-2 gap-3"><button type="button" onClick={() => setForm((p) => ({ ...p, conformidade: "C" }))} className={cn("rounded-xl border p-3 text-sm font-body-semibold transition-smooth", form.conformidade === "C" ? "bg-success/10 border-success/30 text-success" : "bg-white border-border text-card-foreground hover:bg-muted/40")}>C - Conforme</button><button type="button" onClick={() => setForm((p) => ({ ...p, conformidade: "NC" }))} className={cn("rounded-xl border p-3 text-sm font-body-semibold transition-smooth", form.conformidade === "NC" ? "bg-error/10 border-error/30 text-error" : "bg-white border-border text-card-foreground hover:bg-muted/40")}>NC - Não conforme</button></div></div>
          <Input label="Observação" placeholder="Ex: limpeza realizada, troca de peça, revisão preventiva" value={form.observacao} onChange={(e) => setForm((p) => ({ ...p, observacao: e.target.value }))} />
          <Button type="submit" fullWidth loading={saving} iconName={editingId ? "Save" : "Plus"}>{editingId ? "Salvar registro" : "Cadastrar registro"}</Button>
        </form>
        <div className="bg-card rounded-xl border border-border shadow-card p-6 space-y-5"><div><h2 className="text-xl font-heading-semibold text-card-foreground">Resumo e alertas</h2><p className="text-sm text-muted-foreground mt-1">O sistema alerta por data fixa semanal/mensal ou intervalo de dias.</p></div><div className="rounded-xl border border-[#eadfd6] bg-[#fff7ed] p-4"><div className="flex items-center gap-2 mb-3"><Icon name="CalendarRange" size={17} className="text-[#D97706]" /><p className="text-sm font-body-medium text-card-foreground">Filtrar registros por data</p></div><div className="grid grid-cols-1 sm:grid-cols-2 gap-3"><Input type="date" label="De" value={period.de} onChange={(e) => setPeriod((p) => ({ ...p, de: e.target.value }))} /><Input type="date" label="Até" value={period.ate} onChange={(e) => setPeriod((p) => ({ ...p, ate: e.target.value }))} /></div></div><div className="grid grid-cols-1 sm:grid-cols-2 gap-3"><div className="rounded-xl border border-[#eadfd6] bg-white/80 p-4"><p className="text-xs text-muted-foreground uppercase">Equipamentos</p><p className="text-2xl font-heading-bold text-card-foreground mt-1">{formatNumber(filteredTotals?.equipamentos)}</p></div><div className="rounded-xl border border-[#eadfd6] bg-white/80 p-4"><p className="text-xs text-muted-foreground uppercase">Registros</p><p className="text-2xl font-heading-bold text-card-foreground mt-1">{formatNumber(filteredTotals?.registros)}</p></div><div className="rounded-xl border border-[#eadfd6] bg-white/80 p-4"><p className="text-xs text-muted-foreground uppercase">Conformes</p><p className="text-2xl font-heading-bold text-success mt-1">{formatNumber(filteredTotals?.conformes)}</p></div><div className="rounded-xl border border-[#eadfd6] bg-white/80 p-4"><p className="text-xs text-muted-foreground uppercase">Não conformes</p><p className="text-2xl font-heading-bold text-error mt-1">{formatNumber(filteredTotals?.naoConformes)}</p></div></div><div className="rounded-xl border border-[#F29F05]/30 bg-gradient-to-r from-[#5A3528] to-[#8B5E3C] p-4 text-white flex items-center justify-between"><div><p className="text-sm font-body-medium">Alertas ativos</p><p className="text-xs text-white/75 mt-1">Hoje, vencidos, pendentes ou próximos</p></div><div className="text-right"><p className="text-2xl font-heading-bold text-[#F29F05]">{formatNumber(filteredTotals?.alertas)}</p><p className="text-xs text-white/75">{formatNumber(filteredTotals?.alertasHoje)} para hoje</p></div></div></div>
      </section>

      <section className="grid grid-cols-1 xl:grid-cols-[1fr_1fr] gap-6"><form id="pcp-equipamento-config-form" onSubmit={submitConfig} className="bg-card rounded-xl border border-border shadow-card p-6 space-y-5"><div className="flex items-center justify-between gap-4"><div><h2 className="text-xl font-heading-semibold text-card-foreground">Parametrização dos equipamentos</h2><p className="text-sm text-muted-foreground mt-1">Defina cronogramas por dia fixo da semana, dia do mês ou intervalo.</p></div>{configEditingId ? <Button type="button" variant="ghost" size="sm" onClick={resetConfigForm}>Cancelar</Button> : null}</div><Input label="Equipamento" placeholder="Ex: FLOWPACK" value={configForm.equipamentoNome} onChange={(e) => setConfigForm((p) => ({ ...p, equipamentoNome: e.target.value }))} required /><ScheduleFields prefix="limpeza" label="limpeza" form={configForm} setForm={setConfigForm} /><ScheduleFields prefix="manutencao" label="manutenção" form={configForm} setForm={setConfigForm} /><label className="inline-flex items-center gap-2 text-sm text-card-foreground"><input type="checkbox" checked={!!configForm.ativo} onChange={(e) => setConfigForm((p) => ({ ...p, ativo: e.target.checked }))} /> Equipamento ativo</label><Button type="submit" fullWidth loading={saving} iconName={configEditingId ? "Save" : "Plus"}>{configEditingId ? "Salvar parametrização" : "Cadastrar equipamento"}</Button></form>
        <div className="bg-card rounded-xl border border-border shadow-card overflow-hidden"><div className="p-6 border-b border-border"><h2 className="text-xl font-heading-semibold text-card-foreground">Equipamentos parametrizados</h2><p className="text-sm text-muted-foreground mt-1">Cronogramas configurados para limpeza e manutenção.</p></div><div className="divide-y divide-border max-h-[620px] overflow-auto">{configs.length === 0 ? <div className="p-8 text-center text-sm text-muted-foreground">Nenhum equipamento parametrizado.</div> : configs.map((config) => <div key={config.id} className="p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 hover:bg-accent/30"><div><div className="font-body-semibold text-card-foreground">{config.equipamentoNome}</div><div className="text-xs text-muted-foreground mt-1">Limpeza: {config.limpezaDescricao || `a cada ${formatNumber(config.limpezaIntervaloDias)} dias`}</div><div className="text-xs text-muted-foreground mt-1">Manutenção: {config.manutencaoDescricao || `a cada ${formatNumber(config.manutencaoIntervaloDias)} dias`}</div></div><div className="flex items-center gap-1"><span className={cn("inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-body-medium", config.ativo ? "bg-success/10 text-success border-success/20" : "bg-muted text-muted-foreground border-border")}>{config.ativo ? "Ativo" : "Inativo"}</span><button type="button" onClick={() => editConfig(config)} className="p-2 rounded-lg text-muted-foreground hover:text-card-foreground hover:bg-accent" title="Editar"><Icon name="Edit3" size={16} /></button><button type="button" onClick={() => deleteConfig(config)} className="p-2 rounded-lg text-error hover:bg-error/10" title="Remover"><Icon name="Trash2" size={16} /></button></div></div>)}</div></div></section>

      <section className="grid grid-cols-1 xl:grid-cols-[0.9fr_1.1fr] gap-6"><div className="bg-card rounded-xl border border-border shadow-card p-6"><div className="mb-6"><h2 className="text-xl font-heading-semibold text-card-foreground">Registros por dia</h2><p className="text-sm text-muted-foreground mt-1">Limpezas, manutenções e não conformidades realizadas no período.</p></div><div className="h-72"><ResponsiveContainer width="100%" height="100%"><BarChart data={charts.daily || []}><CartesianGrid strokeDasharray="3 3" vertical={false} stroke={chartColors.light} /><XAxis dataKey="dia" tick={{ fontSize: 12, fill: chartColors.axis }} axisLine={{ stroke: chartColors.light }} tickLine={{ stroke: chartColors.light }} /><YAxis tick={{ fontSize: 12, fill: chartColors.axis }} axisLine={{ stroke: chartColors.light }} tickLine={{ stroke: chartColors.light }} /><Tooltip content={<CustomTooltip />} /><Legend /><Bar dataKey="limpezas" name="Limpezas" fill={chartColors.orange} radius={[8, 8, 0, 0]} /><Bar dataKey="manutencoes" name="Manutenções" fill={chartColors.brown} radius={[8, 8, 0, 0]} /><Bar dataKey="naoConformes" name="NC" fill="#DC2626" radius={[8, 8, 0, 0]} /></BarChart></ResponsiveContainer></div></div>
        <div className="bg-card rounded-xl border border-border shadow-card overflow-hidden"><div className="p-6 border-b border-border"><h2 className="text-xl font-heading-semibold text-card-foreground">Alertas de prazo</h2><p className="text-sm text-muted-foreground mt-1">Acompanhe o que vence hoje, próximo ou está pendente.</p></div><div className="divide-y divide-border max-h-[350px] overflow-auto">{alerts.length === 0 ? <div className="p-8 text-center text-sm text-muted-foreground">Nenhum alerta ativo. Todos os equipamentos estão em dia.</div> : alerts.map((alert) => <div key={alert.id} className="p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"><div><div className="font-body-semibold text-card-foreground">{alert.equipamentoNome}</div><div className="text-sm text-muted-foreground mt-1">{alert.tipoLabel} · {alert.agendaDescricao} · última: {alert.ultimaData ? formatDate(alert.ultimaData) : "sem registro"}</div>{alert.alertaTexto ? <div className="text-sm font-body-medium text-[#5A3528] mt-1">{alert.alertaTexto}</div> : null}</div><span className={cn("inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-body-medium", statusBadge(alert.status))}>{statusLabel(alert.status)}</span></div>)}</div></div></section>

      <section className="bg-card rounded-xl border border-border shadow-card overflow-hidden"><div className="p-6 border-b border-border flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4"><div><h2 className="text-xl font-heading-semibold text-card-foreground">Histórico de limpeza e manutenção</h2><p className="text-sm text-muted-foreground mt-1">Registros manuais por equipamento com status C/NC.</p></div><Input type="search" placeholder="Buscar equipamento, tipo ou responsável..." value={search} onChange={(e) => setSearch(e.target.value)} className="lg:w-80" /></div><div className="overflow-x-auto"><table className="w-full"><thead className="bg-muted/60"><tr><th className="p-4 text-left text-xs text-muted-foreground uppercase">Data</th><th className="p-4 text-left text-xs text-muted-foreground uppercase">Equipamento</th><th className="p-4 text-left text-xs text-muted-foreground uppercase">Tipo</th><th className="p-4 text-left text-xs text-muted-foreground uppercase">Status</th><th className="p-4 text-left text-xs text-muted-foreground uppercase">Responsável</th><th className="p-4 text-left text-xs text-muted-foreground uppercase">Observação</th><th className="p-4 text-right text-xs text-muted-foreground uppercase">Ações</th></tr></thead><tbody>{loading ? <tr><td colSpan={7} className="p-8 text-center text-sm text-muted-foreground">Carregando...</td></tr> : filteredRecords.length === 0 ? <tr><td colSpan={7} className="p-8 text-center text-sm text-muted-foreground">Nenhum registro encontrado.</td></tr> : filteredRecords.map((record) => <tr key={record.id} className="border-b border-border hover:bg-accent/30 transition-smooth"><td className="p-4 text-sm text-muted-foreground whitespace-nowrap">{formatDate(record.data)}</td><td className="p-4 font-body-medium text-card-foreground min-w-[220px]">{record.equipamentoNome}</td><td className="p-4"><span className={cn("inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-body-medium", record.tipo === "manutencao" ? "bg-[#5A3528]/10 text-[#5A3528] border-[#5A3528]/20" : "bg-[#F29F05]/15 text-[#8B5E00] border-[#F29F05]/25")}>{record.tipo === "manutencao" ? "Manutenção" : "Limpeza"}</span></td><td className="p-4"><span className={cn("inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-body-medium", record.conformidade === "NC" ? "bg-error/10 text-error border-error/20" : "bg-success/10 text-success border-success/20")}>{record.conformidade === "NC" ? "NC" : "C"}</span></td><td className="p-4 text-sm text-muted-foreground">{record.responsavel || "-"}</td><td className="p-4 text-sm text-muted-foreground min-w-[260px]">{record.observacao || "-"}</td><td className="p-4"><div className="flex justify-end gap-1"><button type="button" onClick={() => editRecord(record)} className="p-2 rounded-lg text-muted-foreground hover:text-card-foreground hover:bg-accent" title="Editar"><Icon name="Edit3" size={16} /></button><button type="button" onClick={() => deleteRecord(record)} className="p-2 rounded-lg text-error hover:bg-error/10" title="Remover"><Icon name="Trash2" size={16} /></button></div></td></tr>)}</tbody></table></div></section>
    </div>
  );
}



function GenericRegistrySection({ config }) {
  const [records, setRecords] = useState([]);
  const [filteredTotals, setFilteredTotals] = useState({});
  const [charts, setCharts] = useState({ daily: [] });
  const [alerts, setAlerts] = useState([]);
  const [period, setPeriod] = useState({ de: thirtyDaysAgo(), ate: today() });
  const [form, setForm] = useState(config.emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });

  const loadData = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${apiBase}${config.endpoint}`, { params: period });
      setRecords(Array.isArray(data.records) ? data.records : []);
      setFilteredTotals(data.filteredTotals || {});
      setCharts(data.charts || { daily: [] });
      setAlerts(Array.isArray(data.alerts) ? data.alerts : []);
    } catch (error) {
      setMessage({ type: "error", text: error?.response?.data?.message || error?.message || `Erro ao carregar ${config.label}.` });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setForm(config.emptyForm);
    setEditingId(null);
    setSearch("");
    setMessage({ type: "", text: "" });
  }, [config.endpoint]);

  useEffect(() => {
    loadData();
  }, [config.endpoint, period.de, period.ate]);

  const filteredRecords = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return records;
    return records.filter((item) => Object.values(item).some((value) => String(value || "").toLowerCase().includes(term)));
  }, [records, search]);

  const resetForm = () => {
    setForm(config.emptyForm);
    setEditingId(null);
  };

  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const submitRecord = async (event) => {
    event.preventDefault();
    setSaving(true);
    setMessage({ type: "", text: "" });
    try {
      const payload = { ...form };
      if (editingId) {
        await axios.put(`${apiBase}${config.endpoint}/${editingId}`, payload);
        setMessage({ type: "success", text: "Registro atualizado com sucesso." });
      } else {
        await axios.post(`${apiBase}${config.endpoint}`, payload);
        setMessage({ type: "success", text: "Registro cadastrado com sucesso." });
      }
      resetForm();
      await loadData();
    } catch (error) {
      setMessage({ type: "error", text: error?.response?.data?.message || error?.message || "Erro ao salvar registro." });
    } finally {
      setSaving(false);
    }
  };

  const editRecord = (record) => {
    setEditingId(record.id);
    setForm({ ...config.emptyForm, ...record });
    document.getElementById(`pcp-generic-${config.endpoint}`)?.scrollIntoView({ behavior: "smooth" });
  };

  const deleteRecord = async (record) => {
    if (!window.confirm("Remover este registro?")) return;
    setSaving(true);
    try {
      await axios.delete(`${apiBase}${config.endpoint}/${record.id}`);
      setMessage({ type: "success", text: "Registro removido com sucesso." });
      await loadData();
    } catch (error) {
      setMessage({ type: "error", text: error?.response?.data?.message || error?.message || "Erro ao remover registro." });
    } finally {
      setSaving(false);
    }
  };

  const renderField = (field) => {
    const value = form[field.key] ?? "";
    if (field.type === "select") {
      return (
        <div key={field.key} className={field.full ? "sm:col-span-2" : ""}>
          <label className="text-sm font-medium text-foreground">{field.label}{field.required ? " *" : ""}</label>
          <select
            value={value}
            onChange={(e) => updateField(field.key, e.target.value)}
            className="mt-2 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            required={field.required}
          >
            <option value="">Selecione</option>
            {(field.options || []).map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
          </select>
        </div>
      );
    }
    if (field.type === "textarea") {
      return (
        <div key={field.key} className={field.full ? "sm:col-span-2" : ""}>
          <label className="text-sm font-medium text-foreground">{field.label}{field.required ? " *" : ""}</label>
          <textarea
            value={value}
            onChange={(e) => updateField(field.key, e.target.value)}
            placeholder={field.placeholder || field.label}
            className="mt-2 min-h-[92px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            required={field.required}
          />
        </div>
      );
    }
    return (
      <Input
        key={field.key}
        type={field.type || "text"}
        step={field.type === "number" ? "0.01" : undefined}
        min={field.type === "number" ? "0" : undefined}
        label={field.label}
        value={value}
        onChange={(e) => updateField(field.key, e.target.value)}
        required={field.required}
        className={field.full ? "sm:col-span-2" : ""}
      />
    );
  };

  const statusLabel = (record) => {
    if (record.materialConforme) return record.materialConforme === "NC" ? "Não conforme" : "Conforme";
    if (record.status) return record.status;
    if (record.produto || record.lote || record.responsavel) return "Registrado";
    return Number(record.quantidade || 0) > 0 ? "Registrado" : "Pendente";
  };

  return (
    <div className="space-y-6">
      {message.text ? (
        <div className={`rounded-xl border px-4 py-3 text-sm flex gap-2 ${message.type === "error" ? "bg-error/10 text-error border-error/20" : "bg-success/10 text-success border-success/20"}`}>
          <Icon name={message.type === "error" ? "AlertCircle" : "CheckCircle2"} size={18} />
          {message.text}
        </div>
      ) : null}

      <section className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <MetricCard title="Registros no período" value={formatNumber(filteredTotals?.registros)} sub="Lançamentos filtrados" icon={config.icon} accent="primary" />
        <MetricCard title="Quantidade total" value={formatNumber(filteredTotals?.quantidade)} sub="Soma das quantidades" icon="Boxes" accent="warning" />
        <MetricCard title="Alertas" value={formatNumber(filteredTotals?.alertas)} sub="NC, reprovado ou cancelado" icon="AlertTriangle" accent="error" />
        <MetricCard title="Conformidade" value={formatPercent(filteredTotals?.conformidade)} sub="Registros dentro do padrão" icon="CheckCircle2" accent="success" />
      </section>

      <section className="grid grid-cols-1 xl:grid-cols-[0.95fr_1.05fr] gap-6">
        <form id={`pcp-generic-${config.endpoint}`} onSubmit={submitRecord} className="bg-card rounded-xl border border-border shadow-card p-6 space-y-5">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h2 className="text-xl font-heading-semibold text-card-foreground">{editingId ? `Editar ${config.label}` : config.label}</h2>
              <p className="text-sm text-muted-foreground mt-1">{config.description}</p>
            </div>
            {editingId ? <Button type="button" variant="ghost" size="sm" onClick={resetForm}>Cancelar</Button> : null}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {config.fields.map(renderField)}
          </div>
          <Button type="submit" fullWidth loading={saving} iconName={editingId ? "Save" : "Plus"}>{editingId ? "Salvar alterações" : "Cadastrar registro"}</Button>
        </form>

        <div className="bg-card rounded-xl border border-border shadow-card p-6 space-y-5">
          <div>
            <h2 className="text-xl font-heading-semibold text-card-foreground">Resumo e filtro</h2>
            <p className="text-sm text-muted-foreground mt-1">Filtre os registros por período e acompanhe os indicadores.</p>
          </div>
          <div className="rounded-xl border border-[#eadfd6] bg-[#fff7ed] p-4">
            <div className="flex items-center gap-2 mb-3">
              <Icon name="CalendarRange" size={17} className="text-[#D97706]" />
              <p className="text-sm font-body-medium text-card-foreground">Filtrar por data</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Input type="date" label="De" value={period.de} onChange={(e) => setPeriod((p) => ({ ...p, de: e.target.value }))} />
              <Input type="date" label="Até" value={period.ate} onChange={(e) => setPeriod((p) => ({ ...p, ate: e.target.value }))} />
            </div>
          </div>
          <div className="rounded-xl border border-[#F29F05]/30 bg-gradient-to-r from-[#5A3528] to-[#8B5E3C] p-4 text-white">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-body-medium">Período filtrado</p>
                <p className="text-xs text-white/75 mt-1">{formatDate(period.de)} até {formatDate(period.ate)}</p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-heading-bold text-[#F29F05]">{formatNumber(filteredTotals?.registros)}</p>
                <p className="text-xs text-white/75">registros</p>
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-border p-4">
            <h3 className="font-body-semibold text-card-foreground mb-3">Alertas do período</h3>
            {alerts.length === 0 ? (
              <p className="text-sm text-muted-foreground">Nenhum alerta encontrado no período.</p>
            ) : alerts.slice(0, 6).map((alert) => (
              <div key={alert.id} className="flex items-center justify-between gap-3 rounded-lg bg-error/5 border border-error/10 px-3 py-2 text-sm mb-2">
                <span className="text-card-foreground truncate">{alert.produto || alert.codTeste || alert.lote || "Registro"}</span>
                <span className="text-error font-body-medium">{statusLabel(alert)}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-card rounded-xl border border-border shadow-card p-6">
        <div className="mb-6">
          <h2 className="text-xl font-heading-semibold text-card-foreground">Registros por dia</h2>
          <p className="text-sm text-muted-foreground mt-1">Volume diário dentro do período filtrado.</p>
        </div>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={charts.daily || []}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={chartColors.light} />
              <XAxis dataKey="dia" tick={{ fontSize: 12, fill: chartColors.axis }} axisLine={{ stroke: chartColors.light }} tickLine={{ stroke: chartColors.light }} />
              <YAxis tick={{ fontSize: 12, fill: chartColors.axis }} axisLine={{ stroke: chartColors.light }} tickLine={{ stroke: chartColors.light }} />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Bar dataKey="registros" name="Registros" fill={chartColors.orange} radius={[8, 8, 0, 0]} />
              <Bar dataKey="alertas" name="Alertas" fill={chartColors.brown} radius={[8, 8, 0, 0]} />
              <Bar dataKey="quantidade" name="Quantidade" fill={chartColors.amber} radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </section>

      <section className="bg-card rounded-xl border border-border shadow-card overflow-hidden">
        <div className="p-6 border-b border-border flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h2 className="text-xl font-heading-semibold text-card-foreground">Histórico</h2>
            <p className="text-sm text-muted-foreground mt-1">Registros cadastrados no período filtrado.</p>
          </div>
          <Input type="search" placeholder="Buscar..." value={search} onChange={(e) => setSearch(e.target.value)} className="lg:w-80" />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/60">
              <tr>
                {config.table.map((key) => <th key={key} className="p-4 text-left text-xs text-muted-foreground uppercase">{config.tableLabels[key] || key}</th>)}
                <th className="p-4 text-left text-xs text-muted-foreground uppercase">Status</th>
                <th className="p-4 text-right text-xs text-muted-foreground uppercase">Ações</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={config.table.length + 2} className="p-8 text-center text-sm text-muted-foreground">Carregando...</td></tr>
              ) : filteredRecords.length === 0 ? (
                <tr><td colSpan={config.table.length + 2} className="p-8 text-center text-sm text-muted-foreground">Nenhum registro encontrado.</td></tr>
              ) : filteredRecords.map((record) => (
                <tr key={record.id} className="border-b border-border hover:bg-accent/30 transition-smooth">
                  {config.table.map((key) => (
                    <td key={key} className="p-4 text-sm text-card-foreground min-w-[120px]">
                      {key.toLowerCase().includes("data") || key === "validade" ? formatDate(record[key]) : key === "quantidade" || key === "pesoMedio" ? formatNumber(record[key]) : record[key] || "-"}
                    </td>
                  ))}
                  <td className="p-4"><span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-body-medium ${statusLabel(record).toLowerCase().includes("não") || statusLabel(record).includes("REPROVADO") || statusLabel(record).includes("CANCELADO") ? "bg-error/10 text-error border-error/20" : "bg-success/10 text-success border-success/20"}`}><Icon name="CheckCircle2" size={13} />{statusLabel(record)}</span></td>
                  <td className="p-4">
                    <div className="flex justify-end gap-1">
                      <button type="button" onClick={() => editRecord(record)} className="p-2 rounded-lg text-muted-foreground hover:text-card-foreground hover:bg-accent" title="Editar"><Icon name="Edit3" size={16} /></button>
                      <button type="button" onClick={() => deleteRecord(record)} className="p-2 rounded-lg text-error hover:bg-error/10" title="Remover"><Icon name="Trash2" size={16} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}


export default function ControleProducaoPage() {
  const [activeTab, setActiveTab] = useState("producao");

  const [products, setProducts] = useState([]);
  const [records, setRecords] = useState([]);
  const [summary, setSummary] = useState({});
  const [filteredTotals, setFilteredTotals] = useState({});
  const [charts, setCharts] = useState({ daily: [], products: [], lossReasons: [] });
  const [period, setPeriod] = useState({ de: thirtyDaysAgo(), ate: today() });
  const [form, setForm] = useState(blankProductionForm);
  const [editingId, setEditingId] = useState(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });

  const [tempConfigs, setTempConfigs] = useState([]);
  const [tempRecords, setTempRecords] = useState([]);
  const [tempSummary, setTempSummary] = useState({});
  const [tempFilteredTotals, setTempFilteredTotals] = useState({});
  const [tempCharts, setTempCharts] = useState({ daily: [], locations: [], status: [] });
  const [tempAlerts, setTempAlerts] = useState([]);
  const [tempPeriod, setTempPeriod] = useState({ de: thirtyDaysAgo(), ate: today() });
  const [tempForm, setTempForm] = useState(blankTemperatureForm);
  const [configForm, setConfigForm] = useState(blankConfigForm);
  const [tempEditingId, setTempEditingId] = useState(null);
  const [configEditingId, setConfigEditingId] = useState(null);
  const [tempSearch, setTempSearch] = useState("");
  const [tempLoading, setTempLoading] = useState(true);
  const [tempSaving, setTempSaving] = useState(false);
  const [tempMessage, setTempMessage] = useState({ type: "", text: "" });

  const [urConfigs, setUrConfigs] = useState([]);
  const [urRecords, setUrRecords] = useState([]);
  const [urSummary, setUrSummary] = useState({});
  const [urFilteredTotals, setUrFilteredTotals] = useState({});
  const [urCharts, setUrCharts] = useState({ daily: [], locations: [], status: [] });
  const [urAlerts, setUrAlerts] = useState([]);
  const [urPeriod, setUrPeriod] = useState({ de: thirtyDaysAgo(), ate: today() });
  const [urForm, setUrForm] = useState(blankHumidityUrForm);
  const [urConfigForm, setUrConfigForm] = useState(blankHumidityUrConfigForm);
  const [urEditingId, setUrEditingId] = useState(null);
  const [urConfigEditingId, setUrConfigEditingId] = useState(null);
  const [urSearch, setUrSearch] = useState("");
  const [urLoading, setUrLoading] = useState(true);
  const [urSaving, setUrSaving] = useState(false);
  const [urMessage, setUrMessage] = useState({ type: "", text: "" });

  const [hygieneRecords, setHygieneRecords] = useState([]);
  const [hygieneSummary, setHygieneSummary] = useState({});
  const [hygieneFilteredTotals, setHygieneFilteredTotals] = useState({});
  const [hygieneCharts, setHygieneCharts] = useState({ daily: [], items: [] });
  const [hygieneAlerts, setHygieneAlerts] = useState([]);
  const [hygienePeriod, setHygienePeriod] = useState({ de: thirtyDaysAgo(), ate: today() });
  const [hygieneForm, setHygieneForm] = useState(blankHygieneForm);
  const [hygieneEditingId, setHygieneEditingId] = useState(null);
  const [hygieneSearch, setHygieneSearch] = useState("");
  const [hygieneLoading, setHygieneLoading] = useState(true);
  const [hygieneSaving, setHygieneSaving] = useState(false);
  const [hygieneMessage, setHygieneMessage] = useState({ type: "", text: "" });

  const [buildingCleaningRecords, setBuildingCleaningRecords] = useState([]);
  const [buildingCleaningSummary, setBuildingCleaningSummary] = useState({});
  const [buildingCleaningFilteredTotals, setBuildingCleaningFilteredTotals] = useState({});
  const [buildingCleaningCharts, setBuildingCleaningCharts] = useState({ daily: [], items: [] });
  const [buildingCleaningAlerts, setBuildingCleaningAlerts] = useState([]);
  const [buildingCleaningPeriod, setBuildingCleaningPeriod] = useState({ de: thirtyDaysAgo(), ate: today() });
  const [buildingCleaningForm, setBuildingCleaningForm] = useState(blankBuildingCleaningForm);
  const [buildingCleaningEditingId, setBuildingCleaningEditingId] = useState(null);
  const [buildingCleaningSearch, setBuildingCleaningSearch] = useState("");
  const [buildingCleaningLoading, setBuildingCleaningLoading] = useState(true);
  const [buildingCleaningSaving, setBuildingCleaningSaving] = useState(false);
  const [buildingCleaningMessage, setBuildingCleaningMessage] = useState({ type: "", text: "" });

  const [equipmentConfigs, setEquipmentConfigs] = useState([]);
  const [equipmentRecords, setEquipmentRecords] = useState([]);
  const [equipmentFilteredTotals, setEquipmentFilteredTotals] = useState({});
  const [equipmentCharts, setEquipmentCharts] = useState({ daily: [] });
  const [equipmentAlerts, setEquipmentAlerts] = useState([]);
  const [equipmentPeriod, setEquipmentPeriod] = useState({ de: thirtyDaysAgo(), ate: today() });
  const [equipmentForm, setEquipmentForm] = useState(blankEquipmentMaintenanceForm);
  const [equipmentConfigForm, setEquipmentConfigForm] = useState(blankEquipmentMaintenanceConfigForm);
  const [equipmentEditingId, setEquipmentEditingId] = useState(null);
  const [equipmentConfigEditingId, setEquipmentConfigEditingId] = useState(null);
  const [equipmentSearch, setEquipmentSearch] = useState("");
  const [equipmentLoading, setEquipmentLoading] = useState(true);
  const [equipmentSaving, setEquipmentSaving] = useState(false);
  const [equipmentMessage, setEquipmentMessage] = useState({ type: "", text: "" });

  const loadProductionData = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${apiBase}/api/controle-producao`, { params: period });
      setProducts(Array.isArray(data.products) ? data.products : []);
      setRecords(Array.isArray(data.records) ? data.records : []);
      setSummary(data.summary || {});
      setFilteredTotals(data.filteredTotals || {});
      setCharts(data.charts || { daily: [], products: [], lossReasons: [] });
    } catch (error) {
      setMessage({ type: "error", text: error?.response?.data?.message || error?.message || "Erro ao carregar produção." });
    } finally {
      setLoading(false);
    }
  };

  const loadTemperatureData = async () => {
    setTempLoading(true);
    try {
      const { data } = await axios.get(`${apiBase}/api/controle-producao/temperatura-umidade`, { params: tempPeriod });
      setTempConfigs(Array.isArray(data.configs) ? data.configs : []);
      setTempRecords(Array.isArray(data.records) ? data.records : []);
      setTempSummary(data.summary || {});
      setTempFilteredTotals(data.filteredTotals || {});
      setTempCharts(data.charts || { daily: [], locations: [], status: [] });
      setTempAlerts(Array.isArray(data.alerts) ? data.alerts : []);
    } catch (error) {
      setTempMessage({ type: "error", text: error?.response?.data?.message || error?.message || "Erro ao carregar medições." });
    } finally {
      setTempLoading(false);
    }
  };

  useEffect(() => {
    loadProductionData();
  }, [period.de, period.ate]);


  const loadHumidityUrData = async () => {
    setUrLoading(true);
    try {
      const { data } = await axios.get(`${apiBase}/api/controle-producao/umidade-ur`, { params: urPeriod });
      setUrConfigs(Array.isArray(data.configs) ? data.configs : []);
      setUrRecords(Array.isArray(data.records) ? data.records : []);
      setUrSummary(data.summary || {});
      setUrFilteredTotals(data.filteredTotals || {});
      setUrCharts(data.charts || { daily: [], locations: [], status: [] });
      setUrAlerts(Array.isArray(data.alerts) ? data.alerts : []);
    } catch (error) {
      setUrMessage({ type: "error", text: error?.response?.data?.message || error?.message || "Erro ao carregar controle de UR." });
    } finally {
      setUrLoading(false);
    }
  };

  useEffect(() => {
    loadTemperatureData();
  }, [tempPeriod.de, tempPeriod.ate]);

  const loadHygieneData = async () => {
    setHygieneLoading(true);
    try {
      const { data } = await axios.get(`${apiBase}/api/controle-producao/higiene-pessoal`, { params: hygienePeriod });
      setHygieneRecords(Array.isArray(data.records) ? data.records : []);
      setHygieneSummary(data.summary || {});
      setHygieneFilteredTotals(data.filteredTotals || {});
      setHygieneCharts(data.charts || { daily: [], items: [] });
      setHygieneAlerts(Array.isArray(data.alerts) ? data.alerts : []);
    } catch (error) {
      setHygieneMessage({ type: "error", text: error?.response?.data?.message || error?.message || "Erro ao carregar higiene pessoal." });
    } finally {
      setHygieneLoading(false);
    }
  };

  const loadBuildingCleaningData = async () => {
    setBuildingCleaningLoading(true);
    try {
      const { data } = await axios.get(`${apiBase}/api/controle-producao/limpeza-edificacao`, { params: buildingCleaningPeriod });
      setBuildingCleaningRecords(Array.isArray(data.records) ? data.records : []);
      setBuildingCleaningSummary(data.summary || {});
      setBuildingCleaningFilteredTotals(data.filteredTotals || {});
      setBuildingCleaningCharts(data.charts || { daily: [], items: [] });
      setBuildingCleaningAlerts(Array.isArray(data.alerts) ? data.alerts : []);
    } catch (error) {
      setBuildingCleaningMessage({ type: "error", text: error?.response?.data?.message || error?.message || "Erro ao carregar limpeza da edificação." });
    } finally {
      setBuildingCleaningLoading(false);
    }
  };

  const loadEquipmentMaintenanceData = async () => {
    setEquipmentLoading(true);
    try {
      const { data } = await axios.get(`${apiBase}/api/controle-producao/limpeza-manutencao-equipamentos`, { params: equipmentPeriod });
      setEquipmentConfigs(Array.isArray(data.configs) ? data.configs : []);
      setEquipmentRecords(Array.isArray(data.records) ? data.records : []);
      setEquipmentFilteredTotals(data.filteredTotals || {});
      setEquipmentCharts(data.charts || { daily: [] });
      setEquipmentAlerts(Array.isArray(data.alerts) ? data.alerts : []);
    } catch (error) {
      setEquipmentMessage({ type: "error", text: error?.response?.data?.message || error?.message || "Erro ao carregar limpeza/manutenção de equipamentos." });
    } finally {
      setEquipmentLoading(false);
    }
  };

  useEffect(() => {
    loadHumidityUrData();
  }, [urPeriod.de, urPeriod.ate]);

  useEffect(() => {
    loadHygieneData();
  }, [hygienePeriod.de, hygienePeriod.ate]);


  useEffect(() => {
    loadBuildingCleaningData();
  }, [buildingCleaningPeriod.de, buildingCleaningPeriod.ate]);

  useEffect(() => {
    loadEquipmentMaintenanceData();
  }, [equipmentPeriod.de, equipmentPeriod.ate]);

  const heroMetrics = useMemo(() => {

    if (genericRegistryModules[activeTab]) {
      const config = genericRegistryModules[activeTab];
      return [
        { title: "Módulo PCP", value: config.label, sub: "Tela interna cadastrada", icon: config.icon, accent: "primary" },
        { title: "Registro manual", value: "Ativo", sub: "Cadastro, edição e remoção", icon: "ClipboardCheck", accent: "success" },
        { title: "Filtros", value: "Data", sub: "BI por período", icon: "CalendarRange", accent: "warning" },
        { title: "Alertas", value: "C/NC", sub: "Controle visual no histórico", icon: "AlertTriangle", accent: "error" },
      ];
    }

    if (activeTab === "limpeza-manutencao-equipamentos") {
      return [
        { title: "Equipamentos", value: formatNumber(equipmentFilteredTotals?.equipamentos), sub: "Parametrizados ativos", icon: "Settings2", accent: "primary" },
        { title: "Registros no período", value: formatNumber(equipmentFilteredTotals?.registros), sub: "Limpezas e manutenções", icon: "ClipboardCheck", accent: "warning" },
        { title: "Alertas", value: formatNumber(equipmentFilteredTotals?.alertas), sub: "Pendente, próximo ou vencido", icon: "AlertTriangle", accent: "error" },
        { title: "Em dia", value: formatNumber(equipmentFilteredTotals?.emDia), sub: "Controles sem alerta", icon: "CheckCircle2", accent: "success" },
      ];
    }

    if (activeTab === "limpeza-edificacao") {
      return [
        { title: "Registros no período", value: formatNumber(buildingCleaningFilteredTotals?.registros), sub: "Verificações de limpeza", icon: "Sparkles", accent: "primary" },
        { title: "Itens verificados", value: formatNumber(buildingCleaningFilteredTotals?.itensVerificados), sub: "Máquinas, utensílios e estrutura", icon: "ClipboardCheck", accent: "warning" },
        { title: "Não conformidades", value: formatNumber(buildingCleaningFilteredTotals?.naoConformidades), sub: "Itens marcados como NC", icon: "AlertTriangle", accent: "error" },
        { title: "Conformidade", value: formatPercent(buildingCleaningFilteredTotals?.conformidade), sub: "Itens conformes", icon: "CheckCircle2", accent: "success" },
      ];
    }

    if (activeTab === "higiene-pessoal") {
      return [
        { title: "Registros no período", value: formatNumber(hygieneFilteredTotals?.registros), sub: "Funcionários verificados", icon: "UserCheck", accent: "primary" },
        { title: "Itens verificados", value: formatNumber(hygieneFilteredTotals?.itensVerificados), sub: "EPIs e aparência", icon: "ClipboardCheck", accent: "warning" },
        { title: "Não conformidades", value: formatNumber(hygieneFilteredTotals?.naoConformidades), sub: "Itens marcados como NC", icon: "AlertTriangle", accent: "error" },
        { title: "Conformidade", value: formatPercent(hygieneFilteredTotals?.conformidade), sub: "Itens conformes", icon: "CheckCircle2", accent: "success" },
      ];
    }

    if (activeTab === "umidade-ur") {
      return [
        { title: "Ambientes UR", value: formatNumber(urConfigs.length), sub: "Salas parametrizadas", icon: "Settings2", accent: "primary" },
        { title: "Medições no período", value: formatNumber(urFilteredTotals?.totalRegistros), sub: `${formatDecimal(urFilteredTotals?.mediaUmidade)}% UR média`, icon: "Droplets", accent: "warning" },
        { title: "Alertas UR", value: formatNumber(urFilteredTotals?.alertas), sub: "Abaixo ou acima do limite", icon: "AlertTriangle", accent: "error" },
        { title: "Conformidade", value: formatPercent(urFilteredTotals?.conformidade), sub: "Dentro da faixa configurada", icon: "CheckCircle2", accent: "success" },
      ];
    }

    if (activeTab === "temperatura") {
      return [
        {
          title: "Ambientes parametrizados",
          value: formatNumber(tempConfigs.length),
          sub: "Locais com limites cadastrados",
          icon: "Settings2",
          accent: "primary",
        },
        {
          title: "Medições no período",
          value: formatNumber(tempFilteredTotals?.totalRegistros),
          sub: `${formatDecimal(tempFilteredTotals?.mediaTemperatura)}°C média`,
          icon: "Thermometer",
          accent: "warning",
        },
        {
          title: "Alertas",
          value: formatNumber(tempFilteredTotals?.alertas),
          sub: "Abaixo ou acima do mínimo/máximo",
          icon: "AlertTriangle",
          accent: "error",
        },
        {
          title: "Conformidade",
          value: formatPercent(tempFilteredTotals?.conformidade),
          sub: `${formatDecimal(tempFilteredTotals?.mediaTemperatura)}°C média`,
          icon: "CheckCircle2",
          accent: "success",
        },
      ];
    }

    return [
      {
        title: "Produtos monitorados",
        value: formatNumber(products.length),
        sub: "Itens cadastrados no PCP",
        icon: "PackageCheck",
        accent: "primary",
      },
      {
        title: "Previsão no período",
        value: formatNumber(filteredTotals?.previsao),
        sub: "Quantidade prevista filtrada",
        icon: "CalendarCheck",
        accent: "warning",
      },
      {
        title: "Realizado",
        value: formatNumber(filteredTotals?.realizado),
        sub: `${formatPercent(filteredTotals?.eficiencia)} de eficiência`,
        icon: "Factory",
        accent: "success",
      },
      {
        title: "Perdas",
        value: formatNumber(filteredTotals?.perdas),
        sub: `${formatPercent(filteredTotals?.perdaPercentual)} de perda`,
        icon: "AlertTriangle",
        accent: "error",
      },
    ];
  }, [activeTab, filteredTotals, products.length, tempConfigs.length, tempFilteredTotals, urConfigs.length, urFilteredTotals, hygieneFilteredTotals, buildingCleaningFilteredTotals, equipmentFilteredTotals]);

  const anyLoading = activeTab === "checklist-diario" ? false : genericRegistryModules[activeTab] ? false : activeTab === "temperatura" ? tempLoading : activeTab === "umidade-ur" ? urLoading : activeTab === "higiene-pessoal" ? hygieneLoading : activeTab === "limpeza-edificacao" ? buildingCleaningLoading : activeTab === "limpeza-manutencao-equipamentos" ? equipmentLoading : loading;

  return (
    <div className="min-h-screen bg-background">
      <NavigationHeader />
      <main className="pt-16 px-6 pb-8">
        <div className="max-w-7xl mx-auto space-y-6">
          <section className="bg-gradient-to-r from-[#4A2415] via-[#5A3528] to-[#7A4B36] rounded-2xl border border-[#70422e] shadow-card p-6 text-white overflow-hidden relative">
            <div className="absolute inset-0 opacity-10 bg-[radial-gradient(circle_at_top_right,_#f6ad1a_0,_transparent_35%)]" />
            <div className="relative z-10 flex flex-col xl:flex-row xl:items-center xl:justify-between gap-6">
              <div className="max-w-3xl">
                <div className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/10 px-3 py-1 text-xs mb-4">
                  <Icon name="Factory" size={14} />
                  PCP
                </div>
                <h1 className="text-3xl font-heading-bold mb-2">Planejamento e Controle de Produção</h1>
                <p className="text-white/85 max-w-2xl text-sm sm:text-base">
                  Centralize páginas internas do PCP. Registre produção manual, acompanhe BI de perdas e controle medições de temperatura, UR, higiene pessoal, limpeza da edificação, manutenção de equipamentos, recebimento de mercadorias, testes e rastreabilidade com alertas automáticos.
                </p>
              </div>
              <div className="flex flex-wrap gap-3">
                <Button variant="outline" className="bg-white/5 border-white/20 text-white hover:bg-white/10" iconName="RefreshCw" onClick={() => activeTab === "temperatura" ? loadTemperatureData() : activeTab === "umidade-ur" ? loadHumidityUrData() : activeTab === "higiene-pessoal" ? loadHygieneData() : activeTab === "limpeza-edificacao" ? loadBuildingCleaningData() : activeTab === "limpeza-manutencao-equipamentos" ? loadEquipmentMaintenanceData() : loadProductionData()}>
                  Atualizar
                </Button>
                <Button iconName={activeTab === "checklist-diario" ? "ListChecks" : activeTab === "temperatura" ? "Thermometer" : activeTab === "umidade-ur" ? "Droplets" : activeTab === "higiene-pessoal" ? "UserCheck" : activeTab === "limpeza-edificacao" ? "Sparkles" : activeTab === "limpeza-manutencao-equipamentos" ? "Wrench" : "Plus"} onClick={() => {
                  const targetId = activeTab === "checklist-diario" ? "pcp-checklist-diario" : genericRegistryModules[activeTab] ? `pcp-generic-${genericRegistryModules[activeTab].endpoint}` : activeTab === "temperatura" ? "pcp-temp-form" : activeTab === "umidade-ur" ? "pcp-ur-form" : activeTab === "higiene-pessoal" ? "pcp-higiene-form" : activeTab === "limpeza-edificacao" ? "pcp-limpeza-form" : activeTab === "limpeza-manutencao-equipamentos" ? "pcp-equipamento-registro-form" : "pcp-producao-form";
                  document.getElementById(targetId)?.scrollIntoView({ behavior: "smooth" });
                }}>
                  {activeTab === "checklist-diario" ? "Preencher checklist" : genericRegistryModules[activeTab] ? "Novo registro" : activeTab === "temperatura" ? "Nova medição" : activeTab === "umidade-ur" ? "Nova UR" : activeTab === "higiene-pessoal" ? "Novo registro" : activeTab === "limpeza-edificacao" ? "Nova limpeza" : activeTab === "limpeza-manutencao-equipamentos" ? "Novo registro" : "Nova produção"}
                </Button>
              </div>
            </div>
          </section>

          <PageTabs activeTab={activeTab} onChange={setActiveTab} />

          {activeTab !== "checklist-diario" ? <section className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            {heroMetrics.map((item) => (
              <MetricCard key={item.title} title={item.title} value={item.value} sub={item.sub} icon={item.icon} accent={item.accent} />
            ))}
          </section> : null}

          {anyLoading ? (
            <div className="bg-card rounded-xl border border-border shadow-card p-8 text-center text-sm text-muted-foreground">Carregando informações do PCP...</div>
          ) : activeTab === "checklist-diario" ? (
            <DailyChecklistSection />
          ) : activeTab === "producao" ? (
            <ProductionSection
              products={products}
              records={records}
              summary={summary}
              filteredTotals={filteredTotals}
              charts={charts}
              period={period}
              setPeriod={setPeriod}
              form={form}
              setForm={setForm}
              editingId={editingId}
              setEditingId={setEditingId}
              search={search}
              setSearch={setSearch}
              loading={loading}
              saving={saving}
              setSaving={setSaving}
              message={message}
              setMessage={setMessage}
              reload={loadProductionData}
            />
          ) : activeTab === "temperatura" ? (
            <TemperatureSection
              configs={tempConfigs}
              records={tempRecords}
              summary={tempSummary}
              filteredTotals={tempFilteredTotals}
              charts={tempCharts}
              alerts={tempAlerts}
              period={tempPeriod}
              setPeriod={setTempPeriod}
              tempForm={tempForm}
              setTempForm={setTempForm}
              tempEditingId={tempEditingId}
              setTempEditingId={setTempEditingId}
              configForm={configForm}
              setConfigForm={setConfigForm}
              configEditingId={configEditingId}
              setConfigEditingId={setConfigEditingId}
              search={tempSearch}
              setSearch={setTempSearch}
              loading={tempLoading}
              saving={tempSaving}
              setSaving={setTempSaving}
              message={tempMessage}
              setMessage={setTempMessage}
              reload={loadTemperatureData}
            />
          ) : activeTab === "umidade-ur" ? (
            <HumidityUrSection
              configs={urConfigs}
              records={urRecords}
              summary={urSummary}
              filteredTotals={urFilteredTotals}
              charts={urCharts}
              alerts={urAlerts}
              period={urPeriod}
              setPeriod={setUrPeriod}
              form={urForm}
              setForm={setUrForm}
              editingId={urEditingId}
              setEditingId={setUrEditingId}
              configForm={urConfigForm}
              setConfigForm={setUrConfigForm}
              configEditingId={urConfigEditingId}
              setConfigEditingId={setUrConfigEditingId}
              search={urSearch}
              setSearch={setUrSearch}
              loading={urLoading}
              saving={urSaving}
              setSaving={setUrSaving}
              message={urMessage}
              setMessage={setUrMessage}
              reload={loadHumidityUrData}
            />
          ) : activeTab === "higiene-pessoal" ? (
            <HygieneSection
              records={hygieneRecords}
              summary={hygieneSummary}
              filteredTotals={hygieneFilteredTotals}
              charts={hygieneCharts}
              alerts={hygieneAlerts}
              period={hygienePeriod}
              setPeriod={setHygienePeriod}
              form={hygieneForm}
              setForm={setHygieneForm}
              editingId={hygieneEditingId}
              setEditingId={setHygieneEditingId}
              search={hygieneSearch}
              setSearch={setHygieneSearch}
              loading={hygieneLoading}
              saving={hygieneSaving}
              setSaving={setHygieneSaving}
              message={hygieneMessage}
              setMessage={setHygieneMessage}
              reload={loadHygieneData}
            />
          ) : activeTab === "limpeza-edificacao" ? (
            <BuildingCleaningSection
              records={buildingCleaningRecords}
              summary={buildingCleaningSummary}
              filteredTotals={buildingCleaningFilteredTotals}
              charts={buildingCleaningCharts}
              alerts={buildingCleaningAlerts}
              period={buildingCleaningPeriod}
              setPeriod={setBuildingCleaningPeriod}
              form={buildingCleaningForm}
              setForm={setBuildingCleaningForm}
              editingId={buildingCleaningEditingId}
              setEditingId={setBuildingCleaningEditingId}
              search={buildingCleaningSearch}
              setSearch={setBuildingCleaningSearch}
              loading={buildingCleaningLoading}
              saving={buildingCleaningSaving}
              setSaving={setBuildingCleaningSaving}
              message={buildingCleaningMessage}
              setMessage={setBuildingCleaningMessage}
              reload={loadBuildingCleaningData}
            />
          ) : genericRegistryModules[activeTab] ? (
            <GenericRegistrySection config={genericRegistryModules[activeTab]} />
          ) : (
            <EquipmentMaintenanceSection
              configs={equipmentConfigs}
              records={equipmentRecords}
              filteredTotals={equipmentFilteredTotals}
              charts={equipmentCharts}
              alerts={equipmentAlerts}
              period={equipmentPeriod}
              setPeriod={setEquipmentPeriod}
              form={equipmentForm}
              setForm={setEquipmentForm}
              editingId={equipmentEditingId}
              setEditingId={setEquipmentEditingId}
              configForm={equipmentConfigForm}
              setConfigForm={setEquipmentConfigForm}
              configEditingId={equipmentConfigEditingId}
              setConfigEditingId={setEquipmentConfigEditingId}
              search={equipmentSearch}
              setSearch={setEquipmentSearch}
              loading={equipmentLoading}
              saving={equipmentSaving}
              setSaving={setEquipmentSaving}
              message={equipmentMessage}
              setMessage={setEquipmentMessage}
              reload={loadEquipmentMaintenanceData}
            />
          )}
          {activeTab !== "checklist-diario" ? <PcpAuditHistory module={activeTab} /> : null}
        </div>
      </main>
    </div>
  );
}
